import { useState, useEffect, useRef, useMemo, useCallback, Fragment } from "react";
import PropTypes from "prop-types";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Typography from "@mui/material/Typography";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Divider from "@mui/material/Divider";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import MenuItem from "@mui/material/MenuItem";
import Link from "@mui/material/Link";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import PageLayout from "components/PageLayout";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";
import { api } from "utils/api";
import {
  createClientzoneContact,
  fetchClientzoneContacts,
  fetchClientzoneTickets,
} from "utils/clientzonePortalApi";
import { useGroupAccess } from "context/GroupAccessContext";
import { userSatisfiesPolicyKey } from "utils/groupPolicyEngine";
import {
  fetchUispSpeedTestHistory,
  fetchUispSpeedTestStatus,
  recordUispSpeedTest,
  requestUispDeviceRestart,
  startUispSpeedTest,
  stopUispSpeedTest,
} from "utils/uispBackendApi";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Icon from "@mui/material/Icon";
import CircularProgress from "@mui/material/CircularProgress";
import useTheme from "@mui/material/styles/useTheme";
import useMediaQuery from "@mui/material/useMediaQuery";
import PageErrorAlert, { PAGE_ERROR_TITLES } from "components/PageErrorAlert";
import Chip from "@mui/material/Chip";
import DataTable from "examples/Tables/DataTable";
import {
  formatDateTime,
  formatDbDateTime,
  formatDate,
  formatUispSpeedTestTimestamp,
  toHtmlDateInputValue,
} from "utils/dateFormatter";
import { getPriorityInfo, getStatusInfo } from "layouts/tickets-view/data/ticketViewData";
import StatusBox from "utils/statusBox";
import PublicIcon from "@mui/icons-material/Public";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import {
  getCustomerBalance,
  getCustomerStatement,
  getInvoicePdfPreviewBlob,
  downloadInvoicePdf,
  sendInvoicePdfByEmailWithContext,
  resolveInvoiceIdFromDocumentNumber,
  getQuotePdfPreviewBlob,
  downloadQuotePdf,
  sendQuotePdfByEmailWithContext,
  resolveQuoteIdFromDocumentNumber,
  getDefaultStatementPdfDateRange,
  buildStatementDateRange,
  getStatementPdfPreviewBlob,
  downloadStatementPdf,
  sendStatementPdfByEmailWithContext,
} from "utils/billingApi";
import { processCustomerStatementHistory } from "utils/processCustomerStatementHistory";
import MDBadge from "components/MDBadge";
import IconButton from '@mui/material/IconButton';
import Tooltip from "@mui/material/Tooltip";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import CachedIcon from "@mui/icons-material/Cached";
import SpeedIcon from "@mui/icons-material/Speed";
import HttpsIcon from "@mui/icons-material/Https";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import CellTowerIcon from "@mui/icons-material/CellTower";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import LinkIcon from "@mui/icons-material/Link";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import EditIcon from "@mui/icons-material/Edit";
import NewTicketDialog from "layouts/tickets-list/components/NewTicketDialog";
import { suspendCustomer, activateCustomer } from "utils/accountActivation";
import {
  CUST_TYPE_ON_HOLD,
  CUSTOMER_STATUS_OPTIONS,
  getCustTypeTypographyColor,
  getCustomerHeaderGradient,
  getCustomerHeaderTitle,
  isOnHoldCustType,
} from "utils/customerCustType";
import AddContactDialog from "components/AddContactDialog";
import DefaultLineChart from "examples/Charts/LineCharts/DefaultLineChart";
import CustomerWhatsAppInboxPanel from "layouts/customer-view/components/CustomerWhatsAppInboxPanel";
import CustomerContactsMobileCards from "layouts/customer-view/components/CustomerContactsMobileCards";
import CustomerTicketsMobileCards from "layouts/customer-view/components/CustomerTicketsMobileCards";
import CustomerContractsMobileCards from "layouts/customer-view/components/CustomerContractsMobileCards";
import CustomerAccountBalancePanel from "layouts/customer-view/components/CustomerAccountBalancePanel";
import CustomerNotesPanel from "layouts/customer-view/components/CustomerNotesPanel";
import CpeSectorAnalysisDialog from "layouts/customer-view/components/CpeSectorAnalysisDialog";
import {
  fetchCustomerMergedContacts,
  isValidCustomerId,
  logCustomerCommunication,
  parseCustomerId,
} from "utils/customerUtils";
import { formatPhoneNumber } from "utils/phoneUtils";
import { sendWhatsAppPdfDocumentTemplate } from "utils/whatsappApi";

// Constants
const CONTRACT_STATE_COLORS = {
  active: "success",
  pending: "secondary",
  suspended: "error",
  terminated: "dark",
  expired: "warning",
};

/** Monospace stack for PPPoE secrets — clearer O/0, I/l than proportional UI fonts. */
const CREDENTIAL_MONO_FONT =
  'ui-monospace, "Cascadia Mono", "Segoe UI Mono", "Roboto Mono", Menlo, Monaco, Consolas, "Liberation Mono", monospace';

const PAYMENT_TYPE_OPTIONS = [
  "Cash / EFT",
  "D/O 1st",
  "D/O 2nd",
  "D/O 3rd",
  "D/O 5th",
  "D/O 7th",
  "D/O 15th",
  "D/O 25th",
  "D/O 31st",
];


const parseTimestampMs = (raw) => {
  if (!raw || raw === "Never") return null;
  const text = String(raw).trim();
  const naive = text.match(/^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2}):(\d{2})$/);
  if (naive) {
    const [, year, month, day, hours, minutes, seconds] = naive;
    return Date.UTC(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hours),
      Number(minutes),
      Number(seconds)
    );
  }
  const parsed = new Date(text);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.getTime();
};

const PDF_PREVIEW_CONTENT_SX = {
  flex: "1 1 auto",
  minHeight: 0,
  overflow: "hidden",
  overflowY: "hidden",
  display: "flex",
  flexDirection: "column",
  p: 0,
};

const parseInvoiceIdFromStatementRow = (row) => {
  const transaction = String(row?.transaction || "").trim().toLowerCase();
  if (!transaction.startsWith("invoice")) {
    return 0;
  }
  const refDigits = String(row?.ref || "").match(/\d+/);
  if (refDigits) {
    return Number(refDigits[0] || 0);
  }
  const txDigits = String(row?.transaction || "").match(/\d+/);
  return txDigits ? Number(txDigits[0] || 0) : 0;
};

const parseInvoiceDocumentNumberFromRow = (row) => {
  const refDigits = String(row?.ref || "").match(/\d+/);
  if (refDigits) {
    return String(refDigits[0] || "");
  }
  const txDigits = String(row?.transaction || "").match(/\d+/);
  return txDigits ? String(txDigits[0] || "") : "";
};

const isQuotationTransaction = (transaction) => {
  const tx = String(transaction || "").trim().toLowerCase();
  return tx.startsWith("quotation") || tx.startsWith("quote");
};

const parseQuoteIdFromStatementRow = (row) => {
  const transaction = String(row?.transaction || "").trim().toLowerCase();
  if (!isQuotationTransaction(transaction)) {
    return 0;
  }
  const refDigits = String(row?.ref || "").match(/\d+/);
  if (refDigits) {
    return Number(refDigits[0] || 0);
  }
  const txDigits = String(row?.transaction || "").match(/\d+/);
  return txDigits ? Number(txDigits[0] || 0) : 0;
};

const parseQuoteDocumentNumberFromRow = (row) => {
  if (!isQuotationTransaction(row?.transaction)) {
    return "";
  }
  return parseInvoiceDocumentNumberFromRow(row);
};

// Priority badge component
const PriorityBadge = ({ priority }) => {
  return (
    <MDBox
      display="inline-flex"
      alignItems="center"
      justifyContent="center"
      px={1.5}
      py={0.5}
      borderRadius="lg"
      sx={{
        backgroundColor: priority.bgColor,
        color: priority.textColor,
        minWidth: "80px",
        textAlign: "center",
      }}
    >
      <MDTypography variant="caption" fontWeight="medium">
        {priority.label}
      </MDTypography>
    </MDBox>
  );
};

function CustomerView({ portalVariant = "admin", customerId: customerIdProp = null, clientAccessToken = null }) {
  const theme = useTheme();
  const isMobileView = useMediaQuery(theme.breakpoints.down("md"));
  const { customerId: routeCustomerId } = useParams();
  const customerId = customerIdProp ?? routeCustomerId;
  const isClientPortal = portalVariant === "client";
  const wrapCustomerViewShell = (content) => {
    if (isClientPortal) {
      return content;
    }

    return (
      <DashboardLayout>
        <DashboardNavbar />
        {content}
      </DashboardLayout>
    );
  };
  const navigate = useNavigate();
  const location = useLocation();
  const returnUrl = location.state?.returnUrl || "/customers/list";
  const [activeTab, setActiveTab] = useState(0);
  const [activeCustomerTab, setActiveCustomerTab] = useState(0);
  const [customerData, setCustomerData] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [customerContractsData, setCustomerContractsData] = useState([]);
  const [pppoeUsers, setPppoeUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [routers, setRouters] = useState([]);
  const [cpes, setCpes] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [ticketsError, setTicketsError] = useState("");
  const [pingStatus, setPingStatus] = useState({});
  const [pppoeUptimeByIp, setPppoeUptimeByIp] = useState({});
  const [cpePingStatus, setCpePingStatus] = useState({});
  const [cpeDiagnosticsByIp, setCpeDiagnosticsByIp] = useState({});
  const [cpeHistoryModalOpen, setCpeHistoryModalOpen] = useState(false);
  const [selectedCpeHistory, setSelectedCpeHistory] = useState([]);
  const [selectedCpeName, setSelectedCpeName] = useState("");
  const [cpeHistoryLoading, setCpeHistoryLoading] = useState(false);
  const [cpeHistoryError, setCpeHistoryError] = useState("");
  const [cpeSectorAnalysisModal, setCpeSectorAnalysisModal] = useState({
    open: false,
    cpeName: "",
    loading: false,
    error: "",
    snapshot: null,
  });
  const [rebootConfirmLoading, setRebootConfirmLoading] = useState(false);
  const [cpeSpeedTestLoading, setCpeSpeedTestLoading] = useState({});
  const [speedTestHistory, setSpeedTestHistory] = useState([]);
  const [speedTestHistoryLoading, setSpeedTestHistoryLoading] = useState(false);
  const [pppoeZabbixModal, setPppoeZabbixModal] = useState({
    open: false,
    user: null,
    loading: false,
    error: "",
    payload: null,
  });
  const [addRadiusModal, setAddRadiusModal] = useState({
    open: false,
    loading: false,
    saving: false,
    error: "",
    packages: [],
    ipPools: [],
    ips: [],
    form: {
      username: "",
      password: "",
      package: "",
      ipPool: "",
      ipTableId: "",
    },
  });
  const [linkRadiusModal, setLinkRadiusModal] = useState({
    open: false,
    loading: false,
    saving: false,
    error: "",
    options: [],
    form: {
      username: "",
      query: "",
    },
  });
  const [editPppoeModal, setEditPppoeModal] = useState({
    open: false,
    loading: false,
    saving: false,
    error: "",
    packages: [],
    ipPools: [],
    ips: [],
    form: {
      username: "",
      password: "",
      package: "",
      ipPool: "",
      ipTableId: "",
    },
  });
  const [linkDecisionModal, setLinkDecisionModal] = useState({
    open: false,
    username: "",
    linkedCustomers: [],
  });
  const [pppoeDangerConfirmModal, setPppoeDangerConfirmModal] = useState({
    open: false,
    action: null, // "unlink" | "delete"
    step: 1,
    username: "",
  });
  const speedTestRecordedKeyRef = useRef(null);
  const [speedTestModal, setSpeedTestModal] = useState({
    open: false,
    cpe: null,
    deviceId: null,
    testId: null,
    detail: null,
    error: null,
    finished: false,
  });
  const [cpeUispTrendModal, setCpeUispTrendModal] = useState({
    open: false,
    cpe: null,
    windowDays: 2,
    maxPoints: 96,
    loading: false,
    error: "",
    payload: null,
  });
  const [rebootSnackbar, setRebootSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [radiusActionSnackbar, setRadiusActionSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [rebootPingModal, setRebootPingModal] = useState({
    open: false,
    cpe: null,
    phase: "waiting_down",
    lastPing: null,
    lastError: null,
    startedAt: null,
  });
  const rebootPingIntervalRef = useRef(null);
  const rebootPingAbortRef = useRef(false);
  const rebootPingDoneRef = useRef(false);
  const [rebootPingElapsedSec, setRebootPingElapsedSec] = useState(0);
  const [routerPingStatus, setRouterPingStatus] = useState({});
  const [diagnosticsLoading, setDiagnosticsLoading] = useState(false);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [contractsLoading, setContractsLoading] = useState(false);
  const [contractCreateLoading, setContractCreateLoading] = useState(false);
  const [pppoeLoading, setPppoeLoading] = useState(false);
  const [routersLoading, setRoutersLoading] = useState(false);
  const [cpesLoading, setCpesLoading] = useState(false);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [newTicketDialogOpen, setNewTicketDialogOpen] = useState(false);
  const [loadedCustomerTabs, setLoadedCustomerTabs] = useState({
    0: true,
    1: false,
    2: false,
    3: false,
    4: false,
    5: false,
  });
  const [loadedServiceTabs, setLoadedServiceTabs] = useState({ 0: true, 1: false, 2: false });
  const [suspendedStatus, setSuspendedStatus] = useState(0);
  const [balance, setBalance] = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(true);
  const [balanceError, setBalanceError] = useState(null);
  const [billingHistory, setBillingHistory] = useState([]);
  const [billingHistoryLoading, setBillingHistoryLoading] = useState(false);
  const [billingHistoryError, setBillingHistoryError] = useState(null);
  const [showBillingHistory, setShowBillingHistory] = useState(false);
  const [processedBillingData, setProcessedBillingData] = useState([]);
  const [outboundCommunication, setOutboundCommunication] = useState([]);
  const [outboundCommunicationLoading, setOutboundCommunicationLoading] = useState(false);
  const [outboundCommunicationError, setOutboundCommunicationError] = useState(null);
  const [invoicePreviewOpen, setInvoicePreviewOpen] = useState(false);
  const [invoicePreviewLoading, setInvoicePreviewLoading] = useState(false);
  const [invoicePreviewError, setInvoicePreviewError] = useState("");
  const [invoicePreviewUrl, setInvoicePreviewUrl] = useState("");
  const invoicePreviewUrlRef = useRef("");
  const [activeInvoiceId, setActiveInvoiceId] = useState(0);
  const [activeInvoiceDocumentNumber, setActiveInvoiceDocumentNumber] = useState("");
  const [invoiceSendEmail, setInvoiceSendEmail] = useState("");
  const [invoiceSendLoading, setInvoiceSendLoading] = useState(false);
  const [invoiceSendWhatsAppLoading, setInvoiceSendWhatsAppLoading] = useState(false);
  const [invoiceDownloadLoading, setInvoiceDownloadLoading] = useState(false);
  const [quotePreviewOpen, setQuotePreviewOpen] = useState(false);
  const [quotePreviewLoading, setQuotePreviewLoading] = useState(false);
  const [quotePreviewError, setQuotePreviewError] = useState("");
  const [quotePreviewUrl, setQuotePreviewUrl] = useState("");
  const quotePreviewUrlRef = useRef("");
  const [activeQuoteId, setActiveQuoteId] = useState(0);
  const [activeQuoteDocumentNumber, setActiveQuoteDocumentNumber] = useState("");
  const [quoteSendEmail, setQuoteSendEmail] = useState("");
  const [quoteSendLoading, setQuoteSendLoading] = useState(false);
  const [quoteSendWhatsAppLoading, setQuoteSendWhatsAppLoading] = useState(false);
  const [quoteDownloadLoading, setQuoteDownloadLoading] = useState(false);
  const [statementPreviewOpen, setStatementPreviewOpen] = useState(false);
  const [statementPreviewLoading, setStatementPreviewLoading] = useState(false);
  const [statementPreviewError, setStatementPreviewError] = useState("");
  const [statementPreviewUrl, setStatementPreviewUrl] = useState("");
  const statementPreviewUrlRef = useRef("");
  const invoicePreviewIframeRef = useRef(null);
  const quotePreviewIframeRef = useRef(null);
  const statementPreviewIframeRef = useRef(null);
  const [statementDateRange, setStatementDateRange] = useState(() =>
    getDefaultStatementPdfDateRange()
  );
  const [statementStartInput, setStatementStartInput] = useState(
    () => getDefaultStatementPdfDateRange().startDate
  );
  const [statementEndInput, setStatementEndInput] = useState(
    () => getDefaultStatementPdfDateRange().endDate
  );
  const [statementSendEmail, setStatementSendEmail] = useState("");
  const [statementSendLoading, setStatementSendLoading] = useState(false);
  const [statementDownloadLoading, setStatementDownloadLoading] = useState(false);
  const [totalActiveMonthlyFee, setTotalActiveMonthlyFee] = useState(0);
  const [terminationScheduledByContractId, setTerminationScheduledByContractId] = useState({});
  const [showAddContactDialog, setShowAddContactDialog] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState(null);
  const [suspensionToggleLoading, setSuspensionToggleLoading] = useState(false);
  const [editForm, setEditForm] = useState({
    id: "",
    name: "",
    cust_type: "",
    payment_type: "",
    type: "",
    vat: "",
    telephone1: "",
    telephone2: "",
    fax: "",
    email: "",
    address: "",
    postal: "",
    signup_date: "",
    cancelled_at: "",
  });
  const contactsFetchInFlightRef = useRef(false);
  const customerNotesPanelRef = useRef(null);

  const userGroups = (() => {
    try {
      const raw = localStorage.getItem("user");
      const parsed = raw ? JSON.parse(raw) : null;
      return parsed?.groups || [];
    } catch {
      return [];
    }
  })();

  const isManagerOrAdmin = isClientPortal
    ? false
    : userSatisfiesPolicyKey(userGroups, "managerOrAdmin");

  const canAddNotes = !isClientPortal;

  const { canAccessPolicy } = useGroupAccess();
  const canManageContracts = isClientPortal ? false : canAccessPolicy("contractManage");

  const handleEditCustomer = () => {
    if (!customerData) return;
    setEditError(null);
    setEditForm({
      id: customerData?.id ?? "",
      name: customerData?.name ?? "",
      cust_type: customerData?.cust_type ?? "",
      payment_type: customerData?.payment_type ?? "",
      type: customerData?.type ?? "",
      vat: customerData?.vat ?? "",
      telephone1: customerData?.telephone1 ?? "",
      telephone2: customerData?.telephone2 ?? "",
      fax: customerData?.fax ?? "",
      email: customerData?.email ?? "",
      address: customerData?.address ?? "",
      postal: customerData?.postal ?? "",
      signup_date: toHtmlDateInputValue(customerData?.signup_date),
      cancelled_at: toHtmlDateInputValue(customerData?.cancelled_at),
    });
    setIsEditModalOpen(true);
  };

  const handleSaveEditCustomer = async () => {
    if (!customerData) return;

    try {
      setEditSaving(true);
      setEditError(null);

      const toNullIfEmpty = (value) => {
        const v = typeof value === "string" ? value.trim() : value;
        return v === "" ? null : v;
      };

      const payload = {
        id: toNullIfEmpty(editForm.id),
        name: toNullIfEmpty(editForm.name),
        cust_type: toNullIfEmpty(editForm.cust_type),
        payment_type: toNullIfEmpty(editForm.payment_type),
        type: toNullIfEmpty(editForm.type),
        vat: toNullIfEmpty(editForm.vat),
        telephone1: toNullIfEmpty(editForm.telephone1),
        telephone2: toNullIfEmpty(editForm.telephone2),
        fax: toNullIfEmpty(editForm.fax),
        email: toNullIfEmpty(editForm.email),
        address: toNullIfEmpty(editForm.address),
        postal: toNullIfEmpty(editForm.postal),
        signup_date: toNullIfEmpty(editForm.signup_date),
      };

      if (editForm.cancelled_at) {
        payload.cancelled_at = toNullIfEmpty(editForm.cancelled_at);
      }

      const response = await api.post(
        `/db/customers/${customerData.customer_id}/update_with_billing`,
        payload
      );
      setCustomerData((prev) =>
        prev ? { ...prev, ...(response?.local_update || payload) } : prev
      );

      const billingSync = response?.billing_sync;
      const billingSyncFailed =
        billingSync?.attempted === true && billingSync?.success === false;

      if (billingSyncFailed) {
        setEditError(
          `Customer updated locally, but billing sync failed: ${
            billingSync?.message || "Unknown billing error."
          }`
        );
        return;
      }

      setIsEditModalOpen(false);
    } catch (err) {
      console.error("Error saving customer edits:", err);
      setEditError(err?.message || "Failed to save customer edits.");
    } finally {
      setEditSaving(false);
    }
  };

  const handleCloseEditModal = () => {
    if (editSaving) return;
    setIsEditModalOpen(false);
  };

  const refreshSuspendedStatus = async () => {
    try {
      const suspendedResponse = await api.post("/db/customer_suspend/filter", {
        filters: { customer_id: parseInt(customerId) },
      });

      const nextSuspended =
        suspendedResponse && suspendedResponse.length > 0
          ? Number(suspendedResponse[0].suspended || 0)
          : 0;

      setSuspendedStatus(nextSuspended);
    } catch (err) {
      console.warn("Failed to refresh suspended status:", err);
    }
  };

  const updateCustomerCustType = async (custType) => {
    if (!customerData?.customer_id) return;
    const response = await api.post(`/db/customers/${customerData.customer_id}/update_with_billing`, {
      cust_type: custType,
    });
    const nextCustomer = response?.local_update || { cust_type: custType };
    setCustomerData((prev) => (prev ? { ...prev, ...nextCustomer } : prev));
    setEditForm((prev) => ({ ...prev, cust_type: custType }));
  };

  const handlePutCustomerOnHold = async () => {
    if (!customerData) return;

    try {
      setSuspensionToggleLoading(true);
      await suspendCustomer({
        customerId: customerData.customer_id,
        description: "On hold until further notice (customer view)",
        actionDateTime: new Date().toISOString(),
      });
      await updateCustomerCustType(CUST_TYPE_ON_HOLD);
      await refreshSuspendedStatus();
      setSuspendedStatus(1);
    } catch (err) {
      console.error("Failed to put customer on hold:", err);
    } finally {
      setSuspensionToggleLoading(false);
    }
  };

  const handleToggleCustomerSuspension = async () => {
    if (!customerData) return;

    try {
      setSuspensionToggleLoading(true);

      const targetSuspended = suspendedStatus ? false : true;

      if (targetSuspended) {
        await suspendCustomer({
          customerId: customerData.customer_id,
          description: "Suspended from customer view modal",
          actionDateTime: new Date().toISOString(),
        });
      } else {
        await activateCustomer({
          customerId: customerData.customer_id,
          description: "Activated from customer view modal",
          actionDateTime: new Date().toISOString(),
        });
        if (isOnHoldCustType(customerData.cust_type)) {
          await updateCustomerCustType("Active");
        }
      }

      await refreshSuspendedStatus();
      if (!targetSuspended) {
        setCustomerData((prev) => (prev ? { ...prev, cancelled_at: null } : prev));
      }
    } catch (err) {
      console.error("Failed to toggle customer suspension:", err);
    } finally {
      setSuspensionToggleLoading(false);
    }
  };

  const handleCopyToClipboard = async (text) => {
    if (!navigator.clipboard) {
      console.error('Clipboard API not available');
      // Optionally: show an alert to the user
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      console.log('Text copied to clipboard:', text);
      // Optionally: show a success message to the user (e.g., using a Snackbar)
    } catch (err) {
      console.error('Failed to copy text: ', err);
      // Optionally: show an error message to the user
    }
  };

  const renderLoadingTabLabel = (label, isLoading) => (
    <MDBox display="flex" alignItems="center" gap={1}>
      <span>{label}</span>
      {isLoading && <CircularProgress size={12} thickness={6} color="inherit" />}
    </MDBox>
  );

  const renderMainTabLabel = (iconName, label, isLoading = false) => (
    <MDBox display="flex" alignItems="center" gap={1}>
      <Icon fontSize="small">{iconName}</Icon>
      <span>{label}</span>
      {isLoading && <CircularProgress size={12} thickness={6} color="inherit" />}
    </MDBox>
  );

  const customerMainTabRows = [
    [
      { index: 0, icon: "badge", label: "Customer Details", isLoading: loading },
      { index: 1, icon: "contacts", label: "Contacts", isLoading: contactsLoading },
      { index: 2, icon: "chat", label: "Messages" },
    ],
    [
      { index: 3, icon: "confirmation_number", label: "Tickets", isLoading: ticketsLoading },
      { index: 4, icon: "description", label: "Contracts", isLoading: contractsLoading },
      {
        index: 5,
        icon: "router",
        label: "Services",
        isLoading: pppoeLoading || routersLoading || cpesLoading,
      },
    ],
  ];
  const customerMainTabs = customerMainTabRows.flat();
  const customerDetailsFieldGridSx = {
    display: "grid",
    gridTemplateColumns: { xs: "1fr", sm: "140px 1fr" },
    gap: 1,
    "& > *": {
      minHeight: { xs: "auto", sm: "32px" },
      display: "flex",
      alignItems: { xs: "flex-start", sm: "center" },
    },
  };
  const customerDetailsLabelValueSx = {
    display: "flex",
    flexDirection: { xs: "column", sm: "row" },
    gap: { xs: 0.25, sm: 0.5 },
    alignItems: { xs: "flex-start", sm: "baseline" },
  };

  const renderCustomerMainTabButton = ({ index, icon, label, isLoading = false }) => {
    const isSelected = activeCustomerTab === index;
    return (
      <MDButton
        key={index}
        role="tab"
        aria-selected={isSelected}
        onClick={() => handleCustomerTabChange(null, index)}
        variant="text"
        color="dark"
        fullWidth
        sx={{
          minHeight: { xs: 48, md: 42 },
          py: 1,
          px: { xs: 1, md: 1.5 },
          fontWeight: 600,
          fontSize: { xs: "0.75rem", md: "0.875rem" },
          color: isSelected ? "#1A73E8" : "#344767",
          bgcolor: isSelected ? "#ffffff" : "transparent",
          borderRadius: 1.5,
          boxShadow: isSelected ? "0 1px 4px rgba(0,0,0,0.08)" : "none",
          justifyContent: "center",
          textTransform: "none",
          whiteSpace: "normal",
          lineHeight: 1.2,
          "&:hover": {
            bgcolor: isSelected ? "#ffffff" : "rgba(0, 0, 0, 0.04)",
          },
        }}
      >
        {renderMainTabLabel(icon, label, isLoading)}
      </MDButton>
    );
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    if (!loadedCustomerTabs[5]) return;
    const loadSubTab = async () => {
      try {
        if (pppoeUsers.length === 0) await fetchPppoeUsersData();
        if (newValue === 1) {
          let routerList = routers;
          if (!loadedServiceTabs[1]) {
            routerList = await fetchRoutersData();
            setLoadedServiceTabs((prev) => ({ ...prev, 1: true }));
          } else if (isClientPortal && routerList.length === 0) {
            routerList = await fetchRoutersData();
          }
          if (isClientPortal && routerList.length > 0) {
            await diagnoseRoutersOnly(routerList);
          }
        }
        if (newValue === 2) {
          let cpeList = cpes;
          if (!loadedServiceTabs[2]) {
            cpeList = await fetchCpesData();
            setLoadedServiceTabs((prev) => ({ ...prev, 2: true }));
          } else if (isClientPortal && cpeList.length === 0) {
            cpeList = await fetchCpesData();
          }
          if (isClientPortal && cpeList.length > 0) {
            await diagnoseCpesOnly(cpeList);
          }
        }
      } catch (error) {
        console.warn("Unable to load customer services sub-tab:", error);
      }
    };
    void loadSubTab();
  };

  const handleCustomerTabChange = (event, newValue) => {
    setActiveCustomerTab(newValue);
    if (newValue === 1 && !loadedCustomerTabs[1]) {
      if (isValidCustomerId(customerId)) {
        void fetchContactsData();
      }
      setLoadedCustomerTabs((prev) => ({ ...prev, 1: true }));
    }
    if (newValue === 2 && !loadedCustomerTabs[2]) {
      setLoadedCustomerTabs((prev) => ({ ...prev, 2: true }));
    }
    if (newValue === 3 && !loadedCustomerTabs[3]) {
      fetchTicketsData();
      setLoadedCustomerTabs((prev) => ({ ...prev, 3: true }));
    }
    if (newValue === 4 && !loadedCustomerTabs[4]) {
      fetchContractsData();
      setLoadedCustomerTabs((prev) => ({ ...prev, 4: true }));
    }
    if (newValue === 5) {
      if (isClientPortal) {
        void (async () => {
          try {
            let users = pppoeUsers;
            if (!loadedCustomerTabs[5]) {
              if (isValidCustomerId(customerId)) {
                users = await fetchPppoeUsersData();
              }
              setLoadedCustomerTabs((prev) => ({ ...prev, 5: true }));
            } else if (users.length === 0 && isValidCustomerId(customerId)) {
              users = await fetchPppoeUsersData();
            }
            if (users.length > 0) {
              await diagnosePppoeOnly(users);
            }
          } catch (error) {
            console.warn("Unable to refresh PPPoE services for client portal:", error);
          }
        })();
        return;
      }
      if (!loadedCustomerTabs[5]) {
        if (isValidCustomerId(customerId)) {
          fetchPppoeUsersData();
        }
        setLoadedCustomerTabs((prev) => ({ ...prev, 5: true }));
      }
    }
  };

  const handleViewContact = (contactId) => {
    if (isClientPortal) {
      navigate(`/customers/contacts/${contactId}`, {
        state: { returnUrl: "/profile", activeCustomerTab: 1 },
      });
      return;
    }
    navigate(`/customers/contacts/${contactId}`, {
      state: { returnUrl: `/customers/view/${customerId}` },
    });
  };

  const updatePingStatus = (username, status, stats = null) => {
    setPingStatus((prev) => ({
      ...prev,
      [username]: {
        status,
        stats,
        lastChecked: new Date().toISOString(),
      },
    }));
  };

  const updateCpePingStatus = (ip, status, stats = null) => {
    setCpePingStatus((prev) => ({
      ...prev,
      [ip]: {
        status,
        stats,
        lastChecked: new Date().toISOString(),
        lastOnlineAt:
          status === "Online" || status === "Degraded"
            ? new Date().toISOString()
            : prev?.[ip]?.lastOnlineAt || null,
      },
    }));
  };

  const updateRouterPingStatus = (ip, status, stats = null) => {
    setRouterPingStatus((prev) => ({
      ...prev,
      [ip]: {
        status,
        stats,
        lastChecked: new Date().toISOString(),
      },
    }));
  };

  const stopRebootPingPolling = () => {
    rebootPingAbortRef.current = true;
    rebootPingDoneRef.current = true;
    if (rebootPingIntervalRef.current) {
      clearInterval(rebootPingIntervalRef.current);
      rebootPingIntervalRef.current = null;
    }
  };

  const closeRebootPingModal = () => {
    stopRebootPingPolling();
    setRebootConfirmLoading(false);
    setRebootPingModal({
      open: false,
      cpe: null,
      phase: "waiting_down",
      lastPing: null,
      lastError: null,
      startedAt: null,
    });
  };

  const loadSpeedTestHistoryForCpe = useCallback(
    async (cpeIp) => {
      if (!customerId || !cpeIp) return;
      setSpeedTestHistoryLoading(true);
      try {
        const res = await fetchUispSpeedTestHistory({
          customer_id: parseInt(customerId, 10),
          cpe_ip: cpeIp,
          limit: 50,
        });
        setSpeedTestHistory(Array.isArray(res.items) ? res.items : []);
      } catch {
        setSpeedTestHistory([]);
      } finally {
        setSpeedTestHistoryLoading(false);
      }
    },
    [customerId]
  );

  useEffect(() => {
    if (!rebootPingModal.open || !rebootPingModal.startedAt) {
      return undefined;
    }
    const tick = () => {
      setRebootPingElapsedSec(
        Math.max(0, Math.floor((Date.now() - rebootPingModal.startedAt) / 1000))
      );
    };
    tick();
    const elapsedId = setInterval(tick, 1000);
    return () => clearInterval(elapsedId);
  }, [rebootPingModal.open, rebootPingModal.startedAt]);

  // Poll only when modal opens for this CPE; updateCpePingStatus omitted from deps intentionally
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!rebootPingModal.open || !rebootPingModal.cpe?.ip) {
      return undefined;
    }
    if (rebootPingModal.phase === "prompt") {
      return undefined;
    }

    rebootPingAbortRef.current = false;
    rebootPingDoneRef.current = false;
    const targetIp = rebootPingModal.cpe.ip;

    const clearPolling = () => {
      if (rebootPingIntervalRef.current) {
        clearInterval(rebootPingIntervalRef.current);
        rebootPingIntervalRef.current = null;
      }
    };

    let inFlight = false;

    const runPing = async () => {
      if (rebootPingAbortRef.current || rebootPingDoneRef.current) return;
      if (inFlight) return;
      inFlight = true;
      try {
        const pingResponse = await api.post("/ros/ping", {
          ip: "127.0.0.1",
          target: targetIp,
          count: 4,
          timeout: 2000,
        });
        if (rebootPingAbortRef.current) return;

        const online = (pingResponse.packets_received || 0) > 0;

        setRebootPingModal((prev) => {
          if (!prev.open || !prev.cpe || rebootPingAbortRef.current) return prev;

          if (prev.phase === "waiting_down") {
            if (!online) {
              return { ...prev, phase: "waiting_up", lastPing: pingResponse, lastError: null };
            }
            return { ...prev, lastPing: pingResponse, lastError: null };
          }

          if (prev.phase === "waiting_up" && online) {
            rebootPingDoneRef.current = true;
            clearPolling();
            const status = pingResponse.packet_loss === 0 ? "Online" : "Degraded";
            updateCpePingStatus(targetIp, status, {
              loss: pingResponse.packet_loss,
              averagePingTime: pingResponse.average_ping_time,
              jitter: pingResponse.jitter,
              packetsReceived: pingResponse.packets_received,
              packetsTransmitted: pingResponse.packets_transmitted,
            });
            return { ...prev, phase: "complete", lastPing: pingResponse, lastError: null };
          }

          if (prev.phase === "waiting_up") {
            return { ...prev, lastPing: pingResponse, lastError: null };
          }

          return prev;
        });
      } catch (err) {
        if (rebootPingAbortRef.current) return;
        setRebootPingModal((prev) => ({
          ...prev,
          lastError: err?.message || "Ping request failed",
          lastPing: null,
        }));
      } finally {
        inFlight = false;
      }
    };

    runPing();
    rebootPingIntervalRef.current = setInterval(runPing, 3000);

    return () => {
      rebootPingAbortRef.current = true;
      clearPolling();
    };
  }, [rebootPingModal.open, rebootPingModal.cpe?.ip, rebootPingModal.phase]);

  useEffect(() => {
    if (
      !speedTestModal.open ||
      speedTestModal.deviceId == null ||
      speedTestModal.testId == null ||
      speedTestModal.finished
    ) {
      return undefined;
    }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      try {
        const res = await fetchUispSpeedTestStatus(speedTestModal.deviceId, speedTestModal.testId);
        const detail = res.detail;
        const st = detail?.identification?.status;
        const done = st === "finished" || st === "failed" || st === "cancelled";
        setSpeedTestModal((prev) => {
          if (!prev.open || prev.deviceId == null || prev.testId == null) return prev;
          return {
            ...prev,
            detail,
            error: null,
            finished: done || prev.finished,
          };
        });
      } catch (err) {
        const msg =
          err?.technicalMessage ||
          err?.responseData?.error ||
          err?.message ||
          "Status request failed.";
        setSpeedTestModal((prev) => ({
          ...prev,
          error: msg,
          finished: true,
        }));
      }
    };
    poll();
    const intervalId = setInterval(poll, 2000);
    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [
    speedTestModal.open,
    speedTestModal.deviceId,
    speedTestModal.testId,
    speedTestModal.finished,
  ]);

  useEffect(() => {
    if (!speedTestModal.open || !speedTestModal.finished || !speedTestModal.detail) {
      return undefined;
    }
    if (!customerId) {
      return undefined;
    }
    const st = speedTestModal.detail?.identification?.status;
    if (!st || !["finished", "failed", "cancelled"].includes(st)) {
      return undefined;
    }
    const key = `${speedTestModal.deviceId}-${speedTestModal.testId}`;
    if (speedTestRecordedKeyRef.current === key) {
      return undefined;
    }
    speedTestRecordedKeyRef.current = key;
    const cpeIp = speedTestModal.cpe?.ip;
    let cancelled = false;
    (async () => {
      try {
        await recordUispSpeedTest({
          customer_id: parseInt(customerId, 10),
          cpe_ip: cpeIp,
          cpe_name: speedTestModal.cpe?.name,
          uisp_device_id: speedTestModal.deviceId,
          uisp_test_id: speedTestModal.testId,
          detail: speedTestModal.detail,
        });
        if (!cancelled && cpeIp) {
          await loadSpeedTestHistoryForCpe(cpeIp);
        }
      } catch {
        speedTestRecordedKeyRef.current = null;
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    speedTestModal.open,
    speedTestModal.finished,
    speedTestModal.detail,
    speedTestModal.deviceId,
    speedTestModal.testId,
    speedTestModal.cpe,
    customerId,
    loadSpeedTestHistoryForCpe,
  ]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      if (!customerId) {
        throw new Error("No customer ID found");
      }

      console.log("Fetching data for customer ID:", customerId);

      // Fetch customer data using filter endpoint
      const customerResponse = await api.post("/db/customers/filter", {
        filters: {
          customer_id: parseInt(customerId),
        },
      });

      if (!customerResponse || customerResponse.length === 0) {
        throw new Error("Customer not found");
      }

      console.log("Customer data response:", customerResponse[0]);
      setCustomerData(customerResponse[0]);

      // Fetch suspended status
      const suspendedResponse = await api.post("/db/customer_suspend/filter", {
        filters: {
          customer_id: parseInt(customerId),
        },
      });

      // Set suspended status (1 if suspended, 0 if not)
      setSuspendedStatus(suspendedResponse && suspendedResponse.length > 0 ? suspendedResponse[0].suspended : 0);

      // Keep initial page load light, but still show summary amount in header.
      const customerContractsSummaryResponse = await api.post("/db/customer_contracts/filter", {
        filters: {
          customer_id: parseInt(customerId),
        },
        columns: ["status", "monthly_fee"],
      });

      const activeFee = (customerContractsSummaryResponse || [])
        .filter((contract) => contract.status === "active" && contract.monthly_fee)
        .reduce((sum, contract) => sum + parseFloat(contract.monthly_fee || 0), 0);
      setTotalActiveMonthlyFee(activeFee);
    } catch (err) {
      console.error("Error loading customer:", err);
      setError(err.message || "Failed to load customer data");
    } finally {
      setLoading(false);
    }
  };

  const fetchContactsData = async () => {
    const parsedCustomerId = parseCustomerId(customerId);
    if (!isValidCustomerId(customerId)) {
      setContacts([]);
      return;
    }
    if (contactsFetchInFlightRef.current) return;
    contactsFetchInFlightRef.current = true;
    try {
      setContactsLoading(true);
      if (isClientPortal) {
        if (!clientAccessToken) {
          setContacts([]);
          return;
        }
        const mergedContacts = await fetchClientzoneContacts(clientAccessToken);
        setContacts(mergedContacts);
        return;
      }
      const mergedContacts = await fetchCustomerMergedContacts(parsedCustomerId);
      setContacts(mergedContacts);
    } catch (err) {
      console.error("Error loading contacts:", err);
      setContacts([]);
    } finally {
      setContactsLoading(false);
      contactsFetchInFlightRef.current = false;
    }
  };

  const handleAddContact = async (newContact) => {
    try {
      if (isClientPortal) {
        if (!clientAccessToken) {
          throw new Error("Session expired. Please sign in again.");
        }
        await createClientzoneContact(
          {
            name: newContact.name,
            telephone: newContact.telephone || null,
            whatsapp: newContact.whatsapp || null,
            email: newContact.email || null,
            primary: newContact.primary,
            get_email: newContact.get_email,
            get_whatsapp: newContact.get_whatsapp,
            get_billing: newContact.get_billing,
            get_notification: newContact.get_notification,
          },
          clientAccessToken
        );
        await fetchContactsData();
        if (String(newContact.primary || "").toLowerCase() === "yes") {
          await fetchInitialData();
        }
        setShowAddContactDialog(false);
        return;
      }

      const contactResponse = await api.post("/db/customer_contacts", {
        name: newContact.name,
        telephone: newContact.telephone || null,
        whatsapp: newContact.whatsapp || null,
        email: newContact.email || null,
      });

      await api.post("/db/customer_contact_links", {
        customer_id: parseInt(customerId),
        customer_contact_id: contactResponse.customer_contact_id,
        primary_contact: newContact.primary,
        get_email: newContact.get_email,
        get_whatsapp: newContact.get_whatsapp,
        get_billing: newContact.get_billing,
        get_notification: newContact.get_notification,
      });

      await fetchContactsData();
      if (String(newContact.primary || "").toLowerCase() === "yes") {
        await fetchInitialData();
      }
      setShowAddContactDialog(false);
    } catch (err) {
      console.error("Error adding contact:", err);
    }
  };

  const fetchContractsData = async () => {
    try {
      setContractsLoading(true);
      const customerContractsResponse = await api.post("/db/customer_contracts/filter", {
        filters: { customer_id: parseInt(customerId) },
        order_by: "created_at DESC",
      });
      const contracts = Array.isArray(customerContractsResponse) ? customerContractsResponse : [];

      const terminationRowsResponse = await api.post("/db/customer_contract_terminate/filter", {
        filters: { customer_id: parseInt(customerId) },
        limit: 500,
      });
      const terminationRows = Array.isArray(terminationRowsResponse)
        ? terminationRowsResponse
        : Array.isArray(terminationRowsResponse?.items)
          ? terminationRowsResponse.items
          : Array.isArray(terminationRowsResponse?.rows)
            ? terminationRowsResponse.rows
            : Array.isArray(terminationRowsResponse?.data)
              ? terminationRowsResponse.data
              : [];
      const scheduledSet = new Set(
        terminationRows
          .filter((row) => Number(row?.is_terminated || 0) !== 1)
          .filter((row) => !["cancelled", "terminated"].includes(String(row?.status || "").toLowerCase()))
          .map((row) => Number(row?.contract_id || 0))
          .filter((id) => id > 0)
      );
      const scheduledMap = {};
      scheduledSet.forEach((id) => {
        scheduledMap[id] = true;
      });
      setTerminationScheduledByContractId(scheduledMap);

      setCustomerContractsData(contracts || []);
      const activeFee = (contracts || [])
        .filter((contract) => contract.status === "active" && contract.monthly_fee)
        .reduce((sum, contract) => sum + parseFloat(contract.monthly_fee || 0), 0);
      setTotalActiveMonthlyFee(activeFee);
    } finally {
      setContractsLoading(false);
    }
  };

  const fetchPppoeUsersData = async () => {
    const parsedCustomerId = parseCustomerId(customerId);
    if (!isValidCustomerId(customerId)) {
      setPppoeUsers([]);
      setPppoeUptimeByIp({});
      return [];
    }

    try {
      setPppoeLoading(true);
      setPppoeUptimeByIp({});
      const services = await api.post(`/db/customers/${parsedCustomerId}/services/pppoe`, {});
      const normalized = Array.isArray(services) ? services : [];
      setPppoeUsers(normalized);

      const ipAddresses = normalized
        .map((row) => row?.ipAddress)
        .filter((ip) => ip && ip !== "Not assigned");
      if (ipAddresses.length > 0) {
        setPppoeZabbixLoading(true);
        api
          .post("/zabbix/pppoe_last_seen", {
            ip_addresses: ipAddresses,
            lookback_seconds: 30 * 24 * 3600,
          })
          .then((zabbixLastSeen) => {
            const rows = Array.isArray(zabbixLastSeen?.rows) ? zabbixLastSeen.rows : [];
            const byIp = rows.reduce((acc, row) => {
              if (row?.ip_address) acc[row.ip_address] = row;
              return acc;
            }, {});
            setPppoeUsers((prev) =>
              prev.map((row) => {
                const zbx = byIp[row?.ipAddress];
                if (!zbx?.last_online_clock) return row;
                return {
                  ...row,
                  zabbixLastOnlineAt: new Date(Number(zbx.last_online_clock) * 1000).toISOString(),
                };
              })
            );
          })
          .catch((zabbixErr) => {
            console.warn("Unable to enrich PPPoE last-seen from Zabbix:", zabbixErr);
          })
          .finally(() => setPppoeZabbixLoading(false));
      }

      return normalized;
    } finally {
      setPppoeLoading(false);
    }
  };

  const [pppoeRefreshingUser, setPppoeRefreshingUser] = useState("");
  const [pppoeZabbixLoading, setPppoeZabbixLoading] = useState(false);

  const refreshSinglePppoeUser = async (username) => {
    const parsedCustomerId = parseCustomerId(customerId);
    if (!isValidCustomerId(customerId) || !username) return;
    try {
      setPppoeRefreshingUser(username);
      const services = await api.post(
        `/db/customers/${parsedCustomerId}/services/pppoe?live=1`,
        {}
      );
      const freshRow = (Array.isArray(services) ? services : []).find(
        (row) => row?.username === username
      );
      if (freshRow) {
        setPppoeUsers((prev) =>
          prev.map((u) => (u.username === username ? { ...u, ...freshRow, snapshotAt: undefined } : u))
        );
      }
    } catch (err) {
      console.warn("Failed to live-refresh PPPoE user:", err);
    } finally {
      setPppoeRefreshingUser("");
    }
  };

  const generateRadiusPassword = () => {
    // Keep all digits, but exclude ambiguous letters (I/O/l/o/S/Z/B/G/q).
    const alphabet = "abcdefghijkmnprstuvwxyzACDEFHJKLMNPQRTUVWXY1234567890";
    let result = "";
    for (let index = 0; index < 8; index += 1) {
      result += alphabet.charAt(Math.floor(Math.random() * alphabet.length));
    }
    return result;
  };

  const buildRadiusUsernameFromCustomerName = (fullName) => {
    const raw = String(fullName || "").trim();
    if (!raw) return "";
    const parts = raw.split(/\s+/).filter(Boolean);
    if (parts.length === 0) return "";
    const first = parts[0] || "";
    const last = parts[parts.length - 1] || "";
    const toSafeToken = (value) => String(value).toLowerCase().replace(/[^a-z0-9]/g, "");
    const firstToken = toSafeToken(first);
    const lastToken = toSafeToken(last);
    if (firstToken && lastToken) return `${firstToken}.${lastToken}`;
    return firstToken || lastToken || "";
  };

  const sanitizeRadiusUsernameInput = (value) =>
    String(value || "")
      .toLowerCase()
      .replace(/\s+/g, "")
      .replace(/[^a-z0-9._@-]/g, "");

  const openAddRadiusModal = async () => {
    const suggestedUsername = buildRadiusUsernameFromCustomerName(customerData?.name);
    setAddRadiusModal((prev) => ({
      ...prev,
      open: true,
      loading: true,
      error: "",
      ips: [],
      form: {
        username: sanitizeRadiusUsernameInput(suggestedUsername),
        password: generateRadiusPassword(),
        package: "",
        ipPool: "",
        ipTableId: "",
      },
    }));
    try {
      const response = await api.get("/radius/options");
      setAddRadiusModal((prev) => ({
        ...prev,
        loading: false,
        packages: Array.isArray(response?.packages) ? response.packages : [],
        ipPools: Array.isArray(response?.ip_pools) ? response.ip_pools : [],
      }));
    } catch (modalError) {
      setAddRadiusModal((prev) => ({
        ...prev,
        loading: false,
        error: modalError?.message || "Failed to load Radius options.",
      }));
    }
  };

  const loadIpPoolIps = async (ipPool, includeUsername = "") => {
    if (!ipPool) {
      setAddRadiusModal((prev) => ({ ...prev, ips: [] }));
      return;
    }
    setAddRadiusModal((prev) => ({ ...prev, loading: true, error: "", ips: [] }));
    try {
      const response = await api.post("/radius/ip_pool_ips", {
        ip_pool: ipPool,
        include_username: includeUsername || undefined,
      });
      setAddRadiusModal((prev) => ({
        ...prev,
        loading: false,
        ips: Array.isArray(response) ? response : [],
      }));
    } catch (modalError) {
      setAddRadiusModal((prev) => ({
        ...prev,
        loading: false,
        error: modalError?.message || "Failed to load pool IPs.",
      }));
    }
  };

  const submitAddRadiusUser = async () => {
    const payload = {
      customer_id: parseInt(customerId, 10),
      username: addRadiusModal.form.username?.trim(),
      password: addRadiusModal.form.password,
      package: addRadiusModal.form.package,
      ip_table_id: addRadiusModal.form.ipTableId,
    };
    payload.username = sanitizeRadiusUsernameInput(payload.username);
    if (!payload.username || !payload.password || !payload.package || !payload.ip_table_id) {
      setAddRadiusModal((prev) => ({ ...prev, error: "Please complete all fields." }));
      return;
    }
    try {
      setAddRadiusModal((prev) => ({ ...prev, saving: true, error: "" }));
      await api.post("/radius/users/create", payload);
      setAddRadiusModal((prev) => ({ ...prev, saving: false, open: false }));
      await fetchPppoeUsersData();
      setRadiusActionSnackbar({
        open: true,
        message: `RADIUS user "${payload.username}" created and linked successfully.`,
        severity: "success",
      });
    } catch (modalError) {
      setAddRadiusModal((prev) => ({
        ...prev,
        saving: false,
        error: modalError?.message || "Failed to create Radius user.",
      }));
      setRadiusActionSnackbar({
        open: true,
        message: modalError?.message || "Failed to create Radius user.",
        severity: "error",
      });
    }
  };

  const openEditPppoeModal = async (user) => {
    const username = sanitizeRadiusUsernameInput(user?.username || "");
    if (!username) return;
    setEditPppoeModal((prev) => ({
      ...prev,
      open: true,
      loading: true,
      error: "",
      ips: [],
      form: {
        username,
        password: "",
        package: "",
        ipPool: "",
        ipTableId: "",
      },
    }));
    try {
      const [options, details] = await Promise.all([
        api.get("/radius/options"),
        api.post("/radius/users/edit_details", { username }),
      ]);
      const ipPool = details?.ip_pool || "";
      const ipTableId = details?.ip_table_id != null ? String(details.ip_table_id) : "";
      let ips = [];
      if (ipPool) {
        const ipRows = await api.post("/radius/ip_pool_ips", {
          ip_pool: ipPool,
          include_username: username,
        });
        ips = Array.isArray(ipRows) ? ipRows : [];
      }
      setEditPppoeModal((prev) => ({
        ...prev,
        loading: false,
        packages: Array.isArray(options?.packages) ? options.packages : [],
        ipPools: Array.isArray(options?.ip_pools) ? options.ip_pools : [],
        ips,
        form: {
          username,
          password: details?.password || "",
          package: details?.package || "",
          ipPool,
          ipTableId,
        },
      }));
    } catch (modalError) {
      setEditPppoeModal((prev) => ({
        ...prev,
        loading: false,
        error: modalError?.message || "Failed to load PPPoE edit details.",
      }));
    }
  };

  const submitEditPppoeUser = async () => {
    const payload = {
      username: sanitizeRadiusUsernameInput(editPppoeModal.form.username),
      password: editPppoeModal.form.password,
      package: editPppoeModal.form.package,
      ip_table_id: editPppoeModal.form.ipTableId,
    };
    if (!payload.username || !payload.password || !payload.package || !payload.ip_table_id) {
      setEditPppoeModal((prev) => ({ ...prev, error: "Please complete all fields." }));
      return;
    }
    try {
      setEditPppoeModal((prev) => ({ ...prev, saving: true, error: "" }));
      await api.post("/radius/users/update", payload);
      setEditPppoeModal((prev) => ({ ...prev, saving: false, open: false }));
      await fetchPppoeUsersData();
      setRadiusActionSnackbar({
        open: true,
        message: `RADIUS user "${payload.username}" updated successfully.`,
        severity: "success",
      });
    } catch (modalError) {
      setEditPppoeModal((prev) => ({
        ...prev,
        saving: false,
        error: modalError?.message || "Failed to update Radius user.",
      }));
      setRadiusActionSnackbar({
        open: true,
        message: modalError?.message || "Failed to update Radius user.",
        severity: "error",
      });
    }
  };

  const openLinkRadiusModal = async () => {
    setLinkRadiusModal((prev) => ({
      ...prev,
      open: true,
      loading: true,
      error: "",
      options: [],
      form: { username: "", query: "" },
    }));
    try {
      const response = await api.post("/radius/users/search", { limit: 30 });
      setLinkRadiusModal((prev) => ({
        ...prev,
        loading: false,
        options: Array.isArray(response) ? response : [],
      }));
    } catch (modalError) {
      setLinkRadiusModal((prev) => ({
        ...prev,
        loading: false,
        error: modalError?.message || "Failed to load existing Radius users.",
      }));
    }
  };

  const searchLinkableRadiusUsers = async (queryText) => {
    setLinkRadiusModal((prev) => ({
      ...prev,
      loading: true,
      error: "",
      form: { ...prev.form, query: queryText },
    }));
    try {
      const response = await api.post("/radius/users/search", { query: queryText, limit: 30 });
      setLinkRadiusModal((prev) => ({
        ...prev,
        loading: false,
        options: Array.isArray(response) ? response : [],
      }));
    } catch (modalError) {
      setLinkRadiusModal((prev) => ({
        ...prev,
        loading: false,
        error: modalError?.message || "Failed to search Radius users.",
      }));
    }
  };

  const submitLinkRadiusUser = async () => {
    const payload = {
      customer_id: parseInt(customerId, 10),
      username: linkRadiusModal.form.username?.trim(),
    };
    if (!payload.username) {
      setLinkRadiusModal((prev) => ({ ...prev, error: "Please choose a Radius username." }));
      return;
    }
    try {
      setLinkRadiusModal((prev) => ({ ...prev, saving: true, error: "" }));
      const preview = await api.post("/radius/users/link_preview", payload);
      const alreadyLinkedToCurrent = Boolean(preview?.already_linked_to_current);
      const otherLinkedCustomerIds = Array.isArray(preview?.other_linked_customer_ids)
        ? preview.other_linked_customer_ids
        : [];
      const otherLinkedCustomers = Array.isArray(preview?.other_linked_customers)
        ? preview.other_linked_customers
        : [];

      if (alreadyLinkedToCurrent) {
        setLinkRadiusModal((prev) => ({
          ...prev,
          saving: false,
          error: "Radius user is already linked to this customer.",
        }));
        return;
      }

      if (otherLinkedCustomerIds.length > 0) {
        setLinkRadiusModal((prev) => ({ ...prev, saving: false }));
        setLinkDecisionModal({
          open: true,
          username: payload.username,
          linkedCustomers: otherLinkedCustomers.length > 0
            ? otherLinkedCustomers
            : otherLinkedCustomerIds.map((id) => ({ customer_id: id, code: "", name: "" })),
        });
        return;
      }

      await api.post("/radius/users/link", { ...payload, link_mode: "shared" });
      setLinkRadiusModal((prev) => ({ ...prev, saving: false, open: false }));
      await fetchPppoeUsersData();
      setRadiusActionSnackbar({
        open: true,
        message: `RADIUS user "${payload.username}" linked successfully.`,
        severity: "success",
      });
    } catch (modalError) {
      setLinkRadiusModal((prev) => ({
        ...prev,
        saving: false,
        error: modalError?.message || "Failed to link Radius user.",
      }));
      setRadiusActionSnackbar({
        open: true,
        message: modalError?.message || "Failed to link Radius user.",
        severity: "error",
      });
    }
  };

  const completeLinkRadiusUser = async (linkMode) => {
    const username = linkDecisionModal.username;
    if (!username) return;
    const payload = {
      customer_id: parseInt(customerId, 10),
      username,
      link_mode: linkMode,
    };
    try {
      setLinkRadiusModal((prev) => ({ ...prev, saving: true, error: "" }));
      await api.post("/radius/users/link", payload);
      setLinkDecisionModal({ open: false, username: "", linkedCustomers: [] });
      setLinkRadiusModal((prev) => ({ ...prev, saving: false, open: false }));
      await fetchPppoeUsersData();
      setRadiusActionSnackbar({
        open: true,
        message:
          linkMode === "transfer"
            ? `RADIUS user "${username}" transferred and linked successfully.`
            : `RADIUS user "${username}" linked successfully on both profiles.`,
        severity: "success",
      });
    } catch (modalError) {
      setLinkRadiusModal((prev) => ({
        ...prev,
        saving: false,
        error: modalError?.message || "Failed to link Radius user.",
      }));
      setRadiusActionSnackbar({
        open: true,
        message: modalError?.message || "Failed to link Radius user.",
        severity: "error",
      });
    }
  };

  const performUnlinkPppoeUserFromCurrentCustomer = async (username) => {
    if (!username) return;
    try {
      await api.post("/radius/users/unlink", {
        customer_id: parseInt(customerId, 10),
        username,
      });
      await fetchPppoeUsersData();
      setRadiusActionSnackbar({
        open: true,
        message: `RADIUS user "${username}" unlinked from this customer profile.`,
        severity: "success",
      });
    } catch (unlinkError) {
      setRadiusActionSnackbar({
        open: true,
        message: unlinkError?.message || "Failed to unlink Radius user.",
        severity: "error",
      });
    }
  };

  const performDeletePppoeUserPermanently = async (username) => {
    if (!username) return;
    try {
      await api.post("/radius/users/delete_permanent", {
        username,
      });
      await fetchPppoeUsersData();
      setRadiusActionSnackbar({
        open: true,
        message: `RADIUS user "${username}" permanently deleted.`,
        severity: "success",
      });
    } catch (deleteError) {
      setRadiusActionSnackbar({
        open: true,
        message: deleteError?.message || "Failed to permanently delete Radius user.",
        severity: "error",
      });
    }
  };

  const requestUnlinkPppoeUser = (username) => {
    if (!username) return;
    setPppoeDangerConfirmModal({
      open: true,
      action: "unlink",
      step: 1,
      username,
    });
  };

  const requestDeletePppoeUser = (username) => {
    if (!username) return;
    setPppoeDangerConfirmModal({
      open: true,
      action: "delete",
      step: 1,
      username,
    });
  };

  const closePppoeDangerConfirmModal = () => {
    setPppoeDangerConfirmModal({
      open: false,
      action: null,
      step: 1,
      username: "",
    });
  };

  const advancePppoeDangerConfirmStep = () => {
    setPppoeDangerConfirmModal((prev) => ({ ...prev, step: 2 }));
  };

  const executePppoeDangerAction = async () => {
    const { action, username } = pppoeDangerConfirmModal;
    if (!username || !action) return;
    closePppoeDangerConfirmModal();
    if (action === "unlink") {
      await performUnlinkPppoeUserFromCurrentCustomer(username);
      return;
    }
    await performDeletePppoeUserPermanently(username);
  };

  const fetchRoutersData = async () => {
    try {
      setRoutersLoading(true);
      const routersResponse = await api.post(
        `/db/customers/${parseInt(customerId)}/services/routers`,
        {}
      );
      const normalized = Array.isArray(routersResponse) ? routersResponse : [];
      setRouters(normalized);
      return normalized;
    } catch (error) {
      console.warn("Unable to load router services:", error);
      setRouters([]);
      return [];
    } finally {
      setRoutersLoading(false);
    }
  };

  const buildLocalCpeDiagnostics = (cpe) => {
    const signal =
      cpe?.latestSignal !== undefined && cpe?.latestSignal !== null && cpe?.latestSignal !== ""
        ? Number(cpe.latestSignal)
        : null;
    const pingMs =
      cpe?.latestPingMs !== undefined && cpe?.latestPingMs !== null && cpe?.latestPingMs !== ""
        ? Number(cpe.latestPingMs)
        : null;
    const pollStatus = String(cpe?.latestPollStatus || "").trim().toLowerCase();
    const ssid = cpe?.latestSsid || null;
    const issues = [];
    if (pollStatus && pollStatus !== "online") issues.push("cpe_poll_offline");
    return {
      ip: cpe?.ip,
      name: cpe?.name,
      ssid,
      diagnostics_source: "cpe_info",
      diagnostics_available: true,
      reason: "Local cpe_info poll history",
      is_healthy: pollStatus === "online" && issues.length === 0,
      issues,
      metrics: {
        signal_dbm: Number.isFinite(signal) ? signal : null,
        cpe_rx_signal_dbm: Number.isFinite(signal) ? signal : null,
        ssid,
        uisp_model: cpe?.type || "CPE",
        ethernet_link: "unknown",
        last_poll_ping_ms: Number.isFinite(pingMs) ? pingMs : null,
        last_poll_status: pollStatus || null,
        last_poll_at: cpe?.latestOnlineCheckIn || null,
      },
      thresholds: {
        min_signal_dbm: -65,
        max_signal_dbm: -50,
      },
    };
  };

  const fetchCpesData = async () => {
    try {
      setCpesLoading(true);
      setCpeDiagnosticsByIp({});
      const cpesResponse = await api.post(
        `/db/customers/${parseInt(customerId)}/services/cpes`,
        {}
      );
      const normalized = Array.isArray(cpesResponse) ? cpesResponse : [];
      setCpes(normalized);

      // Always seed card metrics/status from local cpe_info (no UISP/Zabbix on load).
      const diagSeed = {};
      const pingSeed = {};
      for (const cpe of normalized) {
        if (!cpe?.ip || cpe.ip === "none") continue;
        diagSeed[cpe.ip] = buildLocalCpeDiagnostics(cpe);
        const poll = String(cpe.latestPollStatus || "").trim().toLowerCase();
        if (poll === "online") {
          pingSeed[cpe.ip] = {
            status: "Online",
            stats: cpe.latestPingMs != null ? { averagePingTime: Number(cpe.latestPingMs) } : null,
            lastChecked: cpe.latestOnlineCheckIn || new Date().toISOString(),
          };
        } else if (poll === "offline") {
          pingSeed[cpe.ip] = {
            status: "Offline",
            stats: { loss: 100 },
            lastChecked: cpe.latestOnlineCheckIn || new Date().toISOString(),
          };
        }
      }
      setCpeDiagnosticsByIp(diagSeed);
      setCpePingStatus(pingSeed);
      return normalized;
    } catch (error) {
      console.warn("Unable to load CPE services:", error);
      setCpes([]);
      setCpeDiagnosticsByIp({});
      setCpePingStatus({});
      return [];
    } finally {
      setCpesLoading(false);
    }
  };

  const renderStatus = (status, stats) => {
    const rawStatus = status || "Not Diagnosed";
    const displayStatus =
      isClientPortal && rawStatus === "Not Diagnosed"
        ? diagnosticsLoading
          ? "Testing..."
          : "Checking..."
        : rawStatus;

    if (displayStatus === "Online") {
      return (
        <MDTypography variant="caption" color="success" fontWeight="medium" sx={{ whiteSpace: "nowrap" }}>
          Online
        </MDTypography>
      );
    }

    if (displayStatus === "Offline") {
      return (
        <MDTypography variant="caption" color="error" fontWeight="medium" sx={{ whiteSpace: "nowrap" }}>
          Offline
        </MDTypography>
      );
    }

    if (displayStatus === "Degraded") {
      return (
        <MDBox>
          <MDTypography variant="caption" color="warning" fontWeight="medium">
            Degraded
          </MDTypography>
          {stats && (
            <MDTypography variant="caption" color="text" display="block">
              {stats.loss}% packet loss
            </MDTypography>
          )}
        </MDBox>
      );
    }

    if (displayStatus === "Testing..." || displayStatus === "Checking...") {
      return (
        <MDTypography variant="caption" color="info" fontWeight="medium" sx={{ whiteSpace: "nowrap" }}>
          {displayStatus}
        </MDTypography>
      );
    }

    return (
      <MDTypography
        variant="caption"
        color="text"
        fontWeight="medium"
        sx={{
          whiteSpace: "pre-line",
          lineHeight: 1.1,
          wordBreak: "keep-all",
          overflowWrap: "normal",
          hyphens: "none",
        }}
      >
        {displayStatus === "Not Diagnosed" ? "Not\nDiagnosed" : displayStatus}
      </MDTypography>
    );
  };

  const diagnosePppoeOnly = async (usersInput = null) => {
    const users = Array.isArray(usersInput) ? usersInput : pppoeUsers;
    if (users.length === 0) return;

    try {
      setDiagnosticsLoading(true);

      await Promise.all(
        users.map(async (user) => {
          if (!user.ipAddress || user.ipAddress === "Not assigned") {
            updatePingStatus(user.username, "No IP");
            return;
          }

          try {
            updatePingStatus(user.username, "Testing...");
            const pingResponse = await api.post("/ros/ping", {
              ip: "127.0.0.1",
              target: user.ipAddress,
              count: 4,
              timeout: 2000,
            });

            if (pingResponse.packets_received > 0) {
              const status = pingResponse.packet_loss === 0 ? "Online" : "Degraded";
              updatePingStatus(user.username, status, { loss: pingResponse.packet_loss });
              return;
            }

            updatePingStatus(user.username, "Offline", { loss: 100 });
          } catch (error) {
            updatePingStatus(user.username, "Offline", { error: error.message });
          }
        })
      );

      try {
        const ips = users
          .map((user) => user?.ipAddress)
          .filter((ip) => ip && ip !== "Not assigned");
        if (!isClientPortal && ips.length > 0) {
          const uptimeSummary = await api.post("/zabbix/pppoe_uptime_summary", {
            ip_addresses: ips,
            lookback_seconds: 172800,
          });
          const rows = Array.isArray(uptimeSummary?.rows) ? uptimeSummary.rows : [];
          const byIp = rows.reduce((acc, row) => {
            if (row?.ip_address) {
              acc[row.ip_address] = row;
            }
            return acc;
          }, {});
          setPppoeUptimeByIp(byIp);
        }
      } catch (zabbixUptimeErr) {
        console.warn("Unable to load Zabbix 2-day uptime summaries for PPPoE diagnostics:", zabbixUptimeErr);
      }

    } finally {
      setDiagnosticsLoading(false);
    }
  };

  const diagnoseRoutersOnly = async (routersInput = null) => {
    const routerList = Array.isArray(routersInput) ? routersInput : routers;
    if (routerList.length === 0) return;

    try {
      setDiagnosticsLoading(true);
      await Promise.all(
        routerList.map(async (router) => {
          if (!router.ip || router.ip === "none") {
            updateRouterPingStatus(router.ip, "No IP");
            return;
          }
          try {
            updateRouterPingStatus(router.ip, "Testing...");
            const pingResponse = await api.post("/ros/ping", {
              ip: "127.0.0.1",
              target: router.ip,
              count: 4,
              timeout: 2000,
            });
            if (pingResponse.packets_received > 0) {
              const status = pingResponse.packet_loss === 0 ? "Online" : "Degraded";
              updateRouterPingStatus(router.ip, status, { loss: pingResponse.packet_loss });
              return;
            }
            updateRouterPingStatus(router.ip, "Offline", { loss: 100 });
          } catch (error) {
            updateRouterPingStatus(router.ip, "Offline", { error: error.message });
          }
        })
      );
    } finally {
      setDiagnosticsLoading(false);
    }
  };

  const diagnoseCpesOnly = async (cpesInput = null) => {
    const cpeList = Array.isArray(cpesInput) ? cpesInput : cpes;
    if (cpeList.length === 0) return;

    try {
      setDiagnosticsLoading(true);
      await Promise.all(
        cpeList.map(async (cpe) => {
          if (!cpe.ip || cpe.ip === "none") {
            updateCpePingStatus(cpe.ip, "No IP");
            return;
          }
          try {
            updateCpePingStatus(cpe.ip, "Testing...");
            const [diagResponse, pingResponse] = await Promise.all([
              api.post("/ros/cpe_diagnostics", {
                ip: cpe.ip,
                cpe_name: cpe.name || undefined,
              }),
              api.post("/ros/ping", {
                ip: "127.0.0.1",
                target: cpe.ip,
                count: 4,
                timeout: 2000,
              }),
            ]);

            // Live path may return cpe_info fallback when UISP/SNMP are offline.
            const effectiveDiag =
              diagResponse && diagResponse.diagnostics_available !== false
                ? diagResponse
                : buildLocalCpeDiagnostics(cpe);

            setCpeDiagnosticsByIp((prev) => ({
              ...prev,
              [cpe.ip]: effectiveDiag,
            }));

            if (pingResponse.packets_received > 0) {
              const status = pingResponse.packet_loss === 0 ? "Online" : "Degraded";
              updateCpePingStatus(cpe.ip, status, {
                loss: pingResponse.packet_loss,
                averagePingTime: pingResponse.average_ping_time,
                jitter: pingResponse.jitter,
                packetsReceived: pingResponse.packets_received,
                packetsTransmitted: pingResponse.packets_transmitted,
              });
              return;
            }

            // Live ping offline — still prefer poll status from cpe_info when present.
            const poll = String(effectiveDiag?.metrics?.last_poll_status || cpe.latestPollStatus || "")
              .trim()
              .toLowerCase();
            if (poll === "online") {
              updateCpePingStatus(cpe.ip, "Online", {
                averagePingTime: effectiveDiag?.metrics?.last_poll_ping_ms ?? cpe.latestPingMs,
              });
            } else {
              updateCpePingStatus(cpe.ip, "Offline", { loss: 100 });
            }
          } catch (diagnosticsError) {
            // UISP/Zabbix/SNMP path failed hard — keep card useful from local cpe_info.
            setCpeDiagnosticsByIp((prev) => ({
              ...prev,
              [cpe.ip]: buildLocalCpeDiagnostics(cpe),
            }));
            try {
              const pingResponse = await api.post("/ros/ping", {
                ip: "127.0.0.1",
                target: cpe.ip,
                count: 4,
                timeout: 2000,
              });

              if (pingResponse.packets_received > 0) {
                const status = pingResponse.packet_loss === 0 ? "Online" : "Degraded";
                updateCpePingStatus(cpe.ip, status, {
                  loss: pingResponse.packet_loss,
                  averagePingTime: pingResponse.average_ping_time,
                  jitter: pingResponse.jitter,
                  packetsReceived: pingResponse.packets_received,
                  packetsTransmitted: pingResponse.packets_transmitted,
                });
                return;
              }

              const poll = String(cpe.latestPollStatus || "").trim().toLowerCase();
              updateCpePingStatus(
                cpe.ip,
                poll === "online" ? "Online" : "Offline",
                poll === "online"
                  ? { averagePingTime: cpe.latestPingMs }
                  : { loss: 100 }
              );
            } catch (pingError) {
              const poll = String(cpe.latestPollStatus || "").trim().toLowerCase();
              updateCpePingStatus(
                cpe.ip,
                poll === "online" ? "Online" : "Offline",
                {
                  error: pingError.message || diagnosticsError.message,
                  ...(poll === "online"
                    ? { averagePingTime: cpe.latestPingMs }
                    : { loss: 100 }),
                }
              );
            }
          }
        })
      );
    } finally {
      setDiagnosticsLoading(false);
    }
  };

  const issueLabelMap = {
    cpe_out_of_alignment: "Out of alignment (signal weaker than expected)",
    signal_too_low: "Signal too low (at or below minimum RSSI)",
    signal_too_strong: "Signal too strong",
    link_hardware_mismatch:
      "Expected RX weaker than link budget (check antenna / radio / path for this link)",
    network_cable_unplugged: "Cable unplugged",
    ethernet_speed_below_100mbps: "Ethernet below 100 Mbps",
    legacy_non_ac_radio:
      "Radio is not 802.11ac (Wi‑Fi 5). This hardware should be upgraded for better throughput and airtime efficiency.",
  };

  const formatLinkBps = (bps) => {
    if (bps == null || bps === "") return "N/A";
    const n = Number(bps);
    if (Number.isNaN(n) || n <= 0) return "N/A";
    if (n >= 1e9) return `${(n / 1e9).toFixed(2)} Gbps`;
    if (n >= 1e6) return `${(n / 1e6).toFixed(1)} Mbps`;
    if (n >= 1e3) return `${(n / 1e3).toFixed(0)} Kbps`;
    return `${n} bps`;
  };

  const formatSpeedBps = (bps) => {
    if (bps == null || bps === "") return "—";
    const n = Number(bps);
    if (Number.isNaN(n)) return "—";
    if (n < 0) return "—";
    if (n >= 1e9) return `${(n / 1e9).toFixed(2)} Gbps`;
    if (n >= 1e6) return `${(n / 1e6).toFixed(2)} Mbps`;
    if (n >= 1e3) return `${(n / 1e3).toFixed(0)} Kbps`;
    return `${n} bps`;
  };

  const formatLatencyMs = (ms) => {
    if (ms == null || ms === "") return "—";
    const n = Number(ms);
    if (Number.isNaN(n)) return "—";
    return `${n.toFixed(1)} ms`;
  };

  const formatUispStatusLabel = (raw) => {
    if (raw == null || raw === "") return "Unknown";
    const s = String(raw).trim();
    return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase().replace(/_/g, " ");
  };

  const getUispConnectionState = (d) => {
    const m = d?.metrics || {};
    const rawStatus = m.uisp_status;
    const explicit = d?.uisp_connection_active ?? m?.uisp_connection_active;
    let connected = false;
    if (explicit === true) connected = true;
    else if (explicit === false) connected = false;
    else {
      const s = String(rawStatus || "").toLowerCase();
      connected = ["connected", "active", "online", "up"].includes(s);
    }
    return {
      connected,
      statusLabel: formatUispStatusLabel(rawStatus),
    };
  };

  const isCpeUispReadyForReboot = (cpe) => {
    const d = cpeDiagnosticsByIp[cpe.ip];
    if (!d || d.diagnostics_available === false || d.diagnostics_source !== "uisp") return false;
    return getUispConnectionState(d).connected;
  };

  const isCpeUispPathFromDiagnostics = (cpe) => {
    const d = cpeDiagnosticsByIp[cpe.ip];
    return Boolean(d && d.diagnostics_available !== false && d.diagnostics_source === "uisp");
  };

  const openCpeRebootPrompt = (cpe) => {
    if (!isCpeUispReadyForReboot(cpe) || !cpe.ip) return;
    setRebootPingElapsedSec(0);
    setRebootPingModal({
      open: true,
      cpe: { ip: cpe.ip, name: cpe.name || cpe.ip },
      phase: "prompt",
      lastPing: null,
      lastError: null,
      startedAt: null,
    });
  };

  const confirmCpeReboot = async () => {
    const cpe = rebootPingModal.cpe;
    if (!cpe?.ip) return;
    if (!isCpeUispReadyForReboot({ ip: cpe.ip, name: cpe.name })) return;
    setRebootConfirmLoading(true);
    try {
      await requestUispDeviceRestart({
        ip: cpe.ip,
        cpe_name: cpe.name || undefined,
      });
      const startedAt = Date.now();
      setRebootPingModal((prev) => ({
        ...prev,
        phase: "waiting_down",
        lastPing: null,
        lastError: null,
        startedAt,
      }));
      setRebootPingElapsedSec(0);
      setRebootSnackbar({
        open: true,
        message: "Reboot requested — monitoring connectivity…",
        severity: "success",
      });
    } catch (err) {
      const msg =
        err?.technicalMessage ||
        err?.responseData?.error ||
        err?.message ||
        "Reboot request failed.";
      setRebootSnackbar({ open: true, message: msg, severity: "error" });
    } finally {
      setRebootConfirmLoading(false);
    }
  };

  const closeSpeedTestModal = () => {
    const m = speedTestModal;
    if (m.open && m.deviceId != null && m.testId != null && !m.finished) {
      stopUispSpeedTest({ device_id: m.deviceId, test_id: m.testId }).catch(() => {});
    }
    setSpeedTestModal({
      open: false,
      cpe: null,
      deviceId: null,
      testId: null,
      detail: null,
      error: null,
      finished: false,
    });
  };

  const closeCpeUispTrendModal = () => {
    setCpeUispTrendModal({
      open: false,
      cpe: null,
      windowDays: 2,
      maxPoints: 96,
      loading: false,
      error: "",
      payload: null,
    });
  };

  const handleCpeSpeedTest = async (cpe) => {
    if (!isCpeUispReadyForReboot(cpe) || !cpe.ip) return;
    speedTestRecordedKeyRef.current = null;
    loadSpeedTestHistoryForCpe(cpe.ip);
    setCpeSpeedTestLoading((prev) => ({ ...prev, [cpe.ip]: true }));
    try {
      const res = await startUispSpeedTest({ ip: cpe.ip, cpe_name: cpe.name || undefined });
      const start = res.start || {};
      const tid = start.id;
      const deviceId = res.uisp_device_id;
      if (tid == null || deviceId == null) {
        throw new Error("UISP did not return a speed test id.");
      }
      setSpeedTestModal({
        open: true,
        cpe: { ip: cpe.ip, name: cpe.name || cpe.ip },
        deviceId,
        testId: tid,
        detail: null,
        error: null,
        finished: false,
      });
    } catch (err) {
      const msg =
        err?.technicalMessage ||
        err?.responseData?.error ||
        err?.message ||
        "Speed test could not be started.";
      const unsupported =
        err?.status === 422 ||
        /doesn'?t support|does not support|not support.*speed test|udapi speed test/i.test(
          String(err?.technicalMessage || err?.responseData?.error || msg)
        );
      setRebootSnackbar({
        open: true,
        message: msg,
        severity: unsupported ? "warning" : "error",
      });
    } finally {
      setCpeSpeedTestLoading((prev) => ({ ...prev, [cpe.ip]: false }));
    }
  };

  const renderCpeDiagnosticsSection = (diagnostics) => {
    let uispStatus = null;
    if (!diagnostics) {
      uispStatus = (
        <MDTypography variant="caption" color="text" fontWeight="medium" display="block">
          UISP: Not verified — run <strong>Diagnose CPEs</strong> to enable reboot
        </MDTypography>
      );
    } else if (diagnostics.diagnostics_source === "cpe_info") {
      uispStatus = (
        <MDTypography variant="caption" color="warning" fontWeight="medium" display="block">
          Local poll (<strong>cpe_info</strong>)
          {diagnostics.reason ? ` — ${diagnostics.reason}` : " — UISP/SNMP not used for these metrics"}
        </MDTypography>
      );
    } else if (diagnostics.diagnostics_available === false) {
      uispStatus = (
        <MDTypography variant="caption" color="warning" fontWeight="medium" display="block">
          UISP: Unavailable ({diagnostics.reason || "see API"})
        </MDTypography>
      );
    } else if (diagnostics.diagnostics_source === "uisp") {
      const { connected, statusLabel } = getUispConnectionState(diagnostics);
      uispStatus = (
        <MDTypography
          variant="caption"
          color={connected ? "success" : "warning"}
          fontWeight="medium"
          display="block"
        >
          UISP: {statusLabel}
          {!connected && (
            <span>
              {" "}
              — UISP is not connected to this device; reboot and live queries are unavailable.
            </span>
          )}
        </MDTypography>
      );
    } else {
      uispStatus = (
        <MDTypography variant="caption" color="text" fontWeight="medium" display="block">
          UISP: Not used (SNMP / fallback diagnostics)
        </MDTypography>
      );
    }

    const issues = Array.isArray(diagnostics?.issues) ? diagnostics.issues : [];
    const showIssues =
      diagnostics &&
      diagnostics.diagnostics_available !== false &&
      issues.length > 0;

    return (
      <MDBox>
        {uispStatus}
        {showIssues && (
          <MDBox display="flex" flexDirection="column" gap={0.3} mt={0.5}>
            {issues.map((issue) => (
              <MDTypography key={issue} variant="caption" color="error" fontWeight="medium">
                - {issueLabelMap[issue] || issue}
              </MDTypography>
            ))}
          </MDBox>
        )}
      </MDBox>
    );
  };

  const getCpeMetric = (ip, key, fallback = "N/A") => {
    const diag = cpeDiagnosticsByIp?.[ip] || {};
    const value =
      diag?.metrics?.[key] !== undefined && diag?.metrics?.[key] !== null && diag?.metrics?.[key] !== ""
        ? diag.metrics[key]
        : diag?.[key];
    if (value === null || value === undefined || value === "") return fallback;
    return value;
  };

  const openCpeHistoryModal = async (cpe) => {
    if (!cpe?.name) return;
    setSelectedCpeName(cpe.name);
    setSelectedCpeHistory([]);
    setCpeHistoryError("");
    setCpeHistoryModalOpen(true);
    setCpeHistoryLoading(true);
    try {
      const historyResponse = await api.post(
        `/db/customers/${parseInt(customerId)}/services/cpes/${encodeURIComponent(cpe.name)}/history`,
        { limit: 168 }
      );
      setSelectedCpeHistory(Array.isArray(historyResponse) ? historyResponse : []);
    } catch (historyError) {
      setCpeHistoryError(historyError?.message || "Failed to load CPE history.");
    } finally {
      setCpeHistoryLoading(false);
    }
  };

  const openCpeSectorAnalysisModal = async (cpe) => {
    if (!cpe?.name) return;
    setCpeSectorAnalysisModal({
      open: true,
      cpeName: cpe.name,
      loading: true,
      error: "",
      snapshot: null,
    });
    try {
      const snapshot = await api.get(
        `/db/customers/${parseInt(customerId)}/services/cpes/${encodeURIComponent(cpe.name)}/sector-analysis`
      );
      setCpeSectorAnalysisModal((prev) => ({
        ...prev,
        loading: false,
        snapshot,
      }));
    } catch (err) {
      setCpeSectorAnalysisModal((prev) => ({
        ...prev,
        loading: false,
        error: err?.message || "No sector analysis data for this CPE",
      }));
    }
  };

  const closeCpeSectorAnalysisModal = () => {
    setCpeSectorAnalysisModal({
      open: false,
      cpeName: "",
      loading: false,
      error: "",
      snapshot: null,
    });
  };

  const cpeHistoryChart = useMemo(() => {
    if (!Array.isArray(selectedCpeHistory) || selectedCpeHistory.length === 0) {
      return { labels: [], datasets: [] };
    }
    const labels = selectedCpeHistory.map((entry) => formatDateTime(entry.updated));
    const pings = selectedCpeHistory.map((entry) => Number(entry.ping ?? 0));
    return {
      labels,
      datasets: [{ label: "Ping (ms)", color: "info", data: pings }],
    };
  }, [selectedCpeHistory]);

  const pppoeIcmpPingChart = useMemo(() => {
    const series = pppoeZabbixModal.payload?.ping?.series || [];
    if (!Array.isArray(series) || series.length === 0) {
      return { labels: [], datasets: [] };
    }
    return {
      labels: series.map((row) => formatDateTime(new Date(Number(row.clock) * 1000).toISOString())),
      datasets: [
        {
          label: "ICMP ping (%)",
          color: "success",
          data: series.map((row) => Number(row.value ?? 0) * 100),
        },
      ],
    };
  }, [pppoeZabbixModal.payload]);

  const pppoeIcmpResponseChart = useMemo(() => {
    const series = pppoeZabbixModal.payload?.response_time?.series || [];
    if (!Array.isArray(series) || series.length === 0) {
      return { labels: [], datasets: [] };
    }
    return {
      labels: series.map((row) => formatDateTime(new Date(Number(row.clock) * 1000).toISOString())),
      datasets: [
        {
          label: "ICMP response time (ms)",
          color: "info",
          data: series.map((row) => Number(row.value ?? 0) * 1000),
        },
      ],
    };
  }, [pppoeZabbixModal.payload]);

  const pppoePingStats = useMemo(() => {
    const values = (pppoeZabbixModal.payload?.ping?.series || [])
      .map((row) => Number(row.value) * 100)
      .filter((v) => Number.isFinite(v));
    if (values.length === 0) {
      return null;
    }
    const min = Math.min(...values);
    const max = Math.max(...values);
    const avg = values.reduce((sum, v) => sum + v, 0) / values.length;
    return { min, max, avg };
  }, [pppoeZabbixModal.payload]);

  const pppoeResponseStats = useMemo(() => {
    const valuesMs = (pppoeZabbixModal.payload?.response_time?.series || [])
      .map((row) => Number(row.value) * 1000)
      .filter((v) => Number.isFinite(v));
    if (valuesMs.length === 0) {
      return null;
    }
    const min = Math.min(...valuesMs);
    const max = Math.max(...valuesMs);
    const avg = valuesMs.reduce((sum, v) => sum + v, 0) / valuesMs.length;
    return { min, max, avg };
  }, [pppoeZabbixModal.payload]);

  const openPppoeZabbixModal = async (user) => {
    if (!user?.ipAddress || user.ipAddress === "Not assigned") {
      return;
    }
    setPppoeZabbixModal({
      open: true,
      user,
      loading: true,
      error: "",
      payload: null,
    });
    try {
      const payload = await api.post("/zabbix/pppoe_icmp_graph", {
        ip_address: user.ipAddress,
        username: user.username || undefined,
        window_seconds: 172800,
        max_points: 96,
        limit: 1200,
      });
      setPppoeZabbixModal((prev) => ({
        ...prev,
        loading: false,
        payload,
      }));
    } catch (err) {
      setPppoeZabbixModal((prev) => ({
        ...prev,
        loading: false,
        error: err?.message || "Failed to load ICMP history.",
      }));
    }
  };

  const fetchCpeUispTrendSeries = async (cpe, windowDays = 2) => {
    if (!cpe?.ip) return;
    const maxPointsByWindow = {
      2: 96,
      30: 72,
      90: 60,
      180: 48,
    };
    const effectivePoints = maxPointsByWindow[windowDays] || 96;
    setCpeUispTrendModal((prev) => ({
      ...prev,
      open: true,
      cpe,
      windowDays,
      maxPoints: effectivePoints,
      loading: true,
      error: "",
    }));
    try {
      const payload = await api.post("/ros/cpe_uisp_trends", {
        ip: cpe.ip,
        cpe_name: cpe.name || undefined,
        window_days: windowDays,
        max_points: effectivePoints,
      });
      setCpeUispTrendModal((prev) => ({
        ...prev,
        loading: false,
        payload,
      }));
    } catch (err) {
      setCpeUispTrendModal((prev) => ({
        ...prev,
        loading: false,
        payload: null,
        error: err?.responseData?.error || err?.message || "Failed to load UISP trend data.",
      }));
    }
  };

  const openCpeUispTrendModal = async (cpe) => {
    await fetchCpeUispTrendSeries(cpe, 2);
  };

  const speedTestLiveChart = useMemo(() => {
    const detail = speedTestModal.detail;
    if (!detail) {
      return { labels: [], datasets: [] };
    }
    const dlLog = Array.isArray(detail.download?.log) ? detail.download.log : [];
    const ulLog = Array.isArray(detail.upload?.log) ? detail.upload.log : [];
    const len = Math.max(dlLog.length, ulLog.length);
    if (len === 0) {
      return { labels: [], datasets: [] };
    }
    const labels = [];
    const dlMbps = [];
    const ulMbps = [];
    for (let i = 0; i < len; i += 1) {
      const d = dlLog[i];
      const u = ulLog[i];
      const t = d?.time ?? u?.time ?? i;
      labels.push(`${Number(t).toFixed(1)}s`);
      const dBps = Number(d?.speed ?? d?.average ?? 0);
      const uBps = Number(u?.speed ?? u?.average ?? 0);
      dlMbps.push(Number.isFinite(dBps) ? dBps / 1e6 : 0);
      ulMbps.push(Number.isFinite(uBps) ? uBps / 1e6 : 0);
    }
    return {
      labels,
      datasets: [
        { label: "Download (Mbps)", color: "info", data: dlMbps },
        { label: "Upload (Mbps)", color: "success", data: ulMbps },
      ],
    };
  }, [speedTestModal.detail]);

  const cpeUispUptimeChart = useMemo(() => {
    const series = cpeUispTrendModal.payload?.series?.service_uptime_percent || [];
    if (!Array.isArray(series) || series.length === 0) {
      return { labels: [], datasets: [] };
    }
    return {
      labels: series.map((row) => formatDateTime(new Date(Number(row.clock) * 1000).toISOString())),
      datasets: [
        {
          label: "Service uptime (%)",
          color: "success",
          data: series.map((row) => Number(row.value ?? 0)),
        },
      ],
    };
  }, [cpeUispTrendModal.payload]);

  const cpeUispThroughputChart = useMemo(() => {
    const downTp = cpeUispTrendModal.payload?.series?.throughput_downlink_mbps || [];
    const upTp = cpeUispTrendModal.payload?.series?.throughput_uplink_mbps || [];
    const rowsDown = Array.isArray(downTp) ? downTp : [];
    const rowsUp = Array.isArray(upTp) ? upTp : [];
    if (rowsDown.length === 0 && rowsUp.length === 0) {
      return { labels: [], datasets: [] };
    }
    const labelsSource = rowsDown.length > 0 ? rowsDown : rowsUp;
    return {
      labels: labelsSource.map((row) => formatDateTime(new Date(Number(row.clock) * 1000).toISOString())),
      datasets: [
        { label: "Downlink (Mbps)", color: "info", data: rowsDown.map((row) => Number(row.value ?? 0)) },
        { label: "Uplink (Mbps)", color: "warning", data: rowsUp.map((row) => Number(row.value ?? 0)) },
      ],
    };
  }, [cpeUispTrendModal.payload]);

  const cpeUispCapacityChart = useMemo(() => {
    const downCap = cpeUispTrendModal.payload?.series?.capacity_downlink_mbps || [];
    const upCap = cpeUispTrendModal.payload?.series?.capacity_uplink_mbps || [];
    const rowsDown = Array.isArray(downCap) ? downCap : [];
    const rowsUp = Array.isArray(upCap) ? upCap : [];
    if (rowsDown.length === 0 && rowsUp.length === 0) {
      return { labels: [], datasets: [] };
    }
    const labelsSource = rowsDown.length > 0 ? rowsDown : rowsUp;
    return {
      labels: labelsSource.map((row) => formatDateTime(new Date(Number(row.clock) * 1000).toISOString())),
      datasets: [
        { label: "Downlink (Mbps)", color: "info", data: rowsDown.map((row) => Number(row.value ?? 0)) },
        { label: "Uplink (Mbps)", color: "warning", data: rowsUp.map((row) => Number(row.value ?? 0)) },
      ],
    };
  }, [cpeUispTrendModal.payload]);

  const cpeUispSignalChart = useMemo(() => {
    const series = cpeUispTrendModal.payload?.series?.signal_dbm || [];
    if (!Array.isArray(series) || series.length === 0) {
      return { labels: [], datasets: [] };
    }
    return {
      labels: series.map((row) => formatDateTime(new Date(Number(row.clock) * 1000).toISOString())),
      datasets: [
        {
          label: "Signal strength (dBm)",
          color: "error",
          data: series.map((row) => Number(row.value ?? 0)),
        },
      ],
    };
  }, [cpeUispTrendModal.payload]);

  const calcDatasetStats = (dataset = []) => {
    const values = (Array.isArray(dataset) ? dataset : [])
      .map((v) => Number(v))
      .filter((v) => Number.isFinite(v));
    if (values.length === 0) return null;
    const min = Math.min(...values);
    const max = Math.max(...values);
    const avg = values.reduce((sum, v) => sum + v, 0) / values.length;
    return { min, max, avg };
  };

  const cpeUispUptimeStats = useMemo(
    () => calcDatasetStats(cpeUispUptimeChart?.datasets?.[0]?.data || []),
    [cpeUispUptimeChart]
  );
  const cpeUispSignalStats = useMemo(
    () => calcDatasetStats(cpeUispSignalChart?.datasets?.[0]?.data || []),
    [cpeUispSignalChart]
  );
  const cpeUispThroughputDownStats = useMemo(
    () => calcDatasetStats(cpeUispThroughputChart?.datasets?.[0]?.data || []),
    [cpeUispThroughputChart]
  );
  const cpeUispThroughputUpStats = useMemo(
    () => calcDatasetStats(cpeUispThroughputChart?.datasets?.[1]?.data || []),
    [cpeUispThroughputChart]
  );
  const cpeUispCapacityDownStats = useMemo(
    () => calcDatasetStats(cpeUispCapacityChart?.datasets?.[0]?.data || []),
    [cpeUispCapacityChart]
  );
  const cpeUispCapacityUpStats = useMemo(
    () => calcDatasetStats(cpeUispCapacityChart?.datasets?.[1]?.data || []),
    [cpeUispCapacityChart]
  );

  const fetchTicketsData = async () => {
    try {
      setTicketsLoading(true);
      setTicketsError("");
      if (isClientPortal) {
        if (!clientAccessToken) {
          setTickets([]);
          setTicketsError("Session expired. Please sign in again.");
          return;
        }
        const ticketsResponse = await fetchClientzoneTickets(clientAccessToken);
        setTickets(Array.isArray(ticketsResponse) ? ticketsResponse : []);
        return;
      }
      const ticketsResponse = await api.post("/db/tickets/filter", {
        login: localStorage.getItem("user")
          ? JSON.parse(localStorage.getItem("user") || "{}")?.login || ""
          : "",
        filters: {
          customer_id: parseInt(customerId),
          create_datetime__lte: `${new Date().toISOString().split("T")[0]} 23:59:59`,
        },
        order_by: "create_datetime DESC",
      });
      setTickets(ticketsResponse || []);
    } catch (error) {
      console.error("Error loading customer tickets:", error);
      const backendMessage = error?.responseData?.error || error?.technicalMessage || "";
      if (backendMessage.includes("Invalid table name: tickets")) {
        setTicketsError("Tickets are unavailable in this environment.");
      } else {
        setTicketsError(error?.message || "Failed to load tickets.");
      }
      setTickets([]);
    } finally {
      setTicketsLoading(false);
    }
  };

  const handleNewTicketSuccess = async (ticketId) => {
    setNewTicketDialogOpen(false);
    await fetchTicketsData();
    const createdTicketId = Number(ticketId || 0);
    if (isClientPortal && createdTicketId > 0) {
      navigate(`/tickets/view/${createdTicketId}`, {
        state: {
          returnUrl: "/profile",
          activeCustomerTab: 3,
          customerId,
          customerName: customerData?.name,
        },
      });
    }
  };

  const handleCreateContractFromCustomerView = async () => {
    if (!customerId) return;
    try {
      setContractCreateLoading(true);
      const response = await api.post("/db/customer_contracts", {
        customer_id: Number(customerId),
        contract_type: "internet",
        status: "pending",
      });
      const contractId = Number(response?.contract_id || response?.id || response?.insertId || 0);
      if (contractId < 1) {
        throw new Error("Contract draft was created but no contract_id was returned.");
      }
      navigate(`/contracts/edit/${contractId}`, {
        state: {
          returnToContractViewUrl: `/contracts/view/${contractId}`,
          parentViewReturnUrl: location.pathname,
          customerData,
        },
      });
    } catch (createErr) {
      setRadiusActionSnackbar({
        open: true,
        message: createErr?.message || "Failed to create contract draft.",
        severity: "error",
      });
    } finally {
      setContractCreateLoading(false);
    }
  };

  const fetchBalance = async () => {
    try {
      setBalanceLoading(true);
      if (!customerData?.vat_customer_id) {
        throw new Error("No VAT customer ID found");
      }

      const balanceResult = await getCustomerBalance(customerData.vat_customer_id);
      if (!balanceResult.ok) {
        setBalance(null);
        setBalanceError(balanceResult.errorMessage);
        return;
      }
      setBalance({
        balance: balanceResult.balance,
        lastUpdated: balanceResult.lastUpdated,
      });
      setBalanceError(null);
    } catch (err) {
      console.error("Error loading balance:", err);
      setBalanceError(err.message || "Failed to load balance");
      setBalance(null);
    } finally {
      setBalanceLoading(false);
    }
  };

  // Process billing data when it changes.
  // Running balances must seed from the earliest Balance b/f (same as statement PDF /
  // billingAnalysis) — do not start at 0 after dropping opening-balance rows.
  useEffect(() => {
    const historyRows = Array.isArray(billingHistory) ? billingHistory : [];
    if (!historyRows.length) {
      setProcessedBillingData([]);
      return;
    }

    const processedData = processCustomerStatementHistory(historyRows).map((entry) => {
      const raw = entry.raw || entry;
      return {
        datetime: entry.datetime,
        transaction: entry.transaction,
        rawTransaction: entry.transaction,
        ref: entry.ref || "-",
        rawRef: entry.ref || "-",
        invoiceDocumentNumber: parseInvoiceDocumentNumberFromRow(raw),
        invoiceId: parseInvoiceIdFromStatementRow(raw),
        quoteDocumentNumber: parseQuoteDocumentNumberFromRow(raw),
        quoteId: parseQuoteIdFromStatementRow(raw),
        amount: `R ${Number(entry.amount || 0).toFixed(2)}`,
        runningBalance: `R ${Number(entry.runningBalance || 0).toFixed(2)}`,
      };
    });

    setProcessedBillingData(processedData);
  }, [billingHistory]);

  const handleOpenInvoicePreview = useCallback(async (row) => {
    const documentNumber = String(row?.invoiceDocumentNumber || "").trim();
    const customerBillingId = Number(customerData?.vat_customer_id || 0);
    if (!documentNumber || customerBillingId < 1) {
      setInvoicePreviewError("Unable to resolve invoice from this statement row.");
      setInvoicePreviewOpen(true);
      return;
    }
    const previousPreviewUrl = invoicePreviewUrlRef.current;
    if (previousPreviewUrl) {
      window.URL.revokeObjectURL(previousPreviewUrl);
    }
    setInvoicePreviewOpen(true);
    setInvoicePreviewLoading(true);
    setInvoicePreviewError("");
    setInvoicePreviewUrl("");
    try {
      setActiveInvoiceDocumentNumber(documentNumber);
      const resolvedInvoiceId = await resolveInvoiceIdFromDocumentNumber({
        customerId: customerBillingId,
        documentNumber,
      });
      setActiveInvoiceId(resolvedInvoiceId);
      const blob = await getInvoicePdfPreviewBlob(resolvedInvoiceId);
      const objectUrl = window.URL.createObjectURL(blob);
      setInvoicePreviewUrl(objectUrl);
    } catch (err) {
      setInvoicePreviewError(err?.message || "Failed to load invoice preview.");
    } finally {
      setInvoicePreviewLoading(false);
    }
  }, [customerData?.vat_customer_id]);

  const handleOpenQuotePreview = useCallback(async (row) => {
    const documentNumber = String(row?.quoteDocumentNumber || "").trim();
    const customerBillingId = Number(customerData?.vat_customer_id || 0);
    if (!documentNumber || customerBillingId < 1) {
      setQuotePreviewError("Unable to resolve quotation from this statement row.");
      setQuotePreviewOpen(true);
      return;
    }
    const previousPreviewUrl = quotePreviewUrlRef.current;
    if (previousPreviewUrl) {
      window.URL.revokeObjectURL(previousPreviewUrl);
    }
    setQuotePreviewOpen(true);
    setQuotePreviewLoading(true);
    setQuotePreviewError("");
    setQuotePreviewUrl("");
    try {
      setActiveQuoteDocumentNumber(documentNumber);
      const resolvedQuoteId = await resolveQuoteIdFromDocumentNumber({
        customerId: customerBillingId,
        documentNumber,
      });
      setActiveQuoteId(resolvedQuoteId);
      const blob = await getQuotePdfPreviewBlob(resolvedQuoteId);
      const objectUrl = window.URL.createObjectURL(blob);
      setQuotePreviewUrl(objectUrl);
    } catch (err) {
      setQuotePreviewError(err?.message || "Failed to load quotation preview.");
    } finally {
      setQuotePreviewLoading(false);
    }
  }, [customerData?.vat_customer_id]);

  const billingHistoryTable = useMemo(
    () => ({
      columns: [
        {
          Header: "Date (GMT+2)",
          accessor: "datetime",
          width: "20%",
          Cell: ({ value }) => (
            <MDTypography variant="caption" color="text" fontWeight="medium">
              {formatDateTime(value)}
            </MDTypography>
          ),
        },
        {
          Header: "Transaction",
          accessor: "transaction",
          width: "35%",
          Cell: ({ row }) => {
            const invoiceId = Number(row?.original?.invoiceId || 0);
            const quoteId = Number(row?.original?.quoteId || 0);
            const transaction = String(row?.original?.rawTransaction || row?.original?.transaction || "—");
            if (invoiceId < 1 && quoteId < 1) {
              return (
                <MDTypography variant="caption" color="text">
                  {transaction}
                </MDTypography>
              );
            }
            const onOpenPreview =
              invoiceId >= 1
                ? () => handleOpenInvoicePreview(row?.original)
                : () => handleOpenQuotePreview(row?.original);
            return (
              <Link
                component="button"
                type="button"
                underline="hover"
                onClick={onOpenPreview}
                sx={{ fontSize: "0.75rem", fontWeight: 500 }}
              >
                {transaction}
              </Link>
            );
          },
        },
        { Header: "Reference", accessor: "ref", width: "15%" },
        {
          Header: "Amount",
          accessor: "amount",
          width: "15%",
          Cell: ({ value }) => {
            const numericValue = Number(String(value).replace("R ", ""));
            return (
              <MDTypography
                variant="caption"
                color={numericValue > 0 ? "error" : numericValue < 0 ? "success" : "text"}
                fontWeight="medium"
              >
                {value}
              </MDTypography>
            );
          },
        },
        {
          Header: "Running Balance",
          accessor: "runningBalance",
          width: "15%",
          Cell: ({ value }) => {
            const numericValue = Number(String(value).replace("R ", ""));
            return (
              <MDTypography
                variant="caption"
                color={numericValue > 0 ? "error" : numericValue < 0 ? "success" : "text"}
                fontWeight="medium"
              >
                {value}
              </MDTypography>
            );
          },
        },
      ],
      rows: [...processedBillingData].reverse(),
    }),
    [processedBillingData, handleOpenInvoicePreview, handleOpenQuotePreview]
  );

  const outboundCommunicationTable = useMemo(
    () => ({
      columns: [
        { Header: "Date", accessor: "datetime", width: "25%" },
        { Header: "Destination", accessor: "destination", width: "30%" },
        { Header: "Message", accessor: "message", width: "45%" },
      ],
      rows: (Array.isArray(outboundCommunication) ? outboundCommunication : []).map((entry) => ({
        datetime: formatDbDateTime(entry?.datetime || ""),
        destination: entry?.destination || "-",
        message: entry?.message || "-",
      })),
    }),
    [outboundCommunication]
  );

  const fetchBillingHistory = async () => {
    try {
      setBillingHistoryLoading(true);
      if (!customerData?.vat_customer_id) {
        throw new Error("No VAT customer ID found");
      }

      // Get current date and set start date to 2022-11-01
      const endDate = new Date().toISOString().split('T')[0];
      const startDate = '2022-11-01';

      const historyData = await getCustomerStatement(
        customerData.vat_customer_id,
        startDate,
        endDate
      );

      setBillingHistory(Array.isArray(historyData) ? historyData : []);
      setBillingHistoryError(null);
    } catch (err) {
      console.error("Error loading billing history:", err);
      setBillingHistoryError(err.message || "Failed to load billing history");
      setBillingHistory([]);
      setProcessedBillingData([]);
    } finally {
      setBillingHistoryLoading(false);
    }
  };

  const fetchOutboundCommunication = async () => {
    const parsedCustomerId = parseCustomerId(customerId);
    if (!isValidCustomerId(customerId)) {
      setOutboundCommunication([]);
      setOutboundCommunicationError(null);
      return;
    }
    try {
      setOutboundCommunicationLoading(true);
      const rows = await api.post("/db/customer_message/filter", {
        filters: { customer_id: parsedCustomerId },
        order_by: "datetime DESC",
      });
      setOutboundCommunication(Array.isArray(rows) ? rows : []);
      setOutboundCommunicationError(null);
    } catch (err) {
      console.error("Error loading outbound communication:", err);
      setOutboundCommunicationError(err?.message || "Failed to load outbound communication.");
      setOutboundCommunication([]);
    } finally {
      setOutboundCommunicationLoading(false);
    }
  };

  const logWhatsAppOutboundCommunication = async ({
    documentLabel,
    documentRef,
    phone,
    succeeded,
    errorMessage = "",
  }) => {
    const localCustomerId = parseCustomerId(customerData?.customer_id || customerId);
    if (!isValidCustomerId(localCustomerId)) {
      return;
    }
    const ref = String(documentRef || "").trim();
    const refSuffix = ref ? ` ${ref}` : "";
    const destination = String(phone || "").trim() || "unknown";
    const message = succeeded
      ? `Sent ${documentLabel}${refSuffix} via WhatsApp`
      : `Failed ${documentLabel}${refSuffix} via WhatsApp: ${String(errorMessage || "Unknown error").trim()}`;
    await logCustomerCommunication({
      customerId: localCustomerId,
      destination,
      message,
    });
  };

  function handleCloseInvoicePreview() {
    setInvoicePreviewOpen(false);
    setInvoicePreviewError("");
    setInvoiceSendEmail("");
    setActiveInvoiceId(0);
    setActiveInvoiceDocumentNumber("");
    if (invoicePreviewUrl) {
      window.URL.revokeObjectURL(invoicePreviewUrl);
      setInvoicePreviewUrl("");
    }
  }

  function handleCloseQuotePreview() {
    setQuotePreviewOpen(false);
    setQuotePreviewError("");
    setQuoteSendEmail("");
    setActiveQuoteId(0);
    setActiveQuoteDocumentNumber("");
    if (quotePreviewUrl) {
      window.URL.revokeObjectURL(quotePreviewUrl);
      setQuotePreviewUrl("");
    }
  }

  async function handleDownloadInvoice() {
    if (activeInvoiceId < 1) {
      return;
    }
    try {
      setInvoiceDownloadLoading(true);
      const defaultFileName = activeInvoiceDocumentNumber
        ? `NanoTech_Invoice_${activeInvoiceDocumentNumber}.pdf`
        : "";
      await downloadInvoicePdf(activeInvoiceId, defaultFileName);
    } catch (err) {
      setInvoicePreviewError(err?.message || "Failed to download invoice.");
    } finally {
      setInvoiceDownloadLoading(false);
    }
  }

  async function handleDownloadQuote() {
    if (activeQuoteId < 1) {
      return;
    }
    try {
      setQuoteDownloadLoading(true);
      const defaultFileName = activeQuoteDocumentNumber
        ? `NanoTech_Quotation_${activeQuoteDocumentNumber}.pdf`
        : "";
      await downloadQuotePdf(activeQuoteId, defaultFileName);
    } catch (err) {
      setQuotePreviewError(err?.message || "Failed to download quotation.");
    } finally {
      setQuoteDownloadLoading(false);
    }
  }

  async function handleSendInvoiceEmail() {
    if (activeInvoiceId < 1) {
      return;
    }
    try {
      setInvoiceSendLoading(true);
      const response = await sendInvoicePdfByEmailWithContext({
        invoiceId: activeInvoiceId,
        toEmail: invoiceSendEmail,
        customerId: parseCustomerId(customerData?.customer_id || customerId),
        documentNumber: activeInvoiceDocumentNumber,
      });
      const message =
        response?.reply?.message ||
        response?.reply ||
        "Invoice email sent successfully.";
      setRadiusActionSnackbar({
        open: true,
        message: String(message),
        severity: "success",
      });
    } catch (err) {
      setInvoicePreviewError(err?.message || "Failed to email invoice.");
    } finally {
      setInvoiceSendLoading(false);
      await fetchOutboundCommunication();
    }
  }

  async function handleSendInvoiceWhatsApp() {
    if (activeInvoiceId < 1) {
      return;
    }
    const phone = formatPhoneNumber(customerData?.telephone1);
    if (!phone) {
      setInvoicePreviewError(
        "This customer has no valid WhatsApp number (telephone1). Add one on the customer record, then try again."
      );
      return;
    }
    const documentNumber = String(activeInvoiceDocumentNumber || "").trim();
    const documentRef = documentNumber || String(activeInvoiceId);
    try {
      setInvoiceSendWhatsAppLoading(true);
      setInvoicePreviewError("");
      const pdfBlob = invoicePreviewUrl
        ? await fetch(invoicePreviewUrl).then((response) => {
            if (!response.ok) {
              throw new Error("Failed to read the invoice PDF for WhatsApp.");
            }
            return response.blob();
          })
        : await getInvoicePdfPreviewBlob(activeInvoiceId);
      const filename = documentNumber
        ? `NanoTech_Invoice_${documentNumber}.pdf`
        : `invoice_${activeInvoiceId}.pdf`;
      const bodyText = documentNumber
        ? `Your invoice ${documentNumber} is attached.`
        : "Your invoice is attached.";
      const outcome = await sendWhatsAppPdfDocumentTemplate({
        phone,
        pdfBlob,
        filename,
        bodyText,
      });
      if (!outcome.ok) {
        throw new Error(outcome.message || "Failed to send invoice via WhatsApp.");
      }
      await logWhatsAppOutboundCommunication({
        documentLabel: "Invoice",
        documentRef,
        phone,
        succeeded: true,
      });
      setRadiusActionSnackbar({
        open: true,
        message: "Invoice sent via WhatsApp.",
        severity: "success",
      });
    } catch (err) {
      await logWhatsAppOutboundCommunication({
        documentLabel: "Invoice",
        documentRef,
        phone,
        succeeded: false,
        errorMessage: err?.message || "Failed to send invoice via WhatsApp.",
      });
      setInvoicePreviewError(err?.message || "Failed to send invoice via WhatsApp.");
    } finally {
      setInvoiceSendWhatsAppLoading(false);
      await fetchOutboundCommunication();
    }
  }

  async function handleSendQuoteEmail() {
    if (activeQuoteId < 1) {
      return;
    }
    try {
      setQuoteSendLoading(true);
      const response = await sendQuotePdfByEmailWithContext({
        quoteId: activeQuoteId,
        toEmail: quoteSendEmail,
        customerId: parseCustomerId(customerData?.customer_id || customerId),
        documentNumber: activeQuoteDocumentNumber,
      });
      const message =
        response?.reply?.message ||
        response?.reply ||
        "Quotation email sent successfully.";
      setRadiusActionSnackbar({
        open: true,
        message: String(message),
        severity: "success",
      });
    } catch (err) {
      setQuotePreviewError(err?.message || "Failed to email quotation.");
    } finally {
      setQuoteSendLoading(false);
      await fetchOutboundCommunication();
    }
  }

  async function handleSendQuoteWhatsApp() {
    if (activeQuoteId < 1) {
      return;
    }
    const phone = formatPhoneNumber(customerData?.telephone1);
    if (!phone) {
      setQuotePreviewError(
        "This customer has no valid WhatsApp number (telephone1). Add one on the customer record, then try again."
      );
      return;
    }
    const documentNumber = String(activeQuoteDocumentNumber || "").trim();
    const documentRef = documentNumber || String(activeQuoteId);
    try {
      setQuoteSendWhatsAppLoading(true);
      setQuotePreviewError("");
      const pdfBlob = quotePreviewUrl
        ? await fetch(quotePreviewUrl).then((response) => {
            if (!response.ok) {
              throw new Error("Failed to read the quotation PDF for WhatsApp.");
            }
            return response.blob();
          })
        : await getQuotePdfPreviewBlob(activeQuoteId);
      const filename = documentNumber
        ? `NanoTech_Quotation_${documentNumber}.pdf`
        : `quotation_${activeQuoteId}.pdf`;
      const bodyText = documentNumber
        ? `Your quotation ${documentNumber} is attached.`
        : "Your quotation is attached.";
      const outcome = await sendWhatsAppPdfDocumentTemplate({
        phone,
        pdfBlob,
        filename,
        bodyText,
      });
      if (!outcome.ok) {
        throw new Error(outcome.message || "Failed to send quotation via WhatsApp.");
      }
      await logWhatsAppOutboundCommunication({
        documentLabel: "Quotation",
        documentRef,
        phone,
        succeeded: true,
      });
      setRadiusActionSnackbar({
        open: true,
        message: "Quotation sent via WhatsApp.",
        severity: "success",
      });
    } catch (err) {
      await logWhatsAppOutboundCommunication({
        documentLabel: "Quotation",
        documentRef,
        phone,
        succeeded: false,
        errorMessage: err?.message || "Failed to send quotation via WhatsApp.",
      });
      setQuotePreviewError(err?.message || "Failed to send quotation via WhatsApp.");
    } finally {
      setQuoteSendWhatsAppLoading(false);
      await fetchOutboundCommunication();
    }
  }

  const handleCreateQuote = useCallback(() => {
    if (!isValidCustomerId(customerId)) {
      return;
    }
    navigate(`/customers/view/${customerId}/quote`);
  }, [customerId, navigate]);

  const handleEditActiveQuote = useCallback(() => {
    if (!isValidCustomerId(customerId) || activeQuoteId < 1) {
      return;
    }
    handleCloseQuotePreview();
    navigate(`/customers/view/${customerId}/quote/${activeQuoteId}/edit`);
  }, [activeQuoteId, customerId, navigate]);

  const loadStatementPreview = useCallback(
    async (dateRange) => {
      const billingCustomerId = Number(customerData?.vat_customer_id || 0);
      if (billingCustomerId < 1) {
        setStatementPreviewError("No billing customer linked for this account.");
        return;
      }
      const previousPreviewUrl = statementPreviewUrlRef.current;
      if (previousPreviewUrl) {
        window.URL.revokeObjectURL(previousPreviewUrl);
      }
      setStatementDateRange(dateRange);
      setStatementPreviewLoading(true);
      setStatementPreviewError("");
      setStatementPreviewUrl("");
      try {
        const blob = await getStatementPdfPreviewBlob(billingCustomerId, dateRange);
        const objectUrl = window.URL.createObjectURL(blob);
        setStatementPreviewUrl(objectUrl);
      } catch (err) {
        setStatementPreviewError(err?.message || "Failed to load statement preview.");
      } finally {
        setStatementPreviewLoading(false);
      }
    },
    [customerData?.vat_customer_id]
  );

  const handleOpenStatementPreview = useCallback(async () => {
    const billingCustomerId = Number(customerData?.vat_customer_id || 0);
    if (billingCustomerId < 1) {
      setStatementPreviewError("No billing customer linked for this account.");
      setStatementPreviewOpen(true);
      return;
    }
    const dateRange = getDefaultStatementPdfDateRange();
    setStatementStartInput(dateRange.startDate);
    setStatementEndInput(dateRange.endDate);
    setStatementPreviewOpen(true);
    setStatementSendEmail("");
    await loadStatementPreview(dateRange);
  }, [customerData?.vat_customer_id, loadStatementPreview]);

  const handleApplyStatementDateRange = useCallback(async () => {
    const dateRange = buildStatementDateRange({
      startInput: statementStartInput,
      endInput: statementEndInput,
    });
    if (!dateRange.startDate || !dateRange.endDate) {
      setStatementPreviewError("Please select both start and end dates.");
      return;
    }
    if (dateRange.endDate < dateRange.startDate) {
      setStatementPreviewError("End date must be on or after the statement start date.");
      return;
    }
    setStatementStartInput(dateRange.startDate);
    await loadStatementPreview(dateRange);
  }, [statementStartInput, statementEndInput, loadStatementPreview]);

  function handleCloseStatementPreview() {
    setStatementPreviewOpen(false);
    setStatementPreviewError("");
    setStatementSendEmail("");
    if (statementPreviewUrl) {
      window.URL.revokeObjectURL(statementPreviewUrl);
      setStatementPreviewUrl("");
    }
  }

  async function handleDownloadStatement() {
    const billingCustomerId = Number(customerData?.vat_customer_id || 0);
    if (billingCustomerId < 1) {
      return;
    }
    try {
      setStatementDownloadLoading(true);
      const code = String(customerData?.code || "").trim();
      const { startDate, endDate } = statementDateRange;
      const defaultFileName = code
        ? `NanoTech_Statement_${code}_${startDate}_to_${endDate}.pdf`
        : "";
      await downloadStatementPdf(billingCustomerId, statementDateRange, defaultFileName);
    } catch (err) {
      setStatementPreviewError(err?.message || "Failed to download statement.");
    } finally {
      setStatementDownloadLoading(false);
    }
  }

  async function handleSendStatementEmail() {
    const billingCustomerId = Number(customerData?.vat_customer_id || 0);
    if (billingCustomerId < 1) {
      return;
    }
    try {
      setStatementSendLoading(true);
      const response = await sendStatementPdfByEmailWithContext({
        billingCustomerId,
        startDate: statementDateRange.startDate,
        endDate: statementDateRange.endDate,
        toEmail: statementSendEmail,
        customerId: parseCustomerId(customerData?.customer_id || customerId),
        customerCode: customerData?.code,
      });
      const message =
        response?.reply?.message ||
        response?.reply ||
        "Statement email sent successfully.";
      setRadiusActionSnackbar({
        open: true,
        message: String(message),
        severity: "success",
      });
    } catch (err) {
      setStatementPreviewError(err?.message || "Failed to email statement.");
    } finally {
      setStatementSendLoading(false);
      await fetchOutboundCommunication();
    }
  }

  const handleRefreshBillingHistory = async () => {
    await Promise.all([fetchBillingHistory(), fetchBalance()]);
  };

  const printPdfIframe = useCallback((iframeRef, previewUrl) => {
    const frame = iframeRef.current;
    if (frame?.contentWindow) {
      try {
        frame.contentWindow.focus();
        frame.contentWindow.print();
        return;
      } catch (err) {
        console.warn("Iframe print failed, opening preview URL:", err);
      }
    }
    if (previewUrl) {
      const printWindow = window.open(previewUrl, "_blank", "noopener,noreferrer");
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.focus();
          printWindow.print();
        };
      }
    }
  }, []);

  const pdfPreviewDialogSx = useMemo(
    () => ({
      "& .MuiDialog-paper": {
        maxHeight: "calc(100vh - 32px)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        ...(isMobileView ? {} : { height: "calc(100vh - 32px)" }),
      },
    }),
    [isMobileView]
  );

  const pdfIframeContainerSx = useMemo(
    () => ({
      flex: 1,
      minHeight: 0,
      ...(isMobileView ? {} : { minHeight: "min(1040px, calc(100vh - 260px))" }),
    }),
    [isMobileView]
  );

  useEffect(() => {
    fetchInitialData();
  }, [customerId]);

  useEffect(() => {
    fetchOutboundCommunication();
  }, [customerId]);

  useEffect(() => {
    invoicePreviewUrlRef.current = invoicePreviewUrl;
  }, [invoicePreviewUrl]);

  useEffect(() => {
    quotePreviewUrlRef.current = quotePreviewUrl;
  }, [quotePreviewUrl]);

  useEffect(() => {
    statementPreviewUrlRef.current = statementPreviewUrl;
  }, [statementPreviewUrl]);

  useEffect(
    () => () => {
      if (invoicePreviewUrlRef.current) {
        window.URL.revokeObjectURL(invoicePreviewUrlRef.current);
      }
      if (quotePreviewUrlRef.current) {
        window.URL.revokeObjectURL(quotePreviewUrlRef.current);
      }
      if (statementPreviewUrlRef.current) {
        window.URL.revokeObjectURL(statementPreviewUrlRef.current);
      }
    },
    []
  );

  useEffect(() => {
    if (!customerData) return;
    if (customerData.vat_customer_id) {
      fetchBalance();
      return;
    }
    console.info(
      "[Billing] Balance/statement APIs not called: customer has no vat_customer_id (link this customer in billing to see requests)."
    );
  }, [customerData]);

  useEffect(() => {
    setLoadedCustomerTabs({ 0: true, 1: false, 2: false, 3: false, 4: false, 5: false });
    setLoadedServiceTabs({ 0: true, 1: false, 2: false });
    setPingStatus({});
    setCpePingStatus({});
    setRouterPingStatus({});
  }, [customerId]);

  useEffect(() => {
    const tab = location.state?.activeCustomerTab;
    if (!Number.isFinite(tab) || tab < 0 || tab > 5) return;
    handleCustomerTabChange(null, tab);
  }, [customerId, location.key]);

  const renderCustomerTabContent = () => {
    switch (activeCustomerTab) {
      case 0:
        return (
          <MDBox>
            <Card>
              <CardContent>
                <MDBox>
                  <MDBox
                    display="flex"
                    flexDirection={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    gap={{ xs: 1, sm: 0 }}
                    mb={2}
                  >
                    <MDTypography
                      variant="body2"
                      color={getCustTypeTypographyColor(customerData?.cust_type)}
                      sx={{
                        textTransform: "uppercase",
                        order: { xs: 1, sm: 2 },
                        fontWeight: "medium",
                      }}
                    >
                      {customerData?.cust_type}
                    </MDTypography>
                    <MDBox sx={{ ...customerDetailsLabelValueSx, order: { xs: 2, sm: 1 } }}>
                      <MDTypography variant="body2" color="text">
                        RSA ID:
                      </MDTypography>
                      <MDTypography variant="body2" fontWeight="medium">
                        {customerData?.id || "Not provided"}
                      </MDTypography>
                    </MDBox>
                  </MDBox>

                  <MDBox
                    display="grid"
                    gridTemplateColumns={{ xs: "1fr", md: "repeat(2, 1fr)" }}
                    gap={3}
                  >
                    <MDBox>
                      <MDBox display="flex" flexDirection="column" gap={2}>
                        <MDBox sx={customerDetailsLabelValueSx}>
                          <MDTypography variant="body2" color="text">
                            Payment Type:
                          </MDTypography>
                          <MDTypography variant="body2" fontWeight="medium">
                            {customerData?.payment_type || "Not specified"}
                          </MDTypography>
                        </MDBox>
                        {customerData?.vat && (
                          <MDBox sx={customerDetailsLabelValueSx}>
                            <MDTypography variant="body2" color="text">
                              VAT Number:
                            </MDTypography>
                            <MDTypography variant="body2" fontWeight="medium">
                              {customerData.vat}
                            </MDTypography>
                          </MDBox>
                        )}
                        <MDBox sx={customerDetailsFieldGridSx}>
                          <MDTypography variant="body2" color="text">
                            Primary Phone:
                          </MDTypography>
                          <MDTypography variant="body2" fontWeight="medium">
                            {customerData?.telephone2 || "Not provided"}
                          </MDTypography>

                          <MDTypography variant="body2" color="text">
                            Secondary Phone:
                          </MDTypography>
                          <MDTypography variant="body2" fontWeight="medium">
                            {customerData?.fax || "Not provided"}
                          </MDTypography>

                          <MDTypography variant="body2" color="text">
                            WhatsApp:
                          </MDTypography>
                          <MDTypography variant="body2" fontWeight="medium">
                            {customerData?.telephone1 || "Not provided"}
                          </MDTypography>

                          <MDTypography variant="body2" color="text">
                            Email:
                          </MDTypography>
                          <MDTypography variant="body2" fontWeight="medium">
                            {customerData?.email || "Not provided"}
                          </MDTypography>
                        </MDBox>
                      </MDBox>
                    </MDBox>

                    <MDBox>
                      <MDBox display="flex" flexDirection="column" gap={2}>
                        <MDBox sx={customerDetailsLabelValueSx}>
                          <MDTypography variant="body2" color="text">
                            Signup date:
                          </MDTypography>
                          <MDTypography variant="body2" fontWeight="medium">
                            {formatDate(customerData?.signup_date)}
                          </MDTypography>
                        </MDBox>
                        <MDBox sx={customerDetailsLabelValueSx}>
                          <MDTypography variant="body2" color="text">
                            Physical Address:
                          </MDTypography>
                          <MDTypography variant="body2" fontWeight="medium" sx={{ wordBreak: "break-word" }}>
                            {customerData?.address || "Not provided"}
                          </MDTypography>
                        </MDBox>
                        <MDBox sx={customerDetailsLabelValueSx}>
                          <MDTypography variant="body2" color="text">
                            Postal Address:
                          </MDTypography>
                          <MDTypography variant="body2" fontWeight="medium" sx={{ wordBreak: "break-word" }}>
                            {customerData?.postal || "Not provided"}
                          </MDTypography>
                        </MDBox>
                      </MDBox>
                    </MDBox>
                  </MDBox>
                </MDBox>
              </CardContent>
            </Card>
          </MDBox>
        );
      case 1:
        return (
          <MDBox>
            <MDBox
              display="flex"
              flexDirection={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "stretch", sm: "center" }}
              gap={{ xs: 1.5, sm: 0 }}
              mb={3}
            >
              <MDTypography variant="h5">Contacts</MDTypography>
              <MDButton
                variant="gradient"
                color="info"
                onClick={() => setShowAddContactDialog(true)}
                sx={{ alignSelf: { xs: "stretch", sm: "auto" } }}
              >
                <Icon>add</Icon>
                Add Contact
              </MDButton>
            </MDBox>
            <Card>
              <CardContent>
                {isMobileView ? (
                  <CustomerContactsMobileCards
                    contacts={contacts}
                    loading={contactsLoading}
                    onViewContact={handleViewContact}
                  />
                ) : (
                <MDBox component="div" sx={{ overflow: "auto", width: "100%" }}>
                  <MDBox
                    component="table"
                    sx={{
                      width: "100%",
                      tableLayout: "fixed",
                      minWidth: "650px",
                      borderCollapse: "collapse",
                      "& th, & td": {
                        whiteSpace: "normal",
                        wordBreak: "normal",
                        overflowWrap: "break-word",
                      },
                    }}
                  >
                    <MDBox component="colgroup">
                      <MDBox component="col" width="20%" />
                      <MDBox component="col" width="12%" />
                      <MDBox component="col" width="20%" />
                      <MDBox component="col" width="20%" />
                      <MDBox component="col" width="20%" />
                      <MDBox component="col" width="10%" />
                    </MDBox>
                    <MDBox component="thead">
                      <MDBox component="tr" sx={{ backgroundColor: "rgba(0, 0, 0, 0.02)" }}>
                        <MDBox
                          component="th"
                          py={1.5}
                          px={2}
                          width="20%"
                          sx={{
                            textAlign: "left",
                            borderBottom: "1px solid rgba(0, 0, 0, 0.1)",
                          }}
                        >
                          <MDTypography variant="subtitle2" fontWeight="medium">
                            Name
                          </MDTypography>
                        </MDBox>
                        <MDBox
                          component="th"
                          py={1.5}
                          px={2}
                          width="12%"
                          sx={{
                            textAlign: "left",
                            borderBottom: "1px solid rgba(0, 0, 0, 0.1)",
                          }}
                        >
                          <MDTypography variant="subtitle2" fontWeight="medium">
                            Actions
                          </MDTypography>
                        </MDBox>
                        <MDBox
                          component="th"
                          py={1.5}
                          px={2}
                          width="20%"
                          sx={{
                            textAlign: "left",
                            borderBottom: "1px solid rgba(0, 0, 0, 0.1)",
                          }}
                        >
                          <MDTypography variant="subtitle2" fontWeight="medium">
                            Telephone
                          </MDTypography>
                        </MDBox>
                        <MDBox
                          component="th"
                          py={1.5}
                          px={2}
                          width="20%"
                          sx={{
                            textAlign: "left",
                            borderBottom: "1px solid rgba(0, 0, 0, 0.1)",
                          }}
                        >
                          <MDTypography variant="subtitle2" fontWeight="medium">
                            WhatsApp
                          </MDTypography>
                        </MDBox>
                        <MDBox
                          component="th"
                          py={1.5}
                          px={2}
                          width="20%"
                          sx={{
                            textAlign: "left",
                            borderBottom: "1px solid rgba(0, 0, 0, 0.1)",
                          }}
                        >
                          <MDTypography variant="subtitle2" fontWeight="medium">
                            Email
                          </MDTypography>
                        </MDBox>
                        <MDBox
                          component="th"
                          py={1.5}
                          px={2}
                          width="10%"
                          sx={{
                            textAlign: "left",
                            borderBottom: "1px solid rgba(0, 0, 0, 0.1)",
                          }}
                        >
                          <MDTypography variant="subtitle2" fontWeight="medium">
                            Primary
                          </MDTypography>
                        </MDBox>
                      </MDBox>
                    </MDBox>
                    <MDBox component="tbody">
                      {contactsLoading ? (
                        <MDBox component="tr">
                          <MDBox component="td" colSpan={6} textAlign="center" py={2}>
                            <MDTypography>Loading contacts...</MDTypography>
                          </MDBox>
                        </MDBox>
                      ) : contacts.length === 0 ? (
                        <MDBox component="tr">
                          <MDBox component="td" colSpan={6} textAlign="center" py={2}>
                            <MDTypography>No contacts found</MDTypography>
                          </MDBox>
                        </MDBox>
                      ) : (
                        contacts.map((contact, index) => (
                          <MDBox
                            component="tr"
                            key={index}
                            sx={{
                              "&:nth-of-type(even)": { backgroundColor: "rgba(0, 0, 0, 0.01)" },
                              "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.03)" },
                            }}
                          >
                            <MDBox component="td" py={1.5} px={2} width="20%">
                              <MDTypography variant="button" fontWeight="regular">
                                {contact.name || "N/A"}
                              </MDTypography>
                            </MDBox>
                            <MDBox component="td" py={1.5} px={2} width="12%">
                              <MDButton
                                variant="text"
                                color="info"
                                size="small"
                                onClick={() => handleViewContact(contact.customer_contact_id)}
                              >
                                <VisibilityIcon />
                              </MDButton>
                            </MDBox>
                            <MDBox component="td" py={1.5} px={2} width="20%">
                              <MDTypography variant="button" fontWeight="regular">
                                {contact.telephone || "N/A"}
                              </MDTypography>
                            </MDBox>
                            <MDBox component="td" py={1.5} px={2} width="20%">
                              <MDTypography variant="button" fontWeight="regular">
                                {contact.whatsapp || "N/A"}
                              </MDTypography>
                            </MDBox>
                            <MDBox component="td" py={1.5} px={2} width="20%">
                              <MDTypography variant="button" fontWeight="regular">
                                {contact.email || "N/A"}
                              </MDTypography>
                            </MDBox>
                            <MDBox component="td" py={1.5} px={2} width="10%">
                              <MDTypography variant="button" fontWeight="regular">
                                {contact.primary === "yes" ? "Yes" : "No"}
                              </MDTypography>
                            </MDBox>
                          </MDBox>
                        ))
                      )}
                    </MDBox>
                  </MDBox>
                </MDBox>
                )}
              </CardContent>
            </Card>
          </MDBox>
        );
      case 4: {
        const handleViewContract = (contract) => {
          if (isClientPortal) {
            navigate(`/contracts/view/${contract.contract_id}`, {
              state: { returnUrl: "/profile", activeCustomerTab: 4 },
            });
            return;
          }
          navigate(`/contracts/view/${contract.contract_id}`, {
            state: { returnUrl: location.pathname },
          });
        };

        return (
          <MDBox>
            <MDBox
              display="flex"
              flexDirection={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "stretch", sm: "center" }}
              gap={{ xs: 1.5, sm: 0 }}
              mb={3}
            >
              <MDTypography variant="h5">Customer Contracts</MDTypography>
              {canManageContracts ? (
                <MDButton
                  variant="gradient"
                  color="info"
                  onClick={handleCreateContractFromCustomerView}
                  disabled={contractCreateLoading}
                  startIcon={<Icon>add</Icon>}
                  sx={{ width: { xs: "100%", sm: "auto" } }}
                >
                  {contractCreateLoading ? "Creating..." : "New Contract"}
                </MDButton>
              ) : null}
            </MDBox>
            <Card>
              <CardContent sx={{ px: { xs: 2, md: 3 }, py: { xs: 2, md: 3 } }}>
                {isMobileView ? (
                  <CustomerContractsMobileCards
                    contracts={customerContractsData}
                    loading={contractsLoading}
                    terminationScheduledByContractId={terminationScheduledByContractId}
                    onViewContract={handleViewContract}
                  />
                ) : (
                <MDBox component="div" sx={{ overflow: "auto", width: "100%" }}>
                  <MDBox
                    component="table"
                    sx={{
                      width: "100%",
                      tableLayout: "fixed",
                      minWidth: "650px",
                      borderCollapse: "collapse",
                      "& th, & td": {
                        whiteSpace: "normal",
                        wordBreak: "normal",
                        overflowWrap: "break-word",
                      },
                    }}
                  >
                    <MDBox component="colgroup">
                      <MDBox component="col" width="15%" />
                      <MDBox component="col" width="15%" />
                      <MDBox component="col" width="15%" />
                      <MDBox component="col" width="15%" />
                      <MDBox component="col" width="15%" />
                      <MDBox component="col" width="15%" />
                      <MDBox component="col" width="15%" />
                    </MDBox>
                    <MDBox component="thead">
                      <MDBox component="tr" sx={{ backgroundColor: "rgba(0, 0, 0, 0.02)" }}>
                        <MDBox
                          component="th"
                          py={1.5}
                          px={2}
                          width="15%"
                          sx={{
                            textAlign: "left",
                            borderBottom: "1px solid rgba(0, 0, 0, 0.1)",
                          }}
                        >
                          <MDTypography variant="subtitle2" fontWeight="medium">
                            Contract Type
                          </MDTypography>
                        </MDBox>
                        <MDBox
                          component="th"
                          py={1.5}
                          px={2}
                          width="20%"
                          sx={{
                            textAlign: "left",
                            borderBottom: "1px solid rgba(0, 0, 0, 0.1)",
                          }}
                        >
                          <MDTypography variant="subtitle2" fontWeight="medium">
                            Service Plan
                          </MDTypography>
                        </MDBox>
                        <MDBox
                          component="th"
                          py={1.5}
                          px={2}
                          width="10%"
                          sx={{
                            textAlign: "left",
                            borderBottom: "1px solid rgba(0, 0, 0, 0.1)",
                          }}
                        >
                          <MDTypography variant="subtitle2" fontWeight="medium">
                            Status
                          </MDTypography>
                        </MDBox>
                        <MDBox
                          component="th"
                          py={1.5}
                          px={2}
                          width="15%"
                          sx={{
                            textAlign: "left",
                            borderBottom: "1px solid rgba(0, 0, 0, 0.1)",
                          }}
                        >
                          <MDTypography variant="subtitle2" fontWeight="medium">
                            Dates (Start/End)
                          </MDTypography>
                        </MDBox>
                        <MDBox
                          component="th"
                          py={1.5}
                          px={2}
                          width="10%"
                          sx={{
                            textAlign: "left",
                            borderBottom: "1px solid rgba(0, 0, 0, 0.1)",
                          }}
                        >
                          <MDTypography variant="subtitle2" fontWeight="medium">
                            Monthly Fee
                          </MDTypography>
                        </MDBox>
                        <MDBox
                          component="th"
                          py={1.5}
                          px={2}
                          width="15%"
                          sx={{
                            textAlign: "left",
                            borderBottom: "1px solid rgba(0, 0, 0, 0.1)",
                          }}
                        >
                          <MDTypography variant="subtitle2" fontWeight="medium">
                            Signed Date
                          </MDTypography>
                        </MDBox>
                        <MDBox
                          component="th"
                          py={1.5}
                          px={2}
                          width="10%"
                          sx={{
                            textAlign: "left",
                            borderBottom: "1px solid rgba(0, 0, 0, 0.1)",
                          }}
                        >
                          <MDTypography variant="subtitle2" fontWeight="medium">
                            Actions
                          </MDTypography>
                        </MDBox>
                      </MDBox>
                    </MDBox>
                    <MDBox component="tbody">
                      {contractsLoading ? (
                        <MDBox component="tr">
                          <MDBox component="td" colSpan={6} textAlign="center" py={2}>
                            <MDTypography>Loading contracts...</MDTypography>
                          </MDBox>
                        </MDBox>
                      ) : customerContractsData.length === 0 ? (
                        <MDBox component="tr">
                          <MDBox component="td" colSpan={6} textAlign="center" py={2}>
                            <MDTypography>No contracts found</MDTypography>
                          </MDBox>
                        </MDBox>
                      ) : (
                        customerContractsData.map((contract, index) => (
                          <MDBox
                            component="tr"
                            key={contract.contract_id || index}
                            sx={{
                              "&:nth-of-type(even)": { backgroundColor: "rgba(0, 0, 0, 0.01)" },
                              "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.03)" },
                            }}
                          >
                            <MDBox component="td" py={1.5} px={2} width="15%">
                              <MDTypography variant="caption" color="text" fontWeight="medium">
                                {contract.contract_type || "N/A"}
                              </MDTypography>
                            </MDBox>
                            <MDBox component="td" py={1.5} px={2} width="20%">
                              <MDTypography variant="caption" color="text" fontWeight="regular">
                                {contract.service_plan || "N/A"}
                              </MDTypography>
                            </MDBox>
                            <MDBox component="td" py={1.5} px={2} width="10%">
                              <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                <MDBadge 
                                  badgeContent={contract.status?.charAt(0).toUpperCase() + contract.status?.slice(1) || "Unknown"} 
                                  color={CONTRACT_STATE_COLORS[contract.status] || "light"} 
                                  variant="gradient" 
                                  size="sm" 
                                />
                                {terminationScheduledByContractId[Number(contract.contract_id || 0)] ? (
                                  <MDBadge
                                    badgeContent={
                                      <MDBox
                                        sx={{
                                          display: "flex",
                                          flexDirection: "column",
                                          alignItems: "center",
                                          lineHeight: 1.05,
                                        }}
                                      >
                                        <span>Termination</span>
                                        <span>Scheduled</span>
                                      </MDBox>
                                    }
                                    color="warning"
                                    variant="gradient"
                                    size="sm"
                                  />
                                ) : null}
                              </MDBox>
                            </MDBox>
                            <MDBox component="td" py={1.5} px={2} width="15%">
                              <MDBox sx={{ display: "flex", flexDirection: "column", lineHeight: 1.2 }}>
                                <MDTypography variant="caption" color="text" fontWeight="regular">
                                  {formatDate(contract.start_date) || "N/A"}
                                </MDTypography>
                                <MDTypography variant="caption" color="text" fontWeight="regular">
                                  {formatDate(contract.end_date) || "N/A"}
                                </MDTypography>
                              </MDBox>
                            </MDBox>
                            <MDBox component="td" py={1.5} px={2} width="10%">
                              <MDTypography variant="caption" color="text" fontWeight="regular">
                                R {Number(contract.monthly_fee || 0).toFixed(2)}
                              </MDTypography>
                            </MDBox>
                            <MDBox component="td" py={1.5} px={2} width="15%">
                              <MDTypography variant="caption" color="text" fontWeight="regular">
                                {formatDate(contract.signed_date) || "Not signed"}
                              </MDTypography>
                            </MDBox>
                            <MDBox component="td" py={1.5} px={2} width="10%">
                              <MDButton
                                variant="text"
                                color="info"
                                size="small"
                                onClick={() => handleViewContract(contract)}
                              >
                                View
                              </MDButton>
                            </MDBox>
                          </MDBox>
                        ))
                      )}
                    </MDBox>
                  </MDBox>
                </MDBox>
                )}
              </CardContent>
            </Card>
          </MDBox>
        );
      }
      case 2:
        return (
          <CustomerWhatsAppInboxPanel
            customerId={customerId}
            customerName={customerData?.name || ""}
            customerContacts={contacts}
            returnPath={`/customers/view/${customerId}`}
          />
        );
      case 3: {
        const handleViewTicket = (ticket) => {
          if (isClientPortal) {
            navigate(`/tickets/view/${ticket.ticket_id}`, {
              state: {
                returnUrl: "/profile",
                activeCustomerTab: 3,
                customerId,
                customerName: customerData?.name,
              },
            });
            return;
          }
          navigate(`/tickets/view/${ticket.ticket_id}`, {
            state: {
              returnUrl: `/customers/view/${customerId}`,
              customerId,
              customerName: customerData?.name,
            },
          });
        };

        return (
          <MDBox>
            <MDBox display="flex" justifyContent="flex-end" mb={2}>
              <MDButton
                variant="contained"
                color="info"
                onClick={() => setNewTicketDialogOpen(true)}
                sx={{ width: { xs: "100%", sm: "auto" } }}
              >
                <Icon>add</Icon>
                Open Ticket
              </MDButton>
            </MDBox>
            {ticketsLoading ? (
              <MDBox display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </MDBox>
            ) : ticketsError ? (
              <MDBox p={2}>
                <PageErrorAlert title={PAGE_ERROR_TITLES.tickets} message={ticketsError} />
              </MDBox>
            ) : tickets.length === 0 ? (
              <MDBox display="flex" justifyContent="center" p={3}>
                <MDTypography variant="body2" color="text">
                  No tickets found for this customer.
                </MDTypography>
              </MDBox>
            ) : isMobileView ? (
              <CustomerTicketsMobileCards
                tickets={tickets}
                onViewTicket={handleViewTicket}
                hideAssignee={isClientPortal}
                showUnreadReplies={!isClientPortal}
              />
            ) : (
              <MDBox>
                <DataTable
                  table={{
                    columns: [
                      { Header: "Ticket ID", accessor: "ticket_id", width: "15%" },
                      ...(!isClientPortal
                        ? [{ Header: "Customer Reply", accessor: "customer_reply", width: "12%" }]
                        : []),
                      { Header: "Actions", accessor: "actions", width: "10%" },
                      { Header: "Department", accessor: "department", width: "15%" },
                      { Header: "Level", accessor: "level", width: "10%" },
                      { Header: "Status", accessor: "resolved", width: "10%" },
                      ...(!isClientPortal
                        ? [{ Header: "Current User", accessor: "current_user", width: "15%" }]
                        : []),
                      { Header: "Created (GMT+2)", accessor: "create_datetime", width: "20%" },
                    ],
                    rows: tickets.map((ticket) => {
                      const priority = getPriorityInfo(ticket.level);
                      const status = getStatusInfo(ticket.resolved);

                      return {
                        ticket_id: ticket.ticket_id,
                        ...(!isClientPortal
                          ? {
                              customer_reply:
                                Number(ticket.unread_customer_replies || 0) > 0 ? (
                                  <Chip
                                    label={`Unread (${ticket.unread_customer_replies})`}
                                    color="warning"
                                    size="small"
                                    sx={{ fontWeight: 600 }}
                                  />
                                ) : (
                                  <MDTypography variant="caption" color="text">
                                    —
                                  </MDTypography>
                                ),
                            }
                          : {}),
                        actions: (
                          <MDButton
                            variant="text"
                            color="info"
                            size="small"
                            onClick={() => handleViewTicket(ticket)}
                          >
                            <VisibilityIcon />
                          </MDButton>
                        ),
                        department: ticket.department,
                        level: (
                          <MDBox
                            display="inline-flex"
                            alignItems="center"
                            justifyContent="center"
                            px={1.5}
                            py={0.5}
                            borderRadius="lg"
                            sx={{
                              backgroundColor: priority.bgColor,
                              color: priority.textColor,
                              minWidth: "80px",
                              textAlign: "center",
                            }}
                          >
                            <MDTypography variant="caption" fontWeight="medium">
                              {priority.label}
                            </MDTypography>
                          </MDBox>
                        ),
                        resolved: (
                          <MDBox
                            display="inline-flex"
                            alignItems="center"
                            justifyContent="center"
                            px={1.5}
                            py={0.5}
                            borderRadius="lg"
                            sx={{
                              backgroundColor: status.bgColor,
                              color: status.textColor,
                              minWidth: "80px",
                              textAlign: "center",
                            }}
                          >
                            <MDTypography variant="caption" fontWeight="medium">
                              {status.label}
                            </MDTypography>
                          </MDBox>
                        ),
                        ...(!isClientPortal
                          ? { current_user: ticket.current_user || "Unassigned" }
                          : {}),
                        create_datetime: formatDbDateTime(ticket.create_datetime),
                      };
                    }),
                  }}
                  isSorted={false}
                  entriesPerPage={false}
                  showTotalEntries={true}
                  noEndBorder
                />
              </MDBox>
            )}
          </MDBox>
        );
      }
      case 5:
        return (
          <MDBox>
            <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <MDTypography variant="h5">Customer Services</MDTypography>
            </MDBox>
            <Tabs value={activeTab} onChange={handleTabChange}>
              <Tab label={renderLoadingTabLabel("PPPoE", pppoeLoading)} />
              <Tab label={renderLoadingTabLabel("Routers", routersLoading)} />
              <Tab label={renderLoadingTabLabel("CPEs", cpesLoading)} />
            </Tabs>
            {renderTabContent()}
          </MDBox>
        );
      default:
        return null;
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 0:
        return (
          <MDBox>
            <MDBox display="flex" justifyContent="space-between" gap={1} mb={2} flexWrap="wrap">
              <MDBox display="flex" gap={1} flexWrap="wrap">
                {isManagerOrAdmin && (
                  <MDButton
                    variant="outlined"
                    color="info"
                    startIcon={<PersonAddIcon />}
                    onClick={openAddRadiusModal}
                  >
                    Add New RADIUS User
                  </MDButton>
                )}
                {isManagerOrAdmin && (
                  <MDButton
                    variant="outlined"
                    color="info"
                    startIcon={<LinkIcon />}
                    onClick={openLinkRadiusModal}
                  >
                    Link Existing RADIUS User
                  </MDButton>
                )}
              </MDBox>
              <MDBox display="flex" gap={1} flexWrap="wrap">
                <MDButton
                  variant="outlined"
                  color="info"
                  onClick={fetchPppoeUsersData}
                  disabled={pppoeLoading}
                >
                  {pppoeLoading ? "Refreshing PPPoE..." : "Refresh PPPoE"}
                </MDButton>
                {!isClientPortal && (
                <MDButton
                  variant="outlined"
                  color="info"
                  onClick={diagnosePppoeOnly}
                  disabled={diagnosticsLoading || pppoeUsers.length === 0}
                >
                  {diagnosticsLoading ? "Diagnosing PPPoE..." : "Diagnose PPPoE"}
                </MDButton>
                )}
              </MDBox>
            </MDBox>
            <Card>
              <CardContent>
                {pppoeLoading ? (
                  <MDBox textAlign="center" py={3}>
                    <MDTypography>Loading PPPoE users...</MDTypography>
                  </MDBox>
                ) : pppoeUsers.length === 0 ? (
                  <MDBox textAlign="center" py={3}>
                    <MDTypography>No PPPoE users found</MDTypography>
                  </MDBox>
                ) : (
                  <Grid container spacing={2}>
                    {pppoeUsers.map((user, index) => {
                      const pppoePing = pingStatus[user.username];
                      const radiusRaw =
                        user.lastActivity && user.lastActivity !== "Never" ? user.lastActivity : null;
                      const lastConnectedRaw = user.zabbixLastOnlineAt
                        ? user.zabbixLastOnlineAt
                        : pppoePing?.status === "Offline"
                          ? radiusRaw
                          : pppoePing?.lastChecked || radiusRaw;
                      const lastConnectedLabel = lastConnectedRaw ? formatDateTime(lastConnectedRaw) : "Never";
                      const lastConnectedMs = parseTimestampMs(lastConnectedRaw);
                      const staleThresholdMs = 2 * 24 * 60 * 60 * 1000;
                      const isLastConnectedStale =
                        Number.isFinite(lastConnectedMs) && Date.now() - lastConnectedMs > staleThresholdMs;
                      const offlineDays = isLastConnectedStale
                        ? Math.floor((Date.now() - Number(lastConnectedMs)) / (24 * 60 * 60 * 1000))
                        : 0;
                      const diagStatus = pppoePing?.status || user.status || "Not Diagnosed";
                      const uptimeSummary = pppoeUptimeByIp?.[user.ipAddress] || null;
                      const uptimeAvg =
                        uptimeSummary && Number.isFinite(Number(uptimeSummary.avg_uptime_percent))
                          ? Number(uptimeSummary.avg_uptime_percent)
                          : null;
                      const uptimeBelow90 = Boolean(uptimeSummary?.below_90_percent);
                      const responseAvgMs =
                        uptimeSummary && Number.isFinite(Number(uptimeSummary.avg_response_ms))
                          ? Number(uptimeSummary.avg_response_ms)
                          : null;
                      const responseOver50 = Boolean(uptimeSummary?.response_over_50ms);
                      return (
                      <Grid
                        item
                        xs={12}
                        md={6}
                        xl={4}
                        key={`${user.username || "pppoe"}-${index}`}
                      >
                        <Card
                          sx={{
                            height: "100%",
                            border: "1px solid rgba(0, 0, 0, 0.08)",
                            boxShadow: "0 2px 10px rgba(0, 0, 0, 0.06)",
                          }}
                        >
                          <CardContent>
                            <MDBox
                              display="flex"
                              justifyContent="space-between"
                              alignItems="flex-start"
                              gap={1}
                            >
                              <MDBox flex={1} minWidth={0}>
                                <MDBox display="flex" alignItems="center" gap={0.25} flexWrap="wrap">
                                  <MDTypography variant="button" fontWeight="bold" component="span">
                                    {user.username || "PPPoE user"}
                                  </MDTypography>
                                  {!isClientPortal && user.username ? (
                                    <Tooltip title="Copy username" arrow>
                                      <IconButton
                                        size="small"
                                        onClick={() => handleCopyToClipboard(String(user.username))}
                                        sx={{ p: 0.5 }}
                                        aria-label="Copy username"
                                      >
                                        <ContentCopyIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  ) : null}
                                  {!isClientPortal && (
                                  <Tooltip title="Refresh from live Radius DB" arrow>
                                    <span>
                                      <IconButton
                                        size="small"
                                        onClick={() => refreshSinglePppoeUser(user.username)}
                                        disabled={pppoeRefreshingUser === user.username}
                                        sx={{ p: 0.5 }}
                                        aria-label="Refresh live"
                                      >
                                        <CachedIcon
                                          fontSize="small"
                                          sx={pppoeRefreshingUser === user.username ? { animation: "spin 1s linear infinite", "@keyframes spin": { "0%": { transform: "rotate(0deg)" }, "100%": { transform: "rotate(360deg)" } } } : undefined}
                                        />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                  )}
                                </MDBox>
                                <MDTypography
                                  variant="caption"
                                  color="text"
                                  display="block"
                                  sx={
                                    String(user.plan || "").trim().toLowerCase() === "suspended"
                                      ? { color: "error.main", fontWeight: 700 }
                                      : undefined
                                  }
                                >
                                  Plan: <strong>{user.plan || "N/A"}</strong>
                                </MDTypography>
                              </MDBox>
                              {renderStatus(
                                pingStatus[user.username]?.status || user.status,
                                pingStatus[user.username]?.stats
                              )}
                            </MDBox>
                            <MDBox mt={2} pt={1.5} sx={{ borderTop: "1px solid rgba(0, 0, 0, 0.08)" }}>
                              <MDBox display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
                                <MDTypography variant="caption" color="text" component="span">
                                  Password:
                                </MDTypography>
                                <Typography
                                  component="span"
                                  variant="caption"
                                  sx={{
                                    fontFamily: CREDENTIAL_MONO_FONT,
                                    letterSpacing: "0.06em",
                                    fontWeight: 700,
                                    fontVariantLigatures: "none",
                                    wordBreak: "break-all",
                                  }}
                                >
                                  {user.password ?? "N/A"}
                                </Typography>
                                {user.password != null && String(user.password).length > 0 && !isClientPortal ? (
                                  <Tooltip title="Copy password" arrow>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleCopyToClipboard(String(user.password))}
                                      sx={{ p: 0.5 }}
                                      aria-label="Copy password"
                                    >
                                      <ContentCopyIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                ) : null}
                              </MDBox>
                              {!isClientPortal && (
                              <MDBox display="flex" alignItems="center" gap={0.25} flexWrap="wrap" mt={1}>
                                <MDTypography variant="caption" color="text" component="span">
                                  IP: <strong>{user.ipAddress || "Not assigned"}</strong>
                                </MDTypography>
                                {user.ipAddress && user.ipAddress !== "Not assigned" && (
                                  <Tooltip title="Copy IP" arrow>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleCopyToClipboard(user.ipAddress)}
                                      sx={{ p: 0.5 }}
                                      aria-label="Copy IP"
                                    >
                                      <ContentCopyIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </MDBox>
                              )}
                              {!isClientPortal &&
                              Array.isArray(user.ipConflictWith) &&
                              user.ipConflictWith.length > 0 && (
                                <MDBox
                                  display="flex"
                                  alignItems="center"
                                  gap={0.5}
                                  mt={0.5}
                                  px={1}
                                  py={0.5}
                                  sx={{
                                    backgroundColor: "rgba(237, 108, 2, 0.08)",
                                    borderLeft: "3px solid #ed6c02",
                                    borderRadius: "4px",
                                  }}
                                >
                                  <WarningAmberIcon sx={{ fontSize: 14, color: "warning.main" }} />
                                  <MDTypography variant="caption" sx={{ color: "warning.main", fontWeight: 600 }}>
                                    IP conflict &mdash; also assigned to: {user.ipConflictWith.join(", ")}
                                  </MDTypography>
                                </MDBox>
                              )}
                              {!isClientPortal && user.ipAddress && user.ipAddress !== "Not assigned" && (
                                <MDBox display="flex" alignItems="center" gap={0.5} mt={0.5}>
                                  <Tooltip title="Open with HTTP" arrow>
                                    <MDButton
                                      variant="text"
                                      color="info"
                                      size="small"
                                      onClick={() => openWebInterface(user.ipAddress, "http")}
                                      sx={{ minWidth: "auto", p: 0.5 }}
                                    >
                                      <PublicIcon fontSize="small" />
                                    </MDButton>
                                  </Tooltip>
                                  <Tooltip title="Open with HTTPS" arrow>
                                    <MDButton
                                      variant="text"
                                      color="info"
                                      size="small"
                                      onClick={() => openWebInterface(user.ipAddress, "https")}
                                      sx={{ minWidth: "auto", p: 0.5 }}
                                    >
                                      <HttpsIcon fontSize="small" />
                                    </MDButton>
                                  </Tooltip>
                                  <Tooltip title="Open ICMP / poll history graph" arrow>
                                    <IconButton
                                      size="small"
                                      onClick={() => openPppoeZabbixModal(user)}
                                      sx={{ p: 0.5 }}
                                    >
                                      <ShowChartIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </MDBox>
                              )}
                              {!isClientPortal && (
                              <>
                              <MDTypography variant="caption" color="text" display="block" mt={1.25}>
                                2-day uptime avg:{" "}
                                <strong style={uptimeBelow90 ? { color: "#d32f2f" } : undefined}>
                                  {uptimeAvg != null ? `${uptimeAvg.toFixed(2)}%` : "N/A"}
                                </strong>
                              </MDTypography>
                              <MDTypography variant="caption" color="text" display="block">
                                2-day ping response avg:{" "}
                                <strong style={responseOver50 ? { color: "#d32f2f" } : undefined}>
                                  {responseAvgMs != null ? `${responseAvgMs.toFixed(2)} ms` : "N/A"}
                                </strong>
                              </MDTypography>
                              </>
                              )}
                              <MDTypography variant="caption" color="text" display="block" mt={1.5}>
                                Last connected:{" "}
                                {pppoeZabbixLoading && !user.zabbixLastOnlineAt ? (
                                  <CircularProgress size={10} thickness={5} sx={{ ml: 0.5, verticalAlign: "middle" }} />
                                ) : (
                                  <strong style={isLastConnectedStale ? { color: "#d32f2f" } : undefined}>
                                    {lastConnectedLabel}
                                  </strong>
                                )}
                              </MDTypography>
                              {!isClientPortal && (
                              <MDTypography variant="caption" color="text" display="block" mt={0.5}>
                                Contract link:{" "}
                                {Array.isArray(user.contractLinks) && user.contractLinks.length > 0 ? (
                                  <strong>
                                    {user.contractLinks
                                      .map((link) => {
                                        const id = Number(link?.contract_id || 0);
                                        const contractCode = String(link?.contract_code || "").trim();
                                        const status = String(link?.status || "").trim() || "unknown";
                                        if (contractCode) return `${contractCode} (${status})`;
                                        return id > 0 ? `#${id} (${status})` : null;
                                      })
                                      .filter(Boolean)
                                      .join(", ")}
                                  </strong>
                                ) : (
                                  <strong style={{ color: "#d32f2f" }}>Not linked to contract</strong>
                                )}
                              </MDTypography>
                              )}
                              {!isClientPortal && (
                              <MDBox
                                mt={1}
                                p={1}
                                sx={{
                                  borderRadius: 1,
                                  border: "1px solid rgba(0,0,0,0.08)",
                                  bgcolor: isLastConnectedStale
                                    ? (theme) =>
                                        theme.palette.mode === "dark"
                                          ? "rgba(211, 47, 47, 0.12)"
                                          : "rgba(211, 47, 47, 0.06)"
                                    : "transparent",
                                }}
                              >
                                <MDTypography variant="caption" color="text" fontWeight="medium" display="block">
                                  Diagnostics
                                </MDTypography>
                                <MDTypography variant="caption" color="text" display="block">
                                  Status: <strong>{diagStatus}</strong>
                                </MDTypography>
                                {uptimeBelow90 && (
                                  <MDTypography variant="caption" color="error" display="block">
                                    2-day average uptime is below 90%.
                                  </MDTypography>
                                )}
                                {responseOver50 && (
                                  <MDTypography variant="caption" color="error" display="block">
                                    2-day average ping response is above 50 ms.
                                  </MDTypography>
                                )}
                                {isLastConnectedStale && (
                                  <MDTypography variant="caption" color="error" display="block">
                                    Connection has been offline for longer than 2 days ({offlineDays} days).
                                  </MDTypography>
                                )}
                              </MDBox>
                              )}
                              {isManagerOrAdmin && (
                                <MDBox
                                  mt={1.25}
                                  pt={1}
                                  sx={{
                                    borderTop: "1px dashed rgba(0,0,0,0.18)",
                                    display: "flex",
                                    justifyContent: "flex-end",
                                    alignItems: "center",
                                    gap: 0.75,
                                    flexWrap: "wrap",
                                  }}
                                >
                                  <MDTypography variant="caption" color="text" sx={{ mr: 0.5 }}>
                                    User actions:
                                  </MDTypography>
                                  <Tooltip title="Edit PPPoE user" arrow>
                                    <MDButton
                                      variant="outlined"
                                      color="info"
                                      size="small"
                                      startIcon={<EditIcon fontSize="small" />}
                                      onClick={() => openEditPppoeModal(user)}
                                      sx={{ minWidth: "auto", px: 1.25, py: 0.5 }}
                                    >
                                      Edit
                                    </MDButton>
                                  </Tooltip>
                                  <Tooltip title="Unlink from this customer profile" arrow>
                                    <MDButton
                                      variant="outlined"
                                      color="warning"
                                      size="small"
                                      startIcon={<LinkOffIcon fontSize="small" />}
                                      onClick={() => requestUnlinkPppoeUser(user.username)}
                                      sx={{ minWidth: "auto", px: 1.25, py: 0.5 }}
                                    >
                                      Unlink
                                    </MDButton>
                                  </Tooltip>
                                  <Tooltip title="Permanently delete Radius user" arrow>
                                    <MDButton
                                      variant="outlined"
                                      color="error"
                                      size="small"
                                      startIcon={<DeleteForeverIcon fontSize="small" />}
                                      onClick={() => requestDeletePppoeUser(user.username)}
                                      sx={{ minWidth: "auto", px: 1.25, py: 0.5 }}
                                    >
                                      Delete
                                    </MDButton>
                                  </Tooltip>
                                </MDBox>
                              )}
                            </MDBox>
                          </CardContent>
                        </Card>
                      </Grid>
                      );
                    })}
                  </Grid>
                )}
              </CardContent>
            </Card>
          </MDBox>
        );
      case 1:
        return (
          <MDBox>
            {!isClientPortal && (
            <MDBox display="flex" justifyContent="flex-end" gap={1} mb={2}>
              <MDButton
                variant="outlined"
                color="info"
                onClick={fetchRoutersData}
                disabled={routersLoading}
              >
                {routersLoading ? "Refreshing Routers..." : "Refresh Routers"}
              </MDButton>
              <MDButton
                variant="outlined"
                color="info"
                onClick={diagnoseRoutersOnly}
                disabled={diagnosticsLoading || routers.length === 0}
              >
                {diagnosticsLoading ? "Diagnosing Routers..." : "Diagnose Routers"}
              </MDButton>
            </MDBox>
            )}
            <Card>
              <CardContent>
                {routersLoading ? (
                  <MDBox textAlign="center" py={3}>
                    <MDTypography>Loading routers...</MDTypography>
                  </MDBox>
                ) : routers.length === 0 ? (
                  <MDBox textAlign="center" py={3}>
                    <MDTypography>No routers found</MDTypography>
                  </MDBox>
                ) : (
                  <Grid container spacing={2}>
                    {routers.map((router, index) => {
                      const routerSsid = router.ssid || router.latestSsid || null;
                      const lastDiag = routerPingStatus[router.ip]?.lastChecked
                        ? formatDateTime(routerPingStatus[router.ip]?.lastChecked)
                        : null;
                      return (
                        <Grid
                          item
                          xs={12}
                          md={6}
                          xl={4}
                          key={`${router.ip || router.name || "router"}-${index}`}
                        >
                          <Card
                            sx={{
                              height: "100%",
                              border: "1px solid rgba(0, 0, 0, 0.08)",
                              boxShadow: "0 2px 10px rgba(0, 0, 0, 0.06)",
                            }}
                          >
                            <CardContent>
                              <MDBox
                                display="flex"
                                justifyContent="space-between"
                                alignItems="flex-start"
                                gap={1}
                              >
                                <MDBox>
                                  <MDTypography variant="button" fontWeight="bold">
                                    {router.name || "Router"}
                                  </MDTypography>
                                  <MDTypography variant="caption" color="text" display="block">
                                    Type: <strong>{router.type || "N/A"}</strong>
                                  </MDTypography>
                                </MDBox>
                                {renderStatus(
                                  routerPingStatus[router.ip]?.status || router.status,
                                  routerPingStatus[router.ip]?.stats
                                )}
                              </MDBox>
                              <MDBox mt={2} pt={1.5} sx={{ borderTop: "1px solid rgba(0, 0, 0, 0.08)" }}>
                                {routerSsid && (
                                  <MDTypography variant="caption" color="text" display="block">
                                    SSID: <strong>{routerSsid}</strong>
                                  </MDTypography>
                                )}
                                {!isClientPortal && (
                                  <>
                                    <MDTypography variant="caption" color="text" display="block" mt={routerSsid ? 1 : 0}>
                                      IP: <strong>{router.ip || "N/A"}</strong>
                                    </MDTypography>
                                    {router.ip && router.ip !== "none" && (
                                      <MDBox display="flex" alignItems="center" gap={0.5} mt={0.5}>
                                        <Tooltip title="Open with HTTP" arrow>
                                          <MDButton
                                            variant="text"
                                            color="info"
                                            size="small"
                                            onClick={() => openWebInterface(router.ip, "http")}
                                            sx={{ minWidth: "auto", p: 0.5 }}
                                          >
                                            <PublicIcon fontSize="small" />
                                          </MDButton>
                                        </Tooltip>
                                        <Tooltip title="Open with HTTPS" arrow>
                                          <MDButton
                                            variant="text"
                                            color="info"
                                            size="small"
                                            onClick={() => openWebInterface(router.ip, "https")}
                                            sx={{ minWidth: "auto", p: 0.5 }}
                                          >
                                            <HttpsIcon fontSize="small" />
                                          </MDButton>
                                        </Tooltip>
                                        <Tooltip title="Copy IP" arrow>
                                          <IconButton
                                            size="small"
                                            onClick={() => handleCopyToClipboard(router.ip)}
                                            sx={{ p: 0.5 }}
                                          >
                                            <ContentCopyIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      </MDBox>
                                    )}
                                  </>
                                )}
                                <MDTypography variant="caption" color="text" display="block" mt={1.5}>
                                  Last seen: <strong>{router.lastSeen || "N/A"}</strong>
                                </MDTypography>
                                <MDTypography variant="caption" color="text" display="block" mt={0.5}>
                                  Last diagnostic ping:{" "}
                                  <strong>{lastDiag || "Not run"}</strong>
                                </MDTypography>
                              </MDBox>
                            </CardContent>
                          </Card>
                        </Grid>
                      );
                    })}
                  </Grid>
                )}
              </CardContent>
            </Card>
          </MDBox>
        );
      case 2:
        return (
          <MDBox>
            {!isClientPortal && (
            <MDBox display="flex" justifyContent="flex-end" gap={1} mb={2}>
              <MDButton
                variant="outlined"
                color="info"
                onClick={fetchCpesData}
                disabled={cpesLoading}
              >
                {cpesLoading ? "Refreshing CPEs..." : "Refresh CPEs"}
              </MDButton>
              <MDButton
                variant="outlined"
                color="info"
                onClick={diagnoseCpesOnly}
                disabled={diagnosticsLoading || cpes.length === 0}
              >
                {diagnosticsLoading ? "Diagnosing CPEs..." : "Diagnose CPEs"}
              </MDButton>
            </MDBox>
            )}
            <Card>
              <CardContent>
                {cpesLoading ? (
                  <MDBox textAlign="center" py={3}>
                    <MDTypography>Loading CPEs...</MDTypography>
                  </MDBox>
                ) : cpes.length === 0 ? (
                  <MDBox textAlign="center" py={3}>
                    <MDTypography>No CPEs found</MDTypography>
                  </MDBox>
                ) : (
                  <Grid container spacing={2}>
                    {cpes.map((cpe, index) => {
                      const pollStatus = String(cpe.latestPollStatus || "").trim().toLowerCase();
                      const pollDerivedStatus =
                        pollStatus === "online"
                          ? "Online"
                          : pollStatus === "offline"
                            ? "Offline"
                            : null;
                      const currentStatus =
                        cpePingStatus[cpe.ip]?.status ||
                        cpe.status ||
                        (isClientPortal ? pollDerivedStatus : null);
                      const cpeDiag = cpeDiagnosticsByIp?.[cpe.ip];
                      const diagIssues = Array.isArray(cpeDiag?.issues) ? cpeDiag.issues : [];
                      const expectedIssue = diagIssues.includes("cpe_out_of_alignment");
                      const ethIssue = diagIssues.some((i) =>
                        ["network_cable_unplugged", "ethernet_speed_below_100mbps"].includes(i)
                      );
                      const signalDbm = getCpeMetric(cpe.ip, "signal_dbm", cpe.latestSignal ?? "N/A");
                      const expectedSignalDbm = getCpeMetric(cpe.ip, "expected_signal_dbm", "N/A");
                      const cpeRxDlRaw = getCpeMetric(cpe.ip, "cpe_rx_signal_dbm", null);
                      const apRxUlRaw = getCpeMetric(cpe.ip, "ap_rx_signal_dbm", null);
                      const cpeRxExpectedRaw = getCpeMetric(cpe.ip, "cpe_rx_signal_expected_dbm", null);
                      const apRxExpectedRaw = getCpeMetric(cpe.ip, "ap_rx_signal_expected_dbm", null);
                      const cpeRxDownlink =
                        cpeRxDlRaw !== null && cpeRxDlRaw !== undefined && cpeRxDlRaw !== ""
                          ? cpeRxDlRaw
                          : signalDbm;
                      const expectedCpeRx =
                        cpeRxExpectedRaw !== null &&
                        cpeRxExpectedRaw !== undefined &&
                        cpeRxExpectedRaw !== ""
                          ? cpeRxExpectedRaw
                          : expectedSignalDbm;
                      const minSigFloor = cpeDiag?.thresholds?.min_signal_dbm ?? -65;
                      const minExpHw = cpeDiag?.thresholds?.min_expected_signal_hardware_dbm ?? -62;
                      const cpeRxNum = Number(cpeRxDownlink);
                      const expectedCpeRxNum = Number(expectedCpeRx);
                      const cpeRxSignalLow =
                        diagIssues.includes("signal_too_low") ||
                        (!Number.isNaN(cpeRxNum) && cpeRxNum <= minSigFloor);
                      const expectedCpeRxHardwareWeak =
                        diagIssues.includes("link_hardware_mismatch") ||
                        (!Number.isNaN(expectedCpeRxNum) && expectedCpeRxNum < minExpHw);
                      const expectedCpeRxHighlight = expectedIssue || expectedCpeRxHardwareWeak;
                      const apRxHighlight = diagIssues.some((i) =>
                        ["signal_too_strong", "cpe_out_of_alignment"].includes(i)
                      );
                      const diagnosticsCritical =
                        diagIssues.includes("signal_too_low") ||
                        diagIssues.includes("link_hardware_mismatch");
                      const cpeLegacyNonAc =
                        cpeDiag?.diagnostics_source === "uisp" &&
                        (diagIssues.includes("legacy_non_ac_radio") ||
                          getCpeMetric(cpe.ip, "uisp_likely_non_ac", false) === true);
                      const diagnosticsLegacyNonAc = cpeLegacyNonAc;
                      const linkDistanceKm = getCpeMetric(cpe.ip, "link_distance_km", "N/A");
                      const linkDistanceIsEstimate =
                        getCpeMetric(cpe.ip, "link_distance_is_estimate", false) === true;
                      const linkDistanceNote = getCpeMetric(cpe.ip, "link_distance_note", "") || "";
                      const apRxSigEst = getCpeMetric(cpe.ip, "ap_rx_signal_is_estimate", false) === true;
                      const apRxSigNote = getCpeMetric(cpe.ip, "ap_rx_signal_note", "") || "";
                      const apRxExpEst =
                        getCpeMetric(cpe.ip, "ap_rx_expected_signal_is_estimate", false) === true;
                      const apRxExpNote = getCpeMetric(cpe.ip, "ap_rx_expected_signal_note", "") || "";
                      const ethernetLinkRaw = getCpeMetric(cpe.ip, "ethernet_link", "unknown");
                      const ethernetLink = String(ethernetLinkRaw).toUpperCase();
                      const wirelessRx = getCpeMetric(cpe.ip, "wireless_rx_link_bps", null);
                      const wirelessTx = getCpeMetric(cpe.ip, "wireless_tx_link_bps", null);
                      const uispModel = getCpeMetric(cpe.ip, "uisp_model", cpe.type || "N/A");
                      const uptimeHuman = cpeDiag?.uptime_human || null;
                      const ssid = getCpeMetric(cpe.ip, "ssid", cpe.latestSsid || "N/A");
                          const pingStats = cpePingStatus?.[cpe.ip]?.stats || {};
                          const diagAvgPing =
                            pingStats.averagePingTime !== undefined && pingStats.averagePingTime !== null
                              ? Number(pingStats.averagePingTime).toFixed(2)
                              : "N/A";
                          const diagJitter =
                            pingStats.jitter !== undefined && pingStats.jitter !== null
                              ? Number(pingStats.jitter).toFixed(2)
                              : "N/A";
                          const diagPacketLoss =
                            pingStats.loss !== undefined && pingStats.loss !== null
                              ? Number(pingStats.loss).toFixed(0)
                              : "N/A";
                          const diagPacketsRx = pingStats.packetsReceived ?? "N/A";
                          const diagPacketsTx = pingStats.packetsTransmitted ?? "N/A";

                      return (
                        <Grid item xs={12} md={6} xl={4} key={`${cpe.ip || cpe.name || "cpe"}-${index}`}>
                          <Card
                            sx={{
                              height: "100%",
                              border: "1px solid rgba(0, 0, 0, 0.08)",
                              boxShadow: "0 2px 10px rgba(0, 0, 0, 0.06)",
                            }}
                          >
                            <CardContent>
                              <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
                                <MDBox>
                                  <MDTypography variant="button" fontWeight="bold">
                                    {cpe.name || "Unknown CPE"}
                                  </MDTypography>
                                  <MDTypography variant="caption" color="text" display="block">
                                    Model:{" "}
                                    <Typography
                                      component="span"
                                      variant="caption"
                                      color={cpeLegacyNonAc ? "error" : "text"}
                                      fontWeight="bold"
                                    >
                                      {uispModel}
                                    </Typography>
                                  </MDTypography>
                                </MDBox>
                                {renderStatus(currentStatus, cpePingStatus[cpe.ip]?.stats)}
                              </MDBox>

                              {!isClientPortal && (
                              <MDBox mt={1.5}>
                                <MDTypography variant="caption" color="text" fontWeight="medium">
                                  IP: {cpe.ip || "N/A"}
                                </MDTypography>
                                {Array.isArray(cpe.ipConflictWith) && cpe.ipConflictWith.length > 0 && (
                                  <MDBox
                                    display="flex"
                                    alignItems="center"
                                    gap={0.5}
                                    mt={0.5}
                                    px={1}
                                    py={0.5}
                                    sx={{
                                      backgroundColor: "rgba(237, 108, 2, 0.08)",
                                      borderLeft: "3px solid #ed6c02",
                                      borderRadius: "4px",
                                    }}
                                  >
                                    <WarningAmberIcon sx={{ fontSize: 14, color: "warning.main" }} />
                                    <MDTypography variant="caption" sx={{ color: "warning.main", fontWeight: 600 }}>
                                      IP conflict &mdash; also assigned to: {cpe.ipConflictWith.join(", ")}
                                    </MDTypography>
                                  </MDBox>
                                )}
                                {cpe.ip && cpe.ip !== "none" && (
                                  <MDBox display="flex" alignItems="center" gap={0.5} mt={0.5}>
                                    <Tooltip title="Open with HTTP" arrow>
                                      <MDButton
                                        variant="text"
                                        color="info"
                                        size="small"
                                        onClick={() => openWebInterface(cpe.ip, "http")}
                                        sx={{ minWidth: "auto", p: 0.5 }}
                                      >
                                        <PublicIcon fontSize="small" />
                                      </MDButton>
                                    </Tooltip>
                                    <Tooltip title="Open with HTTPS" arrow>
                                      <MDButton
                                        variant="text"
                                        color="info"
                                        size="small"
                                        onClick={() => openWebInterface(cpe.ip, "https")}
                                        sx={{ minWidth: "auto", p: 0.5 }}
                                      >
                                        <HttpsIcon fontSize="small" />
                                      </MDButton>
                                    </Tooltip>
                                    <Tooltip title="Copy IP" arrow>
                                      <IconButton
                                        size="small"
                                        onClick={() => handleCopyToClipboard(cpe.ip)}
                                        sx={{ p: 0.5 }}
                                      >
                                        <ContentCopyIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <MDBox display="flex" alignItems="center" gap={0.25}>
                                      <Tooltip
                                        title={
                                          isCpeUispReadyForReboot(cpe)
                                            ? "Reboot CPE via UISP (opens confirmation)"
                                            : isCpeUispPathFromDiagnostics(cpe)
                                              ? "UISP is not connected to this device — reboot is unavailable"
                                              : "Run Diagnose CPEs first — reboot requires UISP diagnostics with an active device connection"
                                        }
                                        arrow
                                      >
                                        <span>
                                          <IconButton
                                            size="small"
                                            color="warning"
                                            disabled={!isCpeUispReadyForReboot(cpe)}
                                            onClick={() => openCpeRebootPrompt(cpe)}
                                            sx={{ p: 0.5 }}
                                          >
                                            <RestartAltIcon fontSize="small" />
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                      <Tooltip
                                        title={
                                          isCpeUispReadyForReboot(cpe)
                                            ? "Speed test (UISP)"
                                            : isCpeUispPathFromDiagnostics(cpe)
                                              ? "UISP is not connected — speed test is unavailable"
                                              : "Run Diagnose CPEs first — speed test requires UISP diagnostics with an active device connection"
                                        }
                                        arrow
                                      >
                                        <span>
                                          <IconButton
                                            size="small"
                                            color="info"
                                            disabled={
                                              !isCpeUispReadyForReboot(cpe) ||
                                              Boolean(cpeSpeedTestLoading[cpe.ip])
                                            }
                                            onClick={() => handleCpeSpeedTest(cpe)}
                                            sx={{ p: 0.5 }}
                                          >
                                            {cpeSpeedTestLoading[cpe.ip] ? (
                                              <CircularProgress size={18} color="inherit" />
                                            ) : (
                                              <SpeedIcon fontSize="small" />
                                            )}
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                      <Tooltip
                                        title={
                                          isCpeUispPathFromDiagnostics(cpe)
                                            ? "UISP trends (2/30/90 days)"
                                            : "Run Diagnose CPEs first — UISP trends require UISP diagnostics context"
                                        }
                                        arrow
                                      >
                                        <span>
                                          <IconButton
                                            size="small"
                                            color="info"
                                            disabled={!isCpeUispPathFromDiagnostics(cpe)}
                                            onClick={() => openCpeUispTrendModal(cpe)}
                                            sx={{ p: 0.5 }}
                                          >
                                            <ShowChartIcon fontSize="small" />
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                      <Tooltip title="Sector Analysis Data" arrow>
                                        <span>
                                          <IconButton
                                            size="small"
                                            color="info"
                                            onClick={() => openCpeSectorAnalysisModal(cpe)}
                                            sx={{ p: 0.5 }}
                                            aria-label="Sector Analysis Data"
                                          >
                                            <CellTowerIcon fontSize="small" />
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                    </MDBox>
                                  </MDBox>
                                )}
                              </MDBox>
                              )}

                              <MDBox mt={isClientPortal ? 1.5 : 2} pt={1.5} sx={{ borderTop: "1px solid rgba(0, 0, 0, 0.08)" }}>
                                <MDTypography variant="caption" color="text" display="block">
                                  SSID: <strong>{ssid}</strong>
                                </MDTypography>
                                <MDTypography variant="caption" color={cpeRxSignalLow ? "error" : "text"} display="block">
                                  CPE RX (downlink): <strong>{cpeRxDownlink} dBm</strong>
                                </MDTypography>
                                {(apRxUlRaw !== null &&
                                  apRxUlRaw !== undefined &&
                                  apRxUlRaw !== "") ||
                                apRxSigEst ? (
                                  <Tooltip title={apRxSigNote || ""} arrow disableHoverListener={!apRxSigNote}>
                                    <MDTypography
                                      variant="caption"
                                      color={
                                        apRxSigEst ? "warning" : apRxHighlight ? "error" : "text"
                                      }
                                      display="block"
                                    >
                                      AP RX (uplink at AP):{" "}
                                      <strong>
                                        {apRxUlRaw !== null &&
                                        apRxUlRaw !== undefined &&
                                        apRxUlRaw !== ""
                                          ? apRxUlRaw
                                          : "N/A"}{" "}
                                        dBm
                                      </strong>
                                    </MDTypography>
                                  </Tooltip>
                                ) : null}
                                <MDTypography variant="caption" color={expectedCpeRxHighlight ? "error" : "text"} display="block">
                                  Expected CPE RX: <strong>{expectedCpeRx} dBm</strong>
                                </MDTypography>
                                {(apRxExpectedRaw !== null &&
                                  apRxExpectedRaw !== undefined &&
                                  apRxExpectedRaw !== "") ||
                                apRxExpEst ? (
                                  <Tooltip title={apRxExpNote || ""} arrow disableHoverListener={!apRxExpNote}>
                                    <MDTypography
                                      variant="caption"
                                      color={
                                        apRxExpEst ? "warning" : expectedIssue ? "error" : "text"
                                      }
                                      display="block"
                                    >
                                      Expected AP RX:{" "}
                                      <strong>
                                        {apRxExpectedRaw !== null &&
                                        apRxExpectedRaw !== undefined &&
                                        apRxExpectedRaw !== ""
                                          ? apRxExpectedRaw
                                          : "N/A"}{" "}
                                        dBm
                                      </strong>
                                    </MDTypography>
                                  </Tooltip>
                                ) : null}
                                <Tooltip
                                  title={linkDistanceNote || ""}
                                  arrow
                                  disableHoverListener={!linkDistanceNote}
                                >
                                  <MDTypography
                                    variant="caption"
                                    color={linkDistanceIsEstimate ? "warning" : "text"}
                                    display="block"
                                  >
                                    Link Distance: <strong>{linkDistanceKm} km</strong>
                                  </MDTypography>
                                </Tooltip>
                                <MDTypography variant="caption" color="text" display="block">
                                  Wireless RX (capacity): <strong>{formatLinkBps(wirelessRx)}</strong>
                                </MDTypography>
                                <MDTypography variant="caption" color="text" display="block">
                                  Wireless TX (capacity): <strong>{formatLinkBps(wirelessTx)}</strong>
                                </MDTypography>
                                <MDTypography
                                  variant="caption"
                                  color={
                                    ethIssue || ethernetLink === "DOWN" || ethernetLink.includes("UNPLUGGED")
                                      ? "error"
                                      : "text"
                                  }
                                  display="block"
                                >
                                  Cable Link: <strong>{ethernetLink}</strong>
                                </MDTypography>
                                <MDTypography variant="caption" color="text" display="block">
                                  Last Poll Ping: <strong>{cpe.latestPingMs ?? "N/A"} ms</strong>
                                </MDTypography>
                                <MDTypography variant="caption" color="text" display="block">
                                  Diagnostic Ping:{" "}
                                  <strong>
                                    avg {diagAvgPing} ms | jitter {diagJitter} ms | loss {diagPacketLoss}% ({diagPacketsRx}/{diagPacketsTx})
                                  </strong>
                                </MDTypography>
                                {!isClientPortal && (
                                <MDBox mt={1}>
                                  <MDButton
                                    variant="outlined"
                                    color="info"
                                    size="small"
                                    onClick={() => openCpeHistoryModal(cpe)}
                                  >
                                    View Ping History
                                  </MDButton>
                                </MDBox>
                                )}
                              </MDBox>

                              {!isClientPortal && (
                              <MDBox
                                mt={2}
                                pt={1.5}
                                px={diagnosticsCritical || diagnosticsLegacyNonAc ? 1 : 0}
                                sx={{
                                  borderTop:
                                    diagnosticsCritical || diagnosticsLegacyNonAc
                                      ? "none"
                                      : "1px solid rgba(0, 0, 0, 0.08)",
                                  ...(diagnosticsCritical || diagnosticsLegacyNonAc
                                    ? {
                                        borderRadius: 1,
                                        bgcolor: (theme) =>
                                          theme.palette.mode === "dark"
                                            ? "rgba(211, 47, 47, 0.12)"
                                            : "rgba(211, 47, 47, 0.07)",
                                        border: "1px solid",
                                        borderColor: "error.main",
                                      }
                                    : {}),
                                }}
                              >
                                <MDTypography variant="caption" color="text" fontWeight="medium" display="block" mb={0.5}>
                                  Diagnostics
                                </MDTypography>
                                {renderCpeDiagnosticsSection(cpeDiagnosticsByIp?.[cpe.ip] || null)}
                                {cpeDiag?.diagnostics_source === "uisp" && uptimeHuman && (
                                  <MDTypography variant="caption" color="text" display="block" mt={0.5}>
                                    Uptime: <strong>{uptimeHuman}</strong>
                                  </MDTypography>
                                )}
                                <MDTypography variant="caption" color="text" display="block" mt={0.8}>
                                  {`Last seen online: ${
                                    cpePingStatus?.[cpe.ip]?.lastOnlineAt
                                      ? formatDateTime(cpePingStatus[cpe.ip].lastOnlineAt)
                                      : cpe.latestOnlinePollAt
                                        ? formatDateTime(cpe.latestOnlinePollAt)
                                        : "Never"
                                  }`}
                                </MDTypography>
                              </MDBox>
                              )}
                            </CardContent>
                          </Card>
                        </Grid>
                      );
                    })}
                  </Grid>
                )}
              </CardContent>
            </Card>
          </MDBox>
        );
      default:
        return null;
    }
  };

  const openWebInterface = (ip, protocol = "http") => {
    if (ip && ip !== "none" && ip !== "Not assigned") {
      window.open(`${protocol}://${ip}`, "_blank");
    }
  };

  if (loading) {
    return wrapCustomerViewShell(
      <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <MDBox>Loading customer details...</MDBox>
      </MDBox>
    );
  }

  if (error) {
    return wrapCustomerViewShell(
      <MDBox py={3} px={3}>
        <PageErrorAlert
          title={PAGE_ERROR_TITLES.customerData}
          message={error}
          sx={{ maxWidth: "960px" }}
        />
      </MDBox>
    );
  }

  return wrapCustomerViewShell(
    <Fragment>
      <MDBox py={3}>
        <MDBox mb={3}>
          <Card>
            <MDBox
              bgColor={getCustomerHeaderGradient({
                custType: customerData?.cust_type,
                suspended: suspendedStatus,
              })}
              variant="gradient"
              borderRadius="lg"
              coloredShadow={getCustomerHeaderGradient({
                custType: customerData?.cust_type,
                suspended: suspendedStatus,
              })}
              mx={2}
              mt={-3}
              p={3}
              mb={1}
              display="flex"
              flexDirection={{ xs: "column", md: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "stretch", md: "center" }}
              gap={{ xs: 1.5, md: 0 }}
              textAlign={{ xs: "left", md: "center" }}
            >
              <MDBox display="flex" flexDirection="column" alignItems={{ xs: "flex-start", md: "center" }} gap={1}>
                <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                  {!isClientPortal && (
                    <MDButton
                      variant="text"
                      color="white"
                      onClick={() => navigate(returnUrl)}
                      sx={{ minWidth: "auto", px: 1 }}
                    >
                      <Icon>arrow_back</Icon>&nbsp;Back
                    </MDButton>
                  )}
                  <MDTypography variant="h6" color="white" sx={{ textAlign: "left" }}>
                    {getCustomerHeaderTitle({
                      custType: customerData?.cust_type,
                      suspended: suspendedStatus,
                      isClientPortal,
                    })}
                  </MDTypography>
                </MDBox>

                <MDBox display="flex" flexDirection={{ xs: "column", sm: "row" }} gap={1} width={{ xs: "100%", md: "auto" }}>
                  {canAddNotes && (
                    <MDButton
                      variant="outlined"
                      color="white"
                      onClick={() => customerNotesPanelRef.current?.openAddModal()}
                      startIcon={<Icon>note_add</Icon>}
                      disabled={!customerData}
                      sx={{
                        alignSelf: { xs: "stretch", md: "flex-start" },
                        display: { xs: "flex", md: "none" },
                      }}
                    >
                      Add Note
                    </MDButton>
                  )}
                  {isManagerOrAdmin && (
                    <MDButton
                      variant="outlined"
                      color="white"
                      onClick={handleEditCustomer}
                      startIcon={<Icon>edit</Icon>}
                      disabled={!customerData}
                      sx={{
                        alignSelf: { xs: "stretch", md: "flex-start" },
                        display: { xs: "flex", md: "none" },
                      }}
                    >
                      Edit Customer
                    </MDButton>
                  )}
                </MDBox>
              </MDBox>

              <MDBox display="flex" gap={1} sx={{ display: { xs: "none", md: "flex" }, flexShrink: 0 }}>
                {canAddNotes && (
                  <MDButton
                    variant="outlined"
                    color="white"
                    onClick={() => customerNotesPanelRef.current?.openAddModal()}
                    startIcon={<Icon>note_add</Icon>}
                    disabled={!customerData}
                  >
                    Add Note
                  </MDButton>
                )}
                {isManagerOrAdmin && (
                  <MDButton
                    variant="outlined"
                    color="white"
                    onClick={handleEditCustomer}
                    startIcon={<Icon>edit</Icon>}
                    disabled={!customerData}
                  >
                    Edit Customer
                  </MDButton>
                )}
              </MDBox>
            </MDBox>
            <MDBox px={{ xs: 2, md: 3 }} py={2} display="flex" flexDirection="column" alignItems="flex-start">
              <MDBox display="flex" alignItems="center" gap={2}>
                        <StatusBox 
                          custType={customerData?.cust_type || 'Unknown'} 
                          suspended={suspendedStatus} 
                          size={48} 
                        />
                  <MDBox>
                    <MDTypography variant="h6" color="text" fontWeight="medium">
                      Customer Code: {customerData?.code?.toUpperCase() || "Not provided"}
                    </MDTypography>
                    <MDTypography variant="h6" color="text" fontWeight="medium">
                      {customerData?.name || "Not provided"}
                    </MDTypography>
                  </MDBox>
              </MDBox>
              <MDBox mt={1}>
                <MDTypography variant="subtitle2" color="text" fontWeight="regular">
                  Total Active Subscriptions: 
                  <MDTypography component="span" variant="subtitle2" color="dark" fontWeight="medium">
                    R {totalActiveMonthlyFee.toFixed(2)}
                  </MDTypography>
                </MDTypography>
                {customerData?.type && !isClientPortal && (
                  <MDTypography variant="subtitle2" color="warning" fontWeight="regular" mt={0.5}>
                    Account Note:{" "}
                    <MDTypography component="span" variant="subtitle2" color="dark" fontWeight="medium">
                      {customerData.type}
                    </MDTypography>
                  </MDTypography>
                )}
              </MDBox>
              {!isClientPortal && (
              <CustomerNotesPanel
                ref={customerNotesPanelRef}
                customerId={customerId}
                canAddNotes={canAddNotes}
                canManageNotes={isManagerOrAdmin}
              />
              )}
            </MDBox>
            <MDBox p={{ xs: 2, md: 3 }}>
              <MDBox>
                <MDBox
                  role="tablist"
                  aria-label="Customer sections"
                  display="grid"
                  gridTemplateColumns={{
                    xs: "repeat(2, minmax(0, 1fr))",
                    md: "repeat(3, minmax(0, 1fr))",
                  }}
                  gap={0.5}
                  sx={{
                    mb: 2,
                    p: 0.5,
                    borderRadius: 2,
                    bgcolor: "#f8f9fa",
                    border: "1px solid #e0e0e0",
                  }}
                >
                  {customerMainTabs.map((tab) => renderCustomerMainTabButton(tab))}
                </MDBox>
                {renderCustomerTabContent()}
              </MDBox>
            </MDBox>
          </Card>
        </MDBox>

        <MDBox mb={3} mt={6}>
          <CustomerAccountBalancePanel
            customerId={customerId}
            isManagerOrAdmin={isManagerOrAdmin}
            balance={balance}
            balanceLoading={balanceLoading}
            balanceError={balanceError}
            showBillingHistory={showBillingHistory}
            onToggleBillingHistory={() => {
              setShowBillingHistory(!showBillingHistory);
              if (!showBillingHistory) {
                handleRefreshBillingHistory();
              }
            }}
            onRefreshBillingHistory={handleRefreshBillingHistory}
            billingCustomerId={Number(customerData?.vat_customer_id || 0)}
            onCreateQuote={isClientPortal ? undefined : handleCreateQuote}
            onViewStatement={handleOpenStatementPreview}
            statementPreviewLoading={statementPreviewLoading}
            billingHistoryLoading={billingHistoryLoading}
            billingHistoryError={billingHistoryError}
            billingHistoryTable={billingHistoryTable}
            billingHistoryCount={billingHistory.length}
          />
        </MDBox>

        <MDBox mb={3}>
          <Card>
            <MDBox
              mx={2}
              mt={-3}
              py={3}
              px={2}
              variant="gradient"
              bgColor="info"
              borderRadius="lg"
              coloredShadow="info"
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <MDTypography variant="h6" color="white">
                Outbound Communication
              </MDTypography>
            </MDBox>
            <CardContent>
              {outboundCommunicationLoading ? (
                <MDBox display="flex" justifyContent="center">
                  <CircularProgress />
                </MDBox>
              ) : outboundCommunicationError ? (
                <PageErrorAlert title={PAGE_ERROR_TITLES.form} message={outboundCommunicationError} />
              ) : outboundCommunication.length > 0 ? (
                <DataTable
                  table={outboundCommunicationTable}
                  isSorted={false}
                  entriesPerPage={false}
                  showTotalEntries
                  noEndBorder
                />
              ) : (
                <MDBox textAlign="center">
                  <MDTypography variant="body2" color="text">
                    No outbound communication available
                  </MDTypography>
                </MDBox>
              )}
            </CardContent>
          </Card>
        </MDBox>

      </MDBox>
      <NewTicketDialog
        open={newTicketDialogOpen}
        onClose={() => setNewTicketDialogOpen(false)}
        onSuccess={handleNewTicketSuccess}
        portalVariant={portalVariant}
        clientAccessToken={clientAccessToken}
        initialCustomer={
          customerData
            ? {
                customer_id: customerData.customer_id,
                code: customerData.code,
                name: customerData.name,
                telephone1: customerData.telephone1,
              }
            : null
        }
        lockCustomerSelection
      />

      <Dialog
        open={invoicePreviewOpen}
        onClose={handleCloseInvoicePreview}
        maxWidth="lg"
        fullWidth
        sx={pdfPreviewDialogSx}
      >
        <DialogTitle sx={{ flexShrink: 0 }}>
          {`Invoice Preview${activeInvoiceId > 0 ? ` #${activeInvoiceId}` : ""}`}
        </DialogTitle>
        <DialogContent dividers sx={PDF_PREVIEW_CONTENT_SX}>
          {invoicePreviewLoading ? (
            <MDBox display="flex" flex={1} justifyContent="center" alignItems="center" p={2}>
              <CircularProgress />
            </MDBox>
          ) : invoicePreviewError ? (
            <MDBox p={2}>
              <PageErrorAlert title={PAGE_ERROR_TITLES.billingHistory} message={invoicePreviewError} />
            </MDBox>
          ) : invoicePreviewUrl ? (
            <MDBox sx={pdfIframeContainerSx}>
              <iframe
                ref={invoicePreviewIframeRef}
                src={invoicePreviewUrl}
                title={`Invoice ${activeInvoiceId}`}
                style={{ border: 0, width: "100%", height: "100%", display: "block" }}
              />
            </MDBox>
          ) : (
            <MDBox p={2}>
              <MDTypography variant="body2" color="text">
                No invoice preview available.
              </MDTypography>
            </MDBox>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            flexShrink: 0,
            flexDirection: "column",
            alignItems: "stretch",
            gap: 1.5,
            px: 2,
            py: 1.5,
          }}
        >
          <TextField
            fullWidth
            size="small"
            label="Email override (optional)"
            placeholder={customerData?.email || "Uses billing profile email if empty"}
            value={invoiceSendEmail}
            onChange={(event) => setInvoiceSendEmail(event.target.value)}
            disabled={invoiceSendLoading || invoiceSendWhatsAppLoading}
            InputLabelProps={{ shrink: true }}
          />
          <MDBox display="flex" flexWrap="wrap" gap={1} justifyContent="flex-end">
            <MDButton onClick={handleCloseInvoicePreview}>Close</MDButton>
            <MDButton
              variant="outlined"
              color="info"
              onClick={handleDownloadInvoice}
              disabled={invoiceDownloadLoading || activeInvoiceId < 1}
            >
              {invoiceDownloadLoading ? "Downloading..." : "Download"}
            </MDButton>
            <MDButton
              variant="outlined"
              color="info"
              onClick={() => printPdfIframe(invoicePreviewIframeRef, invoicePreviewUrl)}
              disabled={!invoicePreviewUrl}
            >
              Print
            </MDButton>
            <MDButton
              variant="gradient"
              color="info"
              onClick={handleSendInvoiceEmail}
              disabled={invoiceSendLoading || invoiceSendWhatsAppLoading || activeInvoiceId < 1}
            >
              {invoiceSendLoading ? "Sending..." : "Send Invoice"}
            </MDButton>
            <Tooltip
              title={
                formatPhoneNumber(customerData?.telephone1)
                  ? `Send to WhatsApp ${customerData?.telephone1}`
                  : "Customer has no valid WhatsApp number"
              }
            >
              <span>
                <MDButton
                  variant="gradient"
                  color="success"
                  onClick={handleSendInvoiceWhatsApp}
                  disabled={
                    invoiceSendLoading ||
                    invoiceSendWhatsAppLoading ||
                    activeInvoiceId < 1 ||
                    !formatPhoneNumber(customerData?.telephone1)
                  }
                >
                  {invoiceSendWhatsAppLoading ? "Sending..." : "Send WhatsApp"}
                </MDButton>
              </span>
            </Tooltip>
          </MDBox>
        </DialogActions>
      </Dialog>

      <Dialog
        open={quotePreviewOpen}
        onClose={handleCloseQuotePreview}
        maxWidth="lg"
        fullWidth
        sx={pdfPreviewDialogSx}
      >
        <DialogTitle sx={{ flexShrink: 0 }}>
          {`Quotation Preview${activeQuoteId > 0 ? ` #${activeQuoteId}` : ""}`}
        </DialogTitle>
        <DialogContent dividers sx={PDF_PREVIEW_CONTENT_SX}>
          {quotePreviewLoading ? (
            <MDBox display="flex" flex={1} justifyContent="center" alignItems="center" p={2}>
              <CircularProgress />
            </MDBox>
          ) : quotePreviewError ? (
            <MDBox p={2}>
              <PageErrorAlert title={PAGE_ERROR_TITLES.billingHistory} message={quotePreviewError} />
            </MDBox>
          ) : quotePreviewUrl ? (
            <MDBox sx={pdfIframeContainerSx}>
              <iframe
                ref={quotePreviewIframeRef}
                src={quotePreviewUrl}
                title={`Quotation ${activeQuoteId}`}
                style={{ border: 0, width: "100%", height: "100%", display: "block" }}
              />
            </MDBox>
          ) : (
            <MDBox p={2}>
              <MDTypography variant="body2" color="text">
                No quotation preview available.
              </MDTypography>
            </MDBox>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            flexShrink: 0,
            flexDirection: "column",
            alignItems: "stretch",
            gap: 1.5,
            px: 2,
            py: 1.5,
          }}
        >
          <TextField
            fullWidth
            size="small"
            label="Email override (optional)"
            placeholder={customerData?.email || "Uses billing profile email if empty"}
            value={quoteSendEmail}
            onChange={(event) => setQuoteSendEmail(event.target.value)}
            disabled={quoteSendLoading || quoteSendWhatsAppLoading}
            InputLabelProps={{ shrink: true }}
          />
          <MDBox display="flex" flexWrap="wrap" gap={1} justifyContent="flex-end">
            <MDButton onClick={handleCloseQuotePreview}>Close</MDButton>
            {isManagerOrAdmin && (
              <MDButton
                variant="outlined"
                color="secondary"
                onClick={handleEditActiveQuote}
                disabled={activeQuoteId < 1}
              >
                Edit Quotation
              </MDButton>
            )}
            <MDButton
              variant="outlined"
              color="info"
              onClick={handleDownloadQuote}
              disabled={quoteDownloadLoading || activeQuoteId < 1}
            >
              {quoteDownloadLoading ? "Downloading..." : "Download"}
            </MDButton>
            <MDButton
              variant="outlined"
              color="info"
              onClick={() => printPdfIframe(quotePreviewIframeRef, quotePreviewUrl)}
              disabled={!quotePreviewUrl}
            >
              Print
            </MDButton>
            <MDButton
              variant="gradient"
              color="info"
              onClick={handleSendQuoteEmail}
              disabled={quoteSendLoading || quoteSendWhatsAppLoading || activeQuoteId < 1}
            >
              {quoteSendLoading ? "Sending..." : "Send Quotation"}
            </MDButton>
            <Tooltip
              title={
                formatPhoneNumber(customerData?.telephone1)
                  ? `Send to WhatsApp ${customerData?.telephone1}`
                  : "Customer has no valid WhatsApp number"
              }
            >
              <span>
                <MDButton
                  variant="gradient"
                  color="success"
                  onClick={handleSendQuoteWhatsApp}
                  disabled={
                    quoteSendLoading ||
                    quoteSendWhatsAppLoading ||
                    activeQuoteId < 1 ||
                    !formatPhoneNumber(customerData?.telephone1)
                  }
                >
                  {quoteSendWhatsAppLoading ? "Sending..." : "Send WhatsApp"}
                </MDButton>
              </span>
            </Tooltip>
          </MDBox>
        </DialogActions>
      </Dialog>

      <Dialog
        open={statementPreviewOpen}
        onClose={handleCloseStatementPreview}
        maxWidth="lg"
        fullWidth
        sx={pdfPreviewDialogSx}
      >
        <DialogTitle sx={{ flexShrink: 0 }}>
          {`Statement Preview${
            statementDateRange?.startDate && statementDateRange?.endDate
              ? ` (${statementDateRange.startDate} — ${statementDateRange.endDate})`
              : ""
          }`}
        </DialogTitle>
        <DialogContent dividers sx={PDF_PREVIEW_CONTENT_SX}>
          {statementPreviewLoading ? (
            <MDBox display="flex" flex={1} justifyContent="center" alignItems="center" p={2}>
              <CircularProgress />
            </MDBox>
          ) : statementPreviewError ? (
            <MDBox p={2}>
              <PageErrorAlert title={PAGE_ERROR_TITLES.billingHistory} message={statementPreviewError} />
            </MDBox>
          ) : statementPreviewUrl ? (
            <MDBox sx={pdfIframeContainerSx}>
              <iframe
                ref={statementPreviewIframeRef}
                src={statementPreviewUrl}
                title="Customer statement"
                style={{ border: 0, width: "100%", height: "100%", display: "block" }}
              />
            </MDBox>
          ) : (
            <MDBox p={2}>
              <MDTypography variant="body2" color="text">
                No statement preview available.
              </MDTypography>
            </MDBox>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            flexShrink: 0,
            flexDirection: "column",
            alignItems: "stretch",
            gap: 1.5,
            px: 2,
            py: 1.5,
          }}
        >
          <TextField
            fullWidth
            size="small"
            label="Email override (optional)"
            placeholder={customerData?.email || "Uses billing profile email if empty"}
            value={statementSendEmail}
            onChange={(event) => setStatementSendEmail(event.target.value)}
            disabled={statementSendLoading}
            InputLabelProps={{ shrink: true }}
          />
          <MDBox
            display="flex"
            flexWrap="wrap"
            gap={1}
            justifyContent="space-between"
            alignItems="center"
          >
            <MDBox display="flex" flexWrap="wrap" gap={1} alignItems="center">
              <TextField
                type="date"
                label="From"
                size="small"
                InputLabelProps={{ shrink: true }}
                value={statementStartInput}
                onChange={(event) => setStatementStartInput(event.target.value)}
                disabled={statementPreviewLoading}
                sx={{ minWidth: 148 }}
              />
              <TextField
                type="date"
                label="To"
                size="small"
                InputLabelProps={{ shrink: true }}
                value={statementEndInput}
                onChange={(event) => setStatementEndInput(event.target.value)}
                disabled={statementPreviewLoading}
                sx={{ minWidth: 148 }}
              />
              <MDButton
                variant="outlined"
                color="info"
                onClick={handleApplyStatementDateRange}
                disabled={
                  statementPreviewLoading || !statementStartInput || !statementEndInput
                }
                sx={{ alignSelf: "flex-start", mt: 0.5 }}
              >
                Apply
              </MDButton>
            </MDBox>
            <MDBox display="flex" flexWrap="wrap" gap={1} alignItems="center">
              <MDButton onClick={handleCloseStatementPreview}>Close</MDButton>
              <MDButton
                variant="outlined"
                color="info"
                onClick={handleDownloadStatement}
                disabled={statementDownloadLoading || !statementPreviewUrl}
              >
                {statementDownloadLoading ? "Downloading..." : "Download"}
              </MDButton>
              <MDButton
                variant="outlined"
                color="info"
                onClick={() => printPdfIframe(statementPreviewIframeRef, statementPreviewUrl)}
                disabled={!statementPreviewUrl}
              >
                Print
              </MDButton>
              <MDButton
                variant="gradient"
                color="info"
                onClick={handleSendStatementEmail}
                disabled={statementSendLoading || !statementPreviewUrl}
              >
                {statementSendLoading ? "Sending..." : "Send Statement"}
              </MDButton>
            </MDBox>
          </MDBox>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={rebootSnackbar.open}
        autoHideDuration={8000}
        onClose={() => setRebootSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setRebootSnackbar((s) => ({ ...s, open: false }))}
          severity={rebootSnackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {rebootSnackbar.message}
        </Alert>
      </Snackbar>

      <Snackbar
        open={radiusActionSnackbar.open}
        autoHideDuration={8000}
        onClose={() => setRadiusActionSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setRadiusActionSnackbar((s) => ({ ...s, open: false }))}
          severity={radiusActionSnackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {radiusActionSnackbar.message}
        </Alert>
      </Snackbar>

      <Dialog
        open={rebootPingModal.open}
        onClose={closeRebootPingModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reboot — {rebootPingModal.cpe?.name || "CPE"}</DialogTitle>
        <DialogContent>
          <MDTypography variant="caption" color="text" display="block" mb={1}>
            Target: <strong>{rebootPingModal.cpe?.ip || "—"}</strong>
            {rebootPingModal.phase !== "prompt" && rebootPingModal.startedAt && (
              <>
                {" · "}
                Elapsed: <strong>{rebootPingElapsedSec}s</strong>
              </>
            )}
          </MDTypography>

          {rebootPingModal.phase === "prompt" && (
            <MDTypography variant="body2" color="text" mb={2}>
              This will restart the CPE through UISP and briefly interrupt service on this device. Confirm only if you
              intend to reboot now.
            </MDTypography>
          )}

          {rebootPingModal.phase === "waiting_down" && (
            <MDTypography variant="body2" color="text" mb={1}>
              Pinging every 3 seconds. Waiting for the device to go offline (reboot starting)…
            </MDTypography>
          )}
          {rebootPingModal.phase === "waiting_up" && (
            <MDTypography variant="body2" color="text" mb={1}>
              Device is offline. Waiting for it to respond to ping again…
            </MDTypography>
          )}
          {rebootPingModal.phase === "complete" && (
            <MDTypography variant="body2" color="success" fontWeight="medium" mb={1}>
              The device is back online.
            </MDTypography>
          )}

          {rebootPingModal.lastPing && (
            <MDTypography variant="caption" color="text" display="block">
              Last ping:{" "}
              {rebootPingModal.lastPing.packets_received > 0 ? (
                <>
                  {rebootPingModal.lastPing.packet_loss ?? 0}% loss · avg{" "}
                  {Number(rebootPingModal.lastPing.average_ping_time ?? 0).toFixed(1)} ms
                </>
              ) : (
                <>no reply (offline)</>
              )}
            </MDTypography>
          )}
          {rebootPingModal.lastError && (
            <MDTypography variant="caption" color="error" display="block" mt={0.5}>
              {rebootPingModal.lastError}
            </MDTypography>
          )}

          {(rebootPingModal.phase === "waiting_down" || rebootPingModal.phase === "waiting_up") && (
            <MDBox display="flex" alignItems="center" gap={1} mt={2}>
              <CircularProgress size={20} />
              <MDTypography variant="caption" color="text">
                Monitoring… (API ping every 3s)
              </MDTypography>
            </MDBox>
          )}
        </DialogContent>
        <DialogActions>
          {rebootPingModal.phase === "prompt" ? (
            <>
              <MDButton color="dark" onClick={closeRebootPingModal}>
                Cancel
              </MDButton>
              <MDButton
                color="warning"
                variant="contained"
                onClick={confirmCpeReboot}
                disabled={rebootConfirmLoading}
              >
                {rebootConfirmLoading ? (
                  <MDBox display="flex" alignItems="center" gap={1}>
                    <CircularProgress size={18} color="inherit" />
                    Rebooting…
                  </MDBox>
                ) : (
                  "Reboot now"
                )}
              </MDButton>
            </>
          ) : rebootPingModal.phase === "complete" ? (
            <MDButton color="info" onClick={closeRebootPingModal}>
              Close
            </MDButton>
          ) : (
            <MDButton color="warning" variant="outlined" onClick={closeRebootPingModal}>
              Stop monitoring
            </MDButton>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={editPppoeModal.open}
        onClose={() =>
          !editPppoeModal.saving && setEditPppoeModal((prev) => ({ ...prev, open: false }))
        }
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit RADIUS User</DialogTitle>
        <DialogContent dividers>
          {editPppoeModal.error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {editPppoeModal.error}
            </Alert>
          )}
          <MDBox display="grid" gap={2} mt={0.5}>
            <TextField
              label="Username"
              value={editPppoeModal.form.username}
              fullWidth
              disabled
              helperText="Username is immutable."
            />
            <TextField
              label="Password"
              value={editPppoeModal.form.password}
              onChange={(event) =>
                setEditPppoeModal((prev) => ({
                  ...prev,
                  form: { ...prev.form, password: event.target.value },
                }))
              }
              fullWidth
            />
            <TextField
              select
              label="Package"
              value={editPppoeModal.form.package}
              onChange={(event) =>
                setEditPppoeModal((prev) => ({
                  ...prev,
                  form: { ...prev.form, package: event.target.value },
                }))
              }
              fullWidth
            >
              {editPppoeModal.packages.map((pkg) => (
                <MenuItem key={pkg} value={pkg}>
                  {pkg}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="IP Pool"
              value={editPppoeModal.form.ipPool}
              onChange={async (event) => {
                const selectedPool = event.target.value;
                setEditPppoeModal((prev) => ({
                  ...prev,
                  form: { ...prev.form, ipPool: selectedPool, ipTableId: "" },
                }));
                try {
                  const ipRows = await api.post("/radius/ip_pool_ips", {
                    ip_pool: selectedPool,
                    include_username: editPppoeModal.form.username,
                  });
                  setEditPppoeModal((prev) => ({
                    ...prev,
                    ips: Array.isArray(ipRows) ? ipRows : [],
                  }));
                } catch (ipError) {
                  setEditPppoeModal((prev) => ({
                    ...prev,
                    error: ipError?.message || "Failed to load pool IPs.",
                  }));
                }
              }}
              fullWidth
            >
              {editPppoeModal.ipPools.map((pool) => (
                <MenuItem key={pool} value={pool}>
                  {pool}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="IP Address"
              value={editPppoeModal.form.ipTableId}
              onChange={(event) =>
                setEditPppoeModal((prev) => ({
                  ...prev,
                  form: { ...prev.form, ipTableId: event.target.value },
                }))
              }
              fullWidth
              disabled={!editPppoeModal.form.ipPool}
            >
              {editPppoeModal.ips.map((row) => (
                <MenuItem key={row.ip_table_id} value={String(row.ip_table_id)}>
                  {row.ip}
                </MenuItem>
              ))}
            </TextField>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton
            variant="text"
            color="secondary"
            onClick={() => setEditPppoeModal((prev) => ({ ...prev, open: false }))}
            disabled={editPppoeModal.saving}
          >
            Cancel
          </MDButton>
          <MDButton
            variant="contained"
            color="info"
            onClick={submitEditPppoeUser}
            disabled={editPppoeModal.loading || editPppoeModal.saving}
          >
            {editPppoeModal.saving ? "Saving..." : "Save PPPoE User"}
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={addRadiusModal.open}
        onClose={() =>
          !addRadiusModal.saving && setAddRadiusModal((prev) => ({ ...prev, open: false }))
        }
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add New RADIUS User</DialogTitle>
        <DialogContent dividers>
          {addRadiusModal.error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {addRadiusModal.error}
            </Alert>
          )}
          <MDBox display="grid" gap={2} mt={0.5}>
            <TextField
              label="Username"
              value={addRadiusModal.form.username}
              onChange={(event) =>
                setAddRadiusModal((prev) => ({
                  ...prev,
                  form: {
                    ...prev.form,
                    username: sanitizeRadiusUsernameInput(event.target.value),
                  },
                }))
              }
              helperText="Suggested from customer name: first.surname (lowercase)"
              fullWidth
            />
            <MDBox display="flex" gap={1}>
              <TextField
                label="Password"
                value={addRadiusModal.form.password}
                onChange={(event) =>
                  setAddRadiusModal((prev) => ({
                    ...prev,
                    form: { ...prev.form, password: event.target.value },
                  }))
                }
                fullWidth
              />
              <MDButton
                variant="outlined"
                color="info"
                onClick={() =>
                  setAddRadiusModal((prev) => ({
                    ...prev,
                    form: { ...prev.form, password: generateRadiusPassword() },
                  }))
                }
              >
                Random
              </MDButton>
            </MDBox>
            <TextField
              select
              label="Package"
              value={addRadiusModal.form.package}
              onChange={(event) =>
                setAddRadiusModal((prev) => ({
                  ...prev,
                  form: { ...prev.form, package: event.target.value },
                }))
              }
              fullWidth
            >
              {addRadiusModal.packages.map((pkg) => (
                <MenuItem key={pkg} value={pkg}>
                  {pkg}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="IP Pool"
              value={addRadiusModal.form.ipPool}
              onChange={async (event) => {
                const selectedPool = event.target.value;
                setAddRadiusModal((prev) => ({
                  ...prev,
                  form: { ...prev.form, ipPool: selectedPool, ipTableId: "" },
                }));
                await loadIpPoolIps(selectedPool);
              }}
              fullWidth
            >
              {addRadiusModal.ipPools.map((pool) => (
                <MenuItem key={pool} value={pool}>
                  {pool}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="IP Address"
              value={addRadiusModal.form.ipTableId}
              onChange={(event) =>
                setAddRadiusModal((prev) => ({
                  ...prev,
                  form: { ...prev.form, ipTableId: event.target.value },
                }))
              }
              fullWidth
              disabled={!addRadiusModal.form.ipPool}
            >
              {addRadiusModal.ips.map((row) => (
                <MenuItem key={row.ip_table_id} value={String(row.ip_table_id)}>
                  {row.ip}
                </MenuItem>
              ))}
            </TextField>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton
            variant="text"
            color="secondary"
            onClick={() => setAddRadiusModal((prev) => ({ ...prev, open: false }))}
            disabled={addRadiusModal.saving}
          >
            Cancel
          </MDButton>
          <MDButton
            variant="contained"
            color="info"
            onClick={submitAddRadiusUser}
            disabled={addRadiusModal.loading || addRadiusModal.saving}
          >
            {addRadiusModal.saving ? "Saving..." : "Create User"}
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={linkRadiusModal.open}
        onClose={() =>
          !linkRadiusModal.saving && setLinkRadiusModal((prev) => ({ ...prev, open: false }))
        }
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Link Existing RADIUS User</DialogTitle>
        <DialogContent dividers>
          {linkRadiusModal.error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {linkRadiusModal.error}
            </Alert>
          )}
          <MDBox display="grid" gap={2} mt={0.5}>
            <Autocomplete
              options={linkRadiusModal.options}
              value={linkRadiusModal.form.username || null}
              inputValue={linkRadiusModal.form.query}
              loading={linkRadiusModal.loading}
              onChange={(_, value) =>
                setLinkRadiusModal((prev) => ({
                  ...prev,
                  form: { ...prev.form, username: value || "" },
                }))
              }
              onInputChange={(_, value) => {
                setLinkRadiusModal((prev) => ({
                  ...prev,
                  form: { ...prev.form, query: value },
                }));
                searchLinkableRadiusUsers(value);
              }}
              noOptionsText={
                linkRadiusModal.form.query?.trim()
                  ? "No matching Radius users"
                  : "Start typing to search Radius users"
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Radius Username"
                  placeholder="Type to search existing Radius usernames"
                  fullWidth
                  helperText="Search and select in one field."
                />
              )}
            />
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton
            variant="text"
            color="secondary"
            onClick={() => setLinkRadiusModal((prev) => ({ ...prev, open: false }))}
            disabled={linkRadiusModal.saving}
          >
            Cancel
          </MDButton>
          <MDButton
            variant="contained"
            color="info"
            onClick={submitLinkRadiusUser}
            disabled={linkRadiusModal.loading || linkRadiusModal.saving}
          >
            {linkRadiusModal.saving ? "Linking..." : "Link User"}
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={linkDecisionModal.open}
        onClose={() =>
          !linkRadiusModal.saving &&
          setLinkDecisionModal({ open: false, username: "", linkedCustomers: [] })
        }
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm link behavior</DialogTitle>
        <DialogContent dividers>
          <MDTypography variant="body2" color="text" mb={1}>
            The RADIUS user <strong>{linkDecisionModal.username}</strong> is already linked to
            customer profile(s):
          </MDTypography>
          <MDBox display="grid" gap={0.5} mb={1.25}>
            {linkDecisionModal.linkedCustomers.map((customer) => {
              const customerId = customer?.customer_id;
              const customerCode = customer?.code || String(customerId || "");
              const customerName = customer?.name || "Unknown Customer";
              const label = `${customerCode} - ${customerName}`;
              return (
                <Link
                  key={`${customerId}-${customerCode}`}
                  href={`/customers/view/${customerId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="hover"
                  variant="body2"
                >
                  {label}
                </Link>
              );
            })}
          </MDBox>
          <MDTypography variant="body2" color="text">
            Should this user be transferred to the current customer only, or kept on both profiles?
          </MDTypography>
        </DialogContent>
        <DialogActions>
          <MDButton
            variant="text"
            color="secondary"
            onClick={() => setLinkDecisionModal({ open: false, username: "", linkedCustomers: [] })}
            disabled={linkRadiusModal.saving}
          >
            Cancel
          </MDButton>
          <MDButton
            variant="outlined"
            color="info"
            onClick={() => completeLinkRadiusUser("shared")}
            disabled={linkRadiusModal.saving}
          >
            Keep on both
          </MDButton>
          <MDButton
            variant="contained"
            color="warning"
            onClick={() => completeLinkRadiusUser("transfer")}
            disabled={linkRadiusModal.saving}
          >
            Transfer to this customer
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog open={pppoeDangerConfirmModal.open} onClose={closePppoeDangerConfirmModal} maxWidth="sm" fullWidth>
        <DialogTitle>
          {pppoeDangerConfirmModal.action === "delete"
            ? "Confirm permanent deletion"
            : "Confirm unlink user"}
        </DialogTitle>
        <DialogContent dividers>
          {pppoeDangerConfirmModal.step === 1 ? (
            <MDTypography variant="body2" color="text">
              {pppoeDangerConfirmModal.action === "delete"
                ? `Delete "${pppoeDangerConfirmModal.username}" from Radius permanently? This removes credentials and links.`
                : `Unlink "${pppoeDangerConfirmModal.username}" from this customer profile only?`}
            </MDTypography>
          ) : (
            <MDTypography variant="body2" color="error">
              {pppoeDangerConfirmModal.action === "delete"
                ? `Final confirmation: PERMANENTLY DELETE "${pppoeDangerConfirmModal.username}"? This cannot be undone.`
                : `Final confirmation: proceed to UNLINK "${pppoeDangerConfirmModal.username}" from this profile?`}
            </MDTypography>
          )}
        </DialogContent>
        <DialogActions>
          <MDButton variant="text" color="secondary" onClick={closePppoeDangerConfirmModal}>
            Cancel
          </MDButton>
          {pppoeDangerConfirmModal.step === 1 ? (
            <MDButton
              variant="outlined"
              color={pppoeDangerConfirmModal.action === "delete" ? "warning" : "info"}
              onClick={advancePppoeDangerConfirmStep}
            >
              Continue
            </MDButton>
          ) : (
            <MDButton
              variant="contained"
              color={pppoeDangerConfirmModal.action === "delete" ? "error" : "warning"}
              onClick={executePppoeDangerAction}
            >
              {pppoeDangerConfirmModal.action === "delete" ? "Delete permanently" : "Unlink now"}
            </MDButton>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={speedTestModal.open} onClose={closeSpeedTestModal} maxWidth="md" fullWidth>
        <DialogTitle>Speed test — {speedTestModal.cpe?.name || "CPE"}</DialogTitle>
        <DialogContent>
          <MDTypography variant="caption" color="text" display="block" mb={1}>
            Target: <strong>{speedTestModal.cpe?.ip || "—"}</strong>
            {speedTestModal.testId != null && (
              <>
                {" · "}
                Test ID: <strong>{String(speedTestModal.testId)}</strong>
              </>
            )}
          </MDTypography>

          <MDTypography variant="button" fontWeight="bold" display="block" mb={0.5}>
            Saved tests (this CPE)
          </MDTypography>
          {speedTestHistoryLoading ? (
            <MDBox display="flex" alignItems="center" gap={1} py={1}>
              <CircularProgress size={18} />
              <MDTypography variant="caption" color="text">
                Loading history…
              </MDTypography>
            </MDBox>
          ) : speedTestHistory.length === 0 ? (
            <MDTypography variant="caption" color="text" display="block" mb={1}>
              No completed tests stored yet. Results are saved when a run finishes (or fails / is cancelled).
            </MDTypography>
          ) : (
            <MDBox
              sx={{
                maxHeight: 220,
                overflow: "auto",
                border: "1px solid rgba(0,0,0,0.08)",
                borderRadius: 1,
                p: 1,
                mb: 2,
              }}
            >
              {speedTestHistory.map((row) => (
                <MDBox
                  key={row.uisp_speedtest_id}
                  py={0.75}
                  sx={{ borderBottom: "1px solid rgba(0,0,0,0.06)", "&:last-child": { borderBottom: "none" } }}
                >
                  <MDBox display="flex" flexWrap="wrap" alignItems="center" gap={0.75} mb={0.25}>
                    <MDTypography variant="caption" color="text">
                      {row.created_at ? formatDateTime(row.created_at) : "—"}
                    </MDTypography>
                    <Chip size="small" label={row.test_status || "—"} />
                  </MDBox>
                  <MDTypography variant="caption" color="text" display="block">
                    DL avg: <strong>{formatSpeedBps(row.download_average_bps)}</strong>
                    {" · "}
                    UL avg: <strong>{formatSpeedBps(row.upload_average_bps)}</strong>
                    {" · "}
                    Latency avg: <strong>{formatLatencyMs(row.latency_average_ms)}</strong>
                  </MDTypography>
                  {(row.server_city || row.server_country || row.server_provider) && (
                    <MDTypography variant="caption" color="text" display="block" sx={{ opacity: 0.85 }}>
                      {[row.server_city, row.server_country].filter(Boolean).join(", ")}
                      {row.server_provider ? ` · ${row.server_provider}` : ""}
                    </MDTypography>
                  )}
                </MDBox>
              ))}
            </MDBox>
          )}

          <Divider sx={{ my: 2 }} />

          <MDTypography variant="button" fontWeight="bold" display="block" mb={1}>
            Current run
          </MDTypography>

          {speedTestModal.error && (
            <MDTypography variant="body2" color="error" mb={1}>
              {speedTestModal.error}
            </MDTypography>
          )}

          {!speedTestModal.detail && !speedTestModal.error && !speedTestModal.finished && (
            <MDBox display="flex" alignItems="center" gap={1} py={2}>
              <CircularProgress size={22} />
              <MDTypography variant="body2" color="text">
                Starting test… (updates every 2s)
              </MDTypography>
            </MDBox>
          )}

          {speedTestModal.detail && (
            <>
              <MDBox display="flex" alignItems="center" gap={1} mb={1} flexWrap="wrap">
                <MDTypography variant="body2" fontWeight="medium">
                  Status:
                </MDTypography>
                <Chip
                  size="small"
                  label={speedTestModal.detail?.identification?.status || "—"}
                  color={
                    speedTestModal.detail?.identification?.status === "finished"
                      ? "success"
                      : speedTestModal.detail?.identification?.status === "running"
                        ? "info"
                        : speedTestModal.detail?.identification?.status === "failed"
                          ? "error"
                          : speedTestModal.detail?.identification?.status === "cancelled"
                            ? "warning"
                            : "default"
                  }
                />
              </MDBox>

              {speedTestLiveChart.labels.length > 0 && (
                <MDBox mb={1.5}>
                  <DefaultLineChart
                    title="Live throughput"
                    description={
                      <span>
                        Download and upload by sample time (Mbps). The graph grows as UISP adds samples (modal polls
                        every 2s).
                        <MDBox component="span" display="block" mt={0.5}>
                          <MDTypography component="span" variant="caption" color="info" fontWeight="medium">
                            ● Download
                          </MDTypography>
                          {" · "}
                          <MDTypography component="span" variant="caption" color="success" fontWeight="medium">
                            ● Upload
                          </MDTypography>
                        </MDBox>
                      </span>
                    }
                    height="14rem"
                    chart={speedTestLiveChart}
                  />
                </MDBox>
              )}

              <MDTypography variant="caption" color="text" display="block">
                Download — current:{" "}
                <strong>{formatSpeedBps(speedTestModal.detail.download?.current)}</strong>
                {" · "}
                average: <strong>{formatSpeedBps(speedTestModal.detail.download?.average)}</strong>
              </MDTypography>
              <MDTypography variant="caption" color="text" display="block">
                Upload — current: <strong>{formatSpeedBps(speedTestModal.detail.upload?.current)}</strong>
                {" · "}
                average: <strong>{formatSpeedBps(speedTestModal.detail.upload?.average)}</strong>
              </MDTypography>
              <MDTypography variant="caption" color="text" display="block" mb={1}>
                Latency —{" "}
                <strong>
                  {Number(speedTestModal.detail.latency?.current ?? 0) === 0 &&
                  Number(speedTestModal.detail.latency?.average ?? 0) === 0
                    ? "— (not reported by UISP)"
                    : `${formatLatencyMs(speedTestModal.detail.latency?.current)} · avg ${formatLatencyMs(
                        speedTestModal.detail.latency?.average
                      )}`}
                </strong>
              </MDTypography>

              {speedTestModal.detail.description &&
                typeof speedTestModal.detail.description === "object" && (
                  <MDBox mt={1} pt={1} sx={{ borderTop: "1px solid rgba(0,0,0,0.08)" }}>
                    <MDTypography variant="caption" fontWeight="medium" display="block" mb={0.5}>
                      Test target / server
                    </MDTypography>
                    {(speedTestModal.detail.description.city ||
                      speedTestModal.detail.description.country) && (
                      <MDTypography variant="caption" color="text" display="block">
                        {[speedTestModal.detail.description.city, speedTestModal.detail.description.country]
                          .filter(Boolean)
                          .join(", ")}
                      </MDTypography>
                    )}
                    {speedTestModal.detail.description.provider && (
                      <MDTypography variant="caption" color="text" display="block">
                        Provider: {speedTestModal.detail.description.provider}
                      </MDTypography>
                    )}
                    {speedTestModal.detail.description.speedMbps != null && (
                      <MDTypography variant="caption" color="text" display="block">
                        Rated: {speedTestModal.detail.description.speedMbps} Mbps
                      </MDTypography>
                    )}
                    {speedTestModal.detail.description.url && (
                      <MDTypography
                        variant="caption"
                        color="text"
                        display="block"
                        sx={{ wordBreak: "break-all" }}
                      >
                        URL: {speedTestModal.detail.description.url}
                      </MDTypography>
                    )}
                  </MDBox>
                )}

              {speedTestModal.detail.timestamp != null && (
                <MDTypography variant="caption" color="text" display="block" mt={1}>
                  UISP report time: {formatUispSpeedTestTimestamp(speedTestModal.detail.timestamp)}
                </MDTypography>
              )}
            </>
          )}

          {speedTestModal.detail && !speedTestModal.finished && (
            <MDBox display="flex" alignItems="center" gap={1} mt={2}>
              <CircularProgress size={20} />
              <MDTypography variant="caption" color="text">
                Test in progress…
              </MDTypography>
            </MDBox>
          )}
        </DialogContent>
        <DialogActions>
          {!speedTestModal.finished ? (
            <MDButton color="warning" variant="outlined" onClick={closeSpeedTestModal}>
              Stop &amp; close
            </MDButton>
          ) : (
            <MDButton color="info" onClick={closeSpeedTestModal}>
              Close
            </MDButton>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={cpeUispTrendModal.open} onClose={closeCpeUispTrendModal} maxWidth="md" fullWidth>
        <DialogTitle>UISP trends — {cpeUispTrendModal.cpe?.name || "CPE"}</DialogTitle>
        <DialogContent>
          <MDBox display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} mb={1.25}>
            <MDTypography variant="caption" color="text">
              Target: <strong>{cpeUispTrendModal.cpe?.ip || "—"}</strong>
            </MDTypography>
            <MDBox display="flex" gap={0.5}>
              {[2, 30, 90, 180].map((days) => (
                <MDButton
                  key={days}
                  size="small"
                  variant={cpeUispTrendModal.windowDays === days ? "contained" : "outlined"}
                  color="info"
                  onClick={() => fetchCpeUispTrendSeries(cpeUispTrendModal.cpe, days)}
                  disabled={cpeUispTrendModal.loading}
                >
                  {days}d
                </MDButton>
              ))}
            </MDBox>
          </MDBox>

          {cpeUispTrendModal.loading && (
            <MDBox display="flex" alignItems="center" gap={1} py={2}>
              <CircularProgress size={20} />
              <MDTypography variant="body2" color="text">
                Loading UISP trend data...
              </MDTypography>
            </MDBox>
          )}

          {!cpeUispTrendModal.loading && cpeUispTrendModal.error && (
            <MDTypography variant="body2" color="error">
              {cpeUispTrendModal.error}
            </MDTypography>
          )}

          {!cpeUispTrendModal.loading && !cpeUispTrendModal.error && (
            <>
              {cpeUispUptimeChart.labels.length > 0 && (
                <MDBox mb={2}>
                  <DefaultLineChart
                    title="Service Uptime"
                    description={
                      <span>
                        Device online trend (% over selected window)
                        {cpeUispUptimeStats && (
                          <MDBox component="span" display="block" mt={0.25}>
                            Min: <strong>{cpeUispUptimeStats.min.toFixed(2)}%</strong>
                            {" · "}
                            Max: <strong>{cpeUispUptimeStats.max.toFixed(2)}%</strong>
                            {" · "}
                            Avg: <strong>{cpeUispUptimeStats.avg.toFixed(2)}%</strong>
                          </MDBox>
                        )}
                      </span>
                    }
                    height="13rem"
                    chart={cpeUispUptimeChart}
                  />
                </MDBox>
              )}

              {cpeUispSignalChart.labels.length > 0 && (
                <MDBox mb={2}>
                  <DefaultLineChart
                    title="5 GHz Signal Strength"
                    description={
                      <span>
                        Signal level trend (dBm)
                        {cpeUispSignalStats && (
                          <MDBox component="span" display="block" mt={0.25}>
                            Min: <strong>{cpeUispSignalStats.min.toFixed(2)} dBm</strong>
                            {" · "}
                            Max: <strong>{cpeUispSignalStats.max.toFixed(2)} dBm</strong>
                            {" · "}
                            Avg: <strong>{cpeUispSignalStats.avg.toFixed(2)} dBm</strong>
                          </MDBox>
                        )}
                      </span>
                    }
                    height="13rem"
                    chart={cpeUispSignalChart}
                  />
                </MDBox>
              )}

              {cpeUispCapacityChart.labels.length > 0 && (
                <MDBox>
                  <DefaultLineChart
                    title="Link Capacity"
                    description={
                      <span>
                        Downlink/uplink link capacity trend (Mbps)
                        {(cpeUispCapacityDownStats || cpeUispCapacityUpStats) && (
                          <MDBox component="span" display="block" mt={0.25}>
                            {cpeUispCapacityDownStats ? (
                              <>
                                Down Min: <strong>{cpeUispCapacityDownStats.min.toFixed(2)}</strong>
                                {" · "}
                                Max: <strong>{cpeUispCapacityDownStats.max.toFixed(2)}</strong>
                                {" · "}
                                Avg: <strong>{cpeUispCapacityDownStats.avg.toFixed(2)}</strong>
                              </>
                            ) : null}
                            {cpeUispCapacityDownStats && cpeUispCapacityUpStats ? " | " : ""}
                            {cpeUispCapacityUpStats ? (
                              <>
                                Up Min: <strong>{cpeUispCapacityUpStats.min.toFixed(2)}</strong>
                                {" · "}
                                Max: <strong>{cpeUispCapacityUpStats.max.toFixed(2)}</strong>
                                {" · "}
                                Avg: <strong>{cpeUispCapacityUpStats.avg.toFixed(2)}</strong>
                              </>
                            ) : null}
                          </MDBox>
                        )}
                      </span>
                    }
                    height="13rem"
                    chart={cpeUispCapacityChart}
                  />
                </MDBox>
              )}

              {cpeUispThroughputChart.labels.length > 0 && (
                <MDBox mt={2}>
                  <DefaultLineChart
                    title="Throughput"
                    description={
                      <span>
                        Device downlink/uplink throughput trend (Mbps)
                        {(cpeUispThroughputDownStats || cpeUispThroughputUpStats) && (
                          <MDBox component="span" display="block" mt={0.25}>
                            {cpeUispThroughputDownStats ? (
                              <>
                                Down Min: <strong>{cpeUispThroughputDownStats.min.toFixed(2)}</strong>
                                {" · "}
                                Max: <strong>{cpeUispThroughputDownStats.max.toFixed(2)}</strong>
                                {" · "}
                                Avg: <strong>{cpeUispThroughputDownStats.avg.toFixed(2)}</strong>
                              </>
                            ) : null}
                            {cpeUispThroughputDownStats && cpeUispThroughputUpStats ? " | " : ""}
                            {cpeUispThroughputUpStats ? (
                              <>
                                Up Min: <strong>{cpeUispThroughputUpStats.min.toFixed(2)}</strong>
                                {" · "}
                                Max: <strong>{cpeUispThroughputUpStats.max.toFixed(2)}</strong>
                                {" · "}
                                Avg: <strong>{cpeUispThroughputUpStats.avg.toFixed(2)}</strong>
                              </>
                            ) : null}
                          </MDBox>
                        )}
                      </span>
                    }
                    height="13rem"
                    chart={cpeUispThroughputChart}
                  />
                </MDBox>
              )}

              {cpeUispUptimeChart.labels.length === 0 &&
                cpeUispThroughputChart.labels.length === 0 &&
                cpeUispSignalChart.labels.length === 0 && (
                  <MDTypography variant="caption" color="text">
                    No UISP trend series were returned for this CPE in the selected window.
                  </MDTypography>
                )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <MDButton color="info" onClick={closeCpeUispTrendModal}>
            Close
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={pppoeZabbixModal.open}
        onClose={() => setPppoeZabbixModal({ open: false, user: null, loading: false, error: "", payload: null })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {pppoeZabbixModal.payload?.source === "cpe_info"
            ? `CPE poll graph — ${pppoeZabbixModal.user?.username || "PPPoE user"}`
            : `ICMP graph — ${pppoeZabbixModal.user?.username || "PPPoE user"}`}
        </DialogTitle>
        <DialogContent>
          <MDTypography variant="caption" color="text" display="block" mb={1.5}>
            Host IP: <strong>{pppoeZabbixModal.user?.ipAddress || "—"}</strong> · Window:{" "}
            <strong>Last 2 days</strong>
            {pppoeZabbixModal.payload?.source === "cpe_info" && (
              <>
                {" · "}
                <strong style={{ color: "#ed6c02" }}>Source: local cpe_info (Zabbix unavailable)</strong>
              </>
            )}
            {pppoeZabbixModal.payload?.source === "zabbix" && (
              <>
                {" · "}
                <strong>Source: Zabbix</strong>
              </>
            )}
          </MDTypography>

          {pppoeZabbixModal.loading && (
            <MDBox display="flex" alignItems="center" gap={1} py={2}>
              <CircularProgress size={20} />
              <MDTypography variant="body2" color="text">
                Loading ICMP history (Zabbix, with cpe_info fallback)...
              </MDTypography>
            </MDBox>
          )}

          {!pppoeZabbixModal.loading && pppoeZabbixModal.error && (
            <MDTypography variant="body2" color="error">
              {pppoeZabbixModal.error}
            </MDTypography>
          )}

          {!pppoeZabbixModal.loading && !pppoeZabbixModal.error && (
            <>
              {pppoeZabbixModal.payload?.source === "cpe_info" && (
                <MDBox
                  mb={1.5}
                  px={1}
                  py={0.75}
                  sx={{
                    backgroundColor: "rgba(237, 108, 2, 0.08)",
                    borderLeft: "3px solid #ed6c02",
                    borderRadius: "4px",
                  }}
                >
                  <MDTypography variant="caption" sx={{ color: "warning.main", fontWeight: 600 }}>
                    Zabbix is offline or returned no data. Showing local CPE poll history instead
                    {pppoeZabbixModal.payload?.host?.name
                      ? ` for "${pppoeZabbixModal.payload.host.name}"`
                      : ""}
                    .
                  </MDTypography>
                </MDBox>
              )}
              {pppoeIcmpPingChart.labels.length > 0 ? (
                <MDBox mb={2}>
                  <DefaultLineChart
                    title={
                      pppoeZabbixModal.payload?.source === "cpe_info"
                        ? "CPE poll reachability"
                        : "ICMP: ICMP ping"
                    }
                    description={
                      <span>
                        Reachability percentage over time (100% = up, 0% = down)
                        {pppoePingStats && (
                          <MDBox component="span" display="block" mt={0.25}>
                            Min: <strong>{pppoePingStats.min.toFixed(1)}%</strong>
                            {" · "}
                            Max: <strong>{pppoePingStats.max.toFixed(1)}%</strong>
                            {" · "}
                            Avg: <strong>{pppoePingStats.avg.toFixed(1)}%</strong>
                          </MDBox>
                        )}
                      </span>
                    }
                    height="14rem"
                    chart={pppoeIcmpPingChart}
                  />
                </MDBox>
              ) : (
                <MDTypography variant="caption" color="text" display="block" mb={1}>
                  No reachability history returned for this host.
                </MDTypography>
              )}

              {pppoeIcmpResponseChart.labels.length > 0 ? (
                <MDBox>
                  <DefaultLineChart
                    title={
                      pppoeZabbixModal.payload?.source === "cpe_info"
                        ? "CPE poll ping"
                        : "ICMP: ICMP response time"
                    }
                    description={
                      <span>
                        Response time converted to milliseconds
                        {pppoeResponseStats && (
                          <MDBox component="span" display="block" mt={0.25}>
                            Min: <strong>{pppoeResponseStats.min.toFixed(2)} ms</strong>
                            {" · "}
                            Max: <strong>{pppoeResponseStats.max.toFixed(2)} ms</strong>
                            {" · "}
                            Avg: <strong>{pppoeResponseStats.avg.toFixed(2)} ms</strong>
                          </MDBox>
                        )}
                      </span>
                    }
                    height="14rem"
                    chart={pppoeIcmpResponseChart}
                  />
                </MDBox>
              ) : (
                <MDTypography variant="caption" color="text" display="block">
                  No ping response history returned for this host.
                </MDTypography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <MDButton
            color="info"
            onClick={() => setPppoeZabbixModal({ open: false, user: null, loading: false, error: "", payload: null })}
          >
            Close
          </MDButton>
        </DialogActions>
      </Dialog>

      {showAddContactDialog && (
        <AddContactDialog
          open={showAddContactDialog}
          onClose={() => setShowAddContactDialog(false)}
          onSave={handleAddContact}
          allContacts={contacts}
        />
      )}

      <CpeSectorAnalysisDialog
        open={cpeSectorAnalysisModal.open}
        onClose={closeCpeSectorAnalysisModal}
        cpeName={cpeSectorAnalysisModal.cpeName}
        loading={cpeSectorAnalysisModal.loading}
        error={cpeSectorAnalysisModal.error}
        snapshot={cpeSectorAnalysisModal.snapshot}
      />

      <Dialog
        open={cpeHistoryModalOpen}
        onClose={() => setCpeHistoryModalOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>{`CPE History - ${selectedCpeName || "Unknown"}`}</DialogTitle>
        <DialogContent>
          {cpeHistoryLoading ? (
            <MDBox py={4} display="flex" justifyContent="center">
              <CircularProgress size={22} />
            </MDBox>
          ) : cpeHistoryError ? (
            <MDTypography variant="caption" color="error">
              {cpeHistoryError}
            </MDTypography>
          ) : selectedCpeHistory.length === 0 ? (
            <MDTypography variant="caption" color="text">
              No history available for this CPE.
            </MDTypography>
          ) : (
            <MDBox mt={1}>
              <DefaultLineChart
                title="Hourly Ping History"
                description="Historical ping values from cpe_info polling"
                height="18rem"
                chart={cpeHistoryChart}
              />
              <MDBox mt={2} sx={{ maxHeight: 240, overflow: "auto" }}>
                {selectedCpeHistory
                  .slice()
                  .reverse()
                  .slice(0, 24)
                  .map((entry, idx) => (
                    <MDTypography key={`${entry.updated}-${idx}`} variant="caption" color="text" display="block">
                      {`${formatDateTime(entry.updated)} | ${entry.status || "unknown"} | ${entry.ping ?? "N/A"} ms | ${entry.ssid || "N/A"}`}
                    </MDTypography>
                  ))}
              </MDBox>
            </MDBox>
          )}
        </DialogContent>
        <DialogActions>
          <MDButton color="info" onClick={() => setCpeHistoryModalOpen(false)}>
            Close
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog open={isEditModalOpen} onClose={handleCloseEditModal} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Typography variant="h6" component="span" sx={{ fontWeight: 500 }}>
            Edit Customer Information
          </Typography>

          <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
            {!suspendedStatus && !isOnHoldCustType(customerData?.cust_type) && (
              <MDButton
                variant="outlined"
                color="warning"
                onClick={handlePutCustomerOnHold}
                disabled={suspensionToggleLoading || editSaving || !customerData}
              >
                {suspensionToggleLoading ? (
                  <MDBox display="flex" alignItems="center" gap={1}>
                    <CircularProgress size={14} color="inherit" />
                    <span>On hold…</span>
                  </MDBox>
                ) : (
                  <span>On Hold</span>
                )}
              </MDButton>
            )}
            <MDButton
              variant="outlined"
              color={suspendedStatus ? "success" : "warning"}
              onClick={handleToggleCustomerSuspension}
              disabled={suspensionToggleLoading || editSaving || !customerData}
            >
              {suspensionToggleLoading ? (
                <MDBox display="flex" alignItems="center" gap={1}>
                  <CircularProgress size={14} color="inherit" />
                  <span>{suspendedStatus ? "Activating..." : "Suspending..."}</span>
                </MDBox>
              ) : (
                <span>{suspendedStatus ? "Activate Customer" : "Suspend Customer"}</span>
              )}
            </MDButton>
          </MDBox>
        </DialogTitle>
        <DialogContent>
          <MDBox display="flex" flexDirection="column" gap={2} mt={1}>
            {editError && (
              <PageErrorAlert title={PAGE_ERROR_TITLES.editCustomer} message={editError} />
            )}

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="RSA ID"
                  fullWidth
                  value={editForm.id}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, id: e.target.value }))}
                />
              </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Customer Name"
                    fullWidth
                    value={editForm.name}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, name: e.target.value }))}
                  />
                </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Account Status"
                  select
                  fullWidth
                  value={editForm.cust_type}
                  onChange={(e) =>
                    setEditForm((prev) => ({ ...prev, cust_type: e.target.value }))
                  }
                >
                  {CUSTOMER_STATUS_OPTIONS.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Payment Type"
                  select
                  fullWidth
                  value={editForm.payment_type}
                  onChange={(e) =>
                    setEditForm((prev) => ({ ...prev, payment_type: e.target.value }))
                  }
                >
                  {PAYMENT_TYPE_OPTIONS.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Account Note"
                  fullWidth
                  value={editForm.type}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, type: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="VAT Number"
                  fullWidth
                  value={editForm.vat}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, vat: e.target.value }))}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  label="Primary Phone"
                  fullWidth
                  value={editForm.telephone2}
                  onChange={(e) =>
                    setEditForm((prev) => ({ ...prev, telephone2: e.target.value }))
                  }
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Secondary Phone"
                  fullWidth
                  value={editForm.fax}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, fax: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="WhatsApp"
                  fullWidth
                  value={editForm.telephone1}
                  onChange={(e) =>
                    setEditForm((prev) => ({ ...prev, telephone1: e.target.value }))
                  }
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Email"
                  fullWidth
                  value={editForm.email}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, email: e.target.value }))}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Signup date"
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={editForm.signup_date ?? ""}
                  onChange={(e) =>
                    setEditForm((prev) => ({ ...prev, signup_date: e.target.value }))
                  }
                />
              </Grid>

              {editForm.cancelled_at ? (
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Cancelled at"
                    type="date"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={editForm.cancelled_at}
                    onChange={(e) =>
                      setEditForm((prev) => ({ ...prev, cancelled_at: e.target.value }))
                    }
                  />
                </Grid>
              ) : null}

              <Grid item xs={12} md={6}>
                <TextField
                  label="Physical Address"
                  fullWidth
                  multiline
                  minRows={3}
                  value={editForm.address}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, address: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Postal Address"
                  fullWidth
                  multiline
                  minRows={3}
                  value={editForm.postal}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, postal: e.target.value }))}
                />
              </Grid>
            </Grid>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton variant="text" color="error" onClick={handleCloseEditModal} disabled={editSaving}>
            Cancel
          </MDButton>
          <MDButton
            variant="gradient"
            color="info"
            onClick={handleSaveEditCustomer}
            disabled={editSaving || !customerData}
          >
            {editSaving ? "Saving..." : "Save Changes"}
          </MDButton>
        </DialogActions>
      </Dialog>
    </Fragment>
  );
}

CustomerView.propTypes = {
  portalVariant: PropTypes.oneOf(["admin", "client"]),
  customerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  clientAccessToken: PropTypes.string,
};

export default CustomerView;